import { useReducer, useState, useEffect, useRef } from "react";
import LandingPage, { ContactForm } from "./LandingPage";
import MapView from "./MapView";
import { ports, connections, fuelConfig, fuelLibrary, DEFAULT_ACTIVE_FUEL_IDS, SCENARIO_YEARS, EU_MARITIME_FACTORS } from "./data";
import { PRESETS } from "./presets";
import {
  routeReducer,
  initialRouteState,
  getAvailableConnections,
} from "./routeReducer";
import "./App.css";
import {
  ComposedChart,
  Bar,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Line,
  ReferenceLine,
} from "recharts";
import { sessionManager } from './sessionManager';




function RightSidebar({ fuelParams, setFuelParams, euFactors, setEuFactors, fuelConfig, EU_MARITIME_FACTORS, activeFuelIds, allFuels, onOpenFuelLibrary, isOpen, onToggle, sidebarRef, manageRef, tourControlRef, onFirstFuelRef, co2InGCO2MJ, setCo2InGCO2MJ }) {

  const FUEL_COLORS = {
    HFO: "#ef4444", VLSFO: "#f97316", MGO: "#f59e0b", LNG: "#06b6d4",
    Methanol: "#8b5cf6", eMethanol: "#8b5cf6", Biofuel: "#22c55e",
    Ammonia: "#a855f7", Hydrogen: "#e2e8f0", "Synthetic LNG": "#a855f7",
  };

  // Section-level collapse: Fuels open, others closed by default
  const [sectionsOpen, setSectionsOpen] = useState({
    fuels: true,
    etsCoverage: false,
    etsTargets: false,
  });

  // Toggle: show CO₂ factor as gCO₂/MJ (intensity) instead of tCO₂/t — lifted to App

  // Individual fuel collapse: all closed by default
  const [expandedFuels, setExpandedFuels] = useState({});

  // Expose imperative tour controls
  useEffect(() => {
    if (!tourControlRef) return;
    tourControlRef.current = {
      collapseAllFuels: () => setExpandedFuels({}),
      expandFirstFuel:  () => {
        const firstKey = Object.keys(fuelParams).find(k => activeFuelIds.includes(k));
        if (firstKey) setExpandedFuels({ [firstKey]: true });
      },
    };
  });  // run every render so activeFuelIds/fuelParams stay current

  function toggleSection(key) {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleFuel(fuelKey) {
    setExpandedFuels(prev => ({ ...prev, [fuelKey]: !prev[fuelKey] }));
  }

  function updateFuelParam(fuelKey, field, raw) {
    // Boolean fields — store directly without numeric conversion
    if (field === "isRfnbo") {
      setFuelParams(prev => ({
        ...prev,
        [fuelKey]: { ...prev[fuelKey], isRfnbo: !!raw },
      }));
      return;
    }
    const val = parseFloat(raw);
    // For optional range fields, allow clearing to undefined
    if (isNaN(val)) {
      if (field === "costLow" || field === "costHigh") {
        setFuelParams(prev => ({
          ...prev,
          [fuelKey]: { ...prev[fuelKey], [field]: undefined },
        }));
      }
      return;
    }
    setFuelParams(prev => ({
      ...prev,
      [fuelKey]: { ...prev[fuelKey], [field]: val },
    }));
  }

  function updateEuCoverage(region, raw) {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setEuFactors(prev => ({ ...prev, [region]: val }));
  }

  function updateEuTarget(year, raw) {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setEuFactors(prev => ({
      ...prev,
      yearlyTargets: { ...prev.yearlyTargets, [year]: val },
    }));
  }

  function resetAll() {
    setFuelParams(
      Object.fromEntries(
        Object.entries(fuelConfig).map(([key, cfg]) => [
          key,
          {
            cost: cfg.cost.value,
            co2: cfg.co2.value,
            hfoEquivalentFactor: cfg.hfoEquivalentFactor.value,
            lhvMJperT: cfg.lhv.value * 1000,
          },
        ])
      )
    );
    setEuFactors({
      inter: EU_MARITIME_FACTORS.inter,
      mixed: EU_MARITIME_FACTORS.mixed,
      out: EU_MARITIME_FACTORS.out,
      yearlyTargets: { ...EU_MARITIME_FACTORS.yearlyTargets },
      fuelEuPenaltyPrice: 2400,
      eurToUsd: 1.08,
    });
  }

  // Reusable section header
  function SectionToggle({ label, sectionKey }) {
    const isOpen = sectionsOpen[sectionKey];
    return (
      <div
        onClick={() => toggleSection(sectionKey)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
          margin: "12px 0 8px",   /* ← was marginTop: 8 */
        }}
      >
        <span className="section-label">{label}</span>
        <div className="section-line" />
        <span style={{
          fontFamily: "var(--font-mono)",
          fontSize: 14,
          color: "var(--teal-400)",
          opacity: 0.7,
          lineHeight: 1,
          flexShrink: 0,
        }}>
          {isOpen ? "−" : "+"}
        </span>
      </div>
    );
  }

  return (
    <aside className={"right-sidebar" + (isOpen ? "" : " collapsed")} ref={sidebarRef}>
      <div className="right-sidebar-header">
        <div className="right-sidebar-title">Parameters</div>
        <div className="right-sidebar-subtitle">Fuel &amp; EU Regulation</div>
      </div>

      <div className="right-sidebar-body">

        {/* ══ SECTION: FUEL PROPERTIES ══ */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 8px" }}>
          <span className="section-label">Fuel Properties</span>
          <div className="section-line" />
          <button
            ref={manageRef}
            onClick={onOpenFuelLibrary}
            style={{
              background: "transparent",
              border: "1px solid rgba(45,212,191,0.35)",
              borderRadius: "var(--radius)",
              color: "var(--teal-400)",
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              padding: "3px 8px",
              cursor: "pointer",
              flexShrink: 0,
              transition: "var(--transition)",
            }}
            onMouseEnter={e => { e.target.style.background = "rgba(45,212,191,0.08)"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; }}
          >
            ⊕ Manage
          </button>
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 14,
            color: "var(--teal-400)", opacity: 0.7, lineHeight: 1, flexShrink: 0, cursor: "pointer",
          }} onClick={() => toggleSection("fuels")}>
            {sectionsOpen.fuels ? "−" : "+"}
          </span>
        </div>

        {/* CO₂ unit toggle row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "5px 8px", borderRadius: 6, background: "rgba(45,212,191,0.04)", border: "1px solid rgba(45,212,191,0.1)" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--teal-400)", opacity: 0.7, letterSpacing: "0.8px", textTransform: "uppercase" }}>
            CO₂ display unit
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--teal-400)", opacity: co2InGCO2MJ ? 0.4 : 1 }}>tCO₂/t</span>
            <div
              onClick={() => setCo2InGCO2MJ(v => !v)}
              style={{
                width: 28, height: 14, borderRadius: 7, cursor: "pointer", flexShrink: 0,
                background: co2InGCO2MJ ? "var(--teal-400)" : "rgba(148,163,184,0.3)",
                position: "relative", transition: "background 0.2s",
              }}
            >
              <div style={{
                position: "absolute", top: 2, left: co2InGCO2MJ ? 16 : 2,
                width: 10, height: 10, borderRadius: "50%", background: "#fff",
                transition: "left 0.2s",
              }} />
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--teal-400)", opacity: co2InGCO2MJ ? 1 : 0.4 }}>gCO₂/MJ</span>
          </div>
        </div>

        {sectionsOpen.fuels && Object.entries(fuelParams)
          .filter(([fuelKey]) => activeFuelIds.includes(fuelKey))
          .map(([fuelKey, vals]) => {
          const isOpen = !!expandedFuels[fuelKey];
          const fuelMeta = allFuels.find(f => f.id === fuelKey);
          const displayName = fuelMeta?.name ?? fuelKey;
          return (
            <div className="param-group" key={fuelKey}
              ref={Object.keys(fuelParams).filter(k => activeFuelIds.includes(k)).indexOf(fuelKey) === 0
                ? el => {
                    if (tourControlRef) tourControlRef.current._firstFuelGroupEl = el;
                    onFirstFuelRef?.(el);
                  }
                : null}
            >
              <div
                className="param-group-header"
                onClick={() => toggleFuel(fuelKey)}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                <div
                  className="param-group-dot"
                  style={{ background: FUEL_COLORS[fuelKey] || "#94a3b8" }}
                />
                <span className="param-group-title" style={{ flex: 1 }}>{displayName}</span>
                {/* Inline stats: cost | co2 */}
                {!isOpen && (
                  <span className="fuel-card-inline-stats">
                    <span className="fuel-stat-cost">${vals.cost}</span>
                    <span className="fuel-stat-sep">·</span>
                    <span className="fuel-stat-co2">
                      {co2InGCO2MJ && vals.lhvMJperT > 0
                        ? `${(vals.co2 * 1e6 / vals.lhvMJperT).toFixed(1)} gCO₂/MJ`
                        : `${vals.co2} tCO₂/t`}
                    </span>
                  </span>
                )}
                <span style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 14,
                  color: "var(--teal-400)",
                  opacity: 0.7,
                  lineHeight: 1,
                  marginLeft: 6,
                }}>
                  {isOpen ? "−" : "+"}
                </span>
              </div>

              {isOpen && (
                <div className="param-rows">
                  {/* Cost Low */}
                  <div className="param-row">
                    <span className="param-label">
                      <span className="param-scenario-badge low" style={{ marginRight: 5, display: "inline-block", minWidth: 52, textAlign: "center" }}>Low</span>
                      Cost
                    </span>
                    <div className="param-input-wrap">
                      <input
                        type="number"
                        className="param-input"
                        value={vals.costLow ?? ""}
                        placeholder="—"
                        step="10"
                        onChange={e => updateFuelParam(fuelKey, "costLow", e.target.value)}
                      />
                      <span className="param-unit">USD/t</span>
                    </div>
                  </div>
                  {/* Cost Expected */}
                  <div className="param-row">
                    <span className="param-label">
                      <span className="param-scenario-badge base" style={{ marginRight: 5, display: "inline-block", minWidth: 52, textAlign: "center" }}>Expected</span>
                      Cost
                    </span>
                    <div className="param-input-wrap">
                      <input
                        type="number"
                        className="param-input"
                        value={vals.cost}
                        step="10"
                        onChange={e => updateFuelParam(fuelKey, "cost", e.target.value)}
                      />
                      <span className="param-unit">USD/t</span>
                    </div>
                  </div>
                  {/* Cost High */}
                  <div className="param-row">
                    <span className="param-label">
                      <span className="param-scenario-badge high" style={{ marginRight: 5, display: "inline-block", minWidth: 52, textAlign: "center" }}>High</span>
                      Cost
                    </span>
                    <div className="param-input-wrap">
                      <input
                        type="number"
                        className="param-input"
                        value={vals.costHigh ?? ""}
                        placeholder="—"
                        step="10"
                        onChange={e => updateFuelParam(fuelKey, "costHigh", e.target.value)}
                      />
                      <span className="param-unit">USD/t</span>
                    </div>
                  </div>
                  <div className="param-row">
                    <span className="param-label">CO₂ factor</span>
                    <div className="param-input-wrap">
                      {/* tCO₂/t field — always the real stored value */}
                      {!co2InGCO2MJ && (<>
                        <input
                          type="number"
                          className="param-input"
                          value={vals.co2}
                          step="0.001"
                          onChange={e => updateFuelParam(fuelKey, "co2", e.target.value)}
                        />
                        <span className="param-unit">tCO₂/t</span>
                      </>)}
                      {/* gCO₂/MJ field — display only, writes back via conversion */}
                      {co2InGCO2MJ && (<>
                        <input
                          type="number"
                          className="param-input"
                          value={vals.lhvMJperT > 0 ? parseFloat((vals.co2 * 1e6 / vals.lhvMJperT).toFixed(2)) : ""}
                          step="0.1"
                          onChange={e => {
                            const gco2mj = parseFloat(e.target.value);
                            if (!isNaN(gco2mj) && vals.lhvMJperT > 0) {
                              updateFuelParam(fuelKey, "co2", (gco2mj * vals.lhvMJperT / 1e6).toFixed(6));
                            }
                          }}
                        />
                        <span className="param-unit">gCO₂/MJ</span>
                      </>)}
                    </div>
                  </div>
                  <div className="param-row">
                    <span className="param-label">HFO equiv.</span>
                    <div className="param-input-wrap">
                      <input
                        type="number"
                        className="param-input"
                        value={vals.hfoEquivalentFactor}
                        step="0.001"
                        onChange={e => updateFuelParam(fuelKey, "hfoEquivalentFactor", e.target.value)}
                      />
                      <span className="param-unit">tHFOeq/t</span>
                    </div>
                  </div>
                  <div className="param-row">
                  <span className="param-label">LHV</span>
                  <div className="param-input-wrap">
                    <input
                      type="number"
                      className="param-input"
                      value={vals.lhvMJperT}
                      step="100"
                      onChange={e => updateFuelParam(fuelKey, "lhvMJperT", e.target.value)}
                    />
                    <span className="param-unit">MJ/t</span>
                  </div>
                </div>
                  {/* RFNBO toggle */}
                  <div className="param-row" style={{ alignItems: "center" }}>
                    <span className="param-label" style={{ lineHeight: 1.3 }}>RFNBO<br/><span style={{ fontSize: 8, opacity: 0.6, textTransform: "none", letterSpacing: 0 }}>2× energy credit</span></span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div
                        onClick={() => updateFuelParam(fuelKey, "isRfnbo", !vals.isRfnbo)}
                        style={{
                          width: 28, height: 14, borderRadius: 7, cursor: "pointer",
                          background: vals.isRfnbo ? "var(--teal-400)" : "rgba(148,163,184,0.3)",
                          position: "relative", transition: "background 0.2s", flexShrink: 0,
                        }}
                      >
                        <div style={{
                          position: "absolute", top: 2, left: vals.isRfnbo ? 16 : 2,
                          width: 10, height: 10, borderRadius: "50%", background: "#fff",
                          transition: "left 0.2s",
                        }} />
                      </div>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: vals.isRfnbo ? "var(--teal-400)" : "#94a3b8" }}>
                        {vals.isRfnbo ? "on" : "off"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ══ SECTION: EU ETS COVERAGE ══ */}
        <SectionToggle label="FuelEU Regulation settings" sectionKey="etsCoverage" />

        {sectionsOpen.etsCoverage && (
          <div className="param-group">
            <div className="param-rows">

              {[
                { key: "inter", label: "Intra EU (factor)" },
                { key: "mixed", label: "Extra EU (factor)" },
                { key: "out", label: "Out of Scope" },
              ].map(({ key, label }) => (
                <div className="param-row" key={key}>
                  <span className="param-label">{label}</span>
                  <div className="param-input-wrap">
                    <input
                      type="number"
                      className="param-input"
                      value={euFactors[key]}
                      min="0" max="1" step="0.1"
                      onChange={e => updateEuCoverage(key, e.target.value)}
                    />
                    <span className="param-unit">factor</span>
                  </div>
                </div>
              ))}

              {/* FuelEU Maritime non-compliance penalty (Art. 23, Reg. 2023/1805) */}
              <div className="param-row" style={{ marginTop: 6 }}>
                <span className="param-label" title="FuelEU Maritime non-compliance penalty (Art. 23, Reg. EU 2023/1805): €2,400 per tonne of VLSFO-equivalent of the GHG intensity shortfall.">
                  FuelEU Penalty
                  <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.55, fontWeight: 400 }}>ⓘ</span>
                </span>
                <div className="param-input-wrap">
                  <input
                    type="number"
                    className="param-input"
                    value={euFactors.fuelEuPenaltyPrice ?? 2400}
                    min="0"
                    step="100"
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val)) setEuFactors(prev => ({ ...prev, fuelEuPenaltyPrice: val }));
                    }}
                  />
                  <span className="param-unit">EUR/tVLSFO</span>
                </div>
              </div>

              {/* EUR → USD conversion for display */}
              <div className="param-row">
                <span className="param-label" title="EUR to USD exchange rate, used to convert FuelEU penalty (calculated in EUR) to USD for display alongside fuel costs.">
                  EUR/USD
                </span>
                <div className="param-input-wrap">
                  <input
                    type="number"
                    className="param-input"
                    value={euFactors.eurToUsd ?? 1.08}
                    min="0.1"
                    step="0.01"
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val)) setEuFactors(prev => ({ ...prev, eurToUsd: val }));
                    }}
                  />
                  <span className="param-unit">rate</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <SectionToggle label="FuelEU Intensity Targets" sectionKey="etsTargets" />

        {sectionsOpen.etsTargets && (
          <div className="param-group">
            <div className="param-rows">
              {Object.keys(euFactors.yearlyTargets).map(year => (
                <div className="param-row" key={year}>
                  <span className="param-label">{year}</span>
                  <div className="param-input-wrap">
                    <input
                      type="number"
                      className="param-input"
                      value={euFactors.yearlyTargets[year] ?? ""}
                      min="0"
                      step="0.01"
                      onChange={e => updateEuTarget(year, e.target.value)}
                    />
                    <span className="param-unit">gCO₂/MJ</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button className="param-reset-btn" onClick={resetAll}>
          ↺ Reset to Defaults
        </button>

      </div>
    </aside>
  );
}



/* ─── GUIDED TOUR ─────────────────────────────────────────────── */

const TOUR_STEPS = [
  {
    id: "welcome",
    target: null,
    position: "center",
    title: "Welcome to the FuelEU Planner",
    icon: "⚓",
    body: "This tool helps you model your fleet's GHG intensity trajectory, optimise fuel blending strategies, and quantify FuelEU Maritime penalty exposure across scenario years (2025–2050).\n\nThis short tour walks you through each section — it takes about 90 seconds.",
  },
  {
    id: "voyages",
    target: "tour-left-sidebar",
    tabTarget: "tour-tab-map",
    position: "right",
    title: "Your Fleet — Voyage Panel",
    icon: "🚢",
    body: "Every calculation starts with voyages. Each voyage defines a port-to-port route, a vessel profile (type, speed, fuel consumption), and how many trips it operates per year.\n\nUse the Voyage Editor tab on the map to build one, then save it here.",
  },
  {
    id: "explore",
    target: "tour-explore-btn",
    tabTarget: "tour-tab-map",
    position: "right",
    title: "Explore Port Connections",
    icon: "🔍",
    body: "Before building a voyage, use Explore mode to understand what connections are available from any port.\n\nClick the button to activate it, then click any port on the map — outgoing routes appear as solid EU-colored lines, incoming routes as dashed purple lines. This helps you plan your route before committing to it.",
  },
  {
    id: "map-controls",
    target: "tour-map-controls",
    tabTarget: "tour-tab-map",
    position: "left",
    title: "Map Controls",
    icon: "⚙️",
    body: "The ⚙ gear button (top-right of the map) opens these controls:\n\n• Toggle port name labels on/off\n• Toggle direction arrows on routes\n• Switch between Standard and Satellite tile layers\n• Adjust route opacity\n• Fit the view to all visible routes\n• Reset to the default world view",
  },
  {
    id: "right-sidebar",
    target: "tour-right-sidebar",
    position: "left",
    title: "Fuel & Regulation Parameters",
    icon: "⚙️",
    body: "Edit fuel prices, CO₂ factors, and LHV values here. These drive every cost and emissions number across the tool.\n\nYou can also adjust the EU ETS coverage factors and the FuelEU intensity targets if regulations change.",
  },
  {
    id: "fuel-params",
    target: "tour-fuel-params-group",
    position: "left",
    title: "Per-Fuel Parameters",
    icon: "🔧",
    body: "Expand any fuel row to edit its cost (low / base / high scenario), CO₂ emission factor, and LHV.\n\nThese values are used in every cost and penalty calculation across the tool — adjust them to reflect your actual procurement contracts or regulatory updates.",
  },
  {
    id: "manage-btn",
    target: "tour-manage-btn",
    tabTarget: null,
    position: "left",
    title: "Fuel Library",
    icon: "⊕",
    body: "Click ⊕ Manage to open the full fuel library. It contains standard fossil fuels, biofuels, ammonia, methanol, hydrogen, and more — each with official GHG intensity and LHV values.\n\nYou can also define fully custom fuels with your own parameters.",
  },
  {
    id: "fuel-card",
    target: "tour-fuel-card",
    tabTarget: null,
    position: "right",
    title: "Toggle Fuels On/Off",
    icon: "🔋",
    body: "Each card represents one fuel. Click a card to add it to your active fuel set — it will then be available as a base or blend fuel in the Fuel Strategy tab.\n\nActive fuels show a ✓ badge. Click again to remove them.",
  },
  {
    id: "fuel-dup",
    target: "tour-fuel-dup",
    tabTarget: null,
    position: "right",
    title: "Duplicate as Custom",
    icon: "⎘",
    body: "The ⎘ duplicate button copies any existing fuel into a new custom fuel you can edit freely — name, CO₂ factor, LHV, cost, density.\n\nUseful for modelling blends, regional variants, or future fuels not yet in the library.",
  },
  {
    id: "strategy",
    target: "tour-first-voyage-card",
    tabTarget: "tour-tab-strategy",
    position: "top",
    title: "Fuel Blending Timeline",
    icon: "⚗️",
    body: "This is the core of the tool. For each voyage, you can assign up to two blend fuels per scenario year — each with its own share (%).\n\nThe timeline lets you phase in greener fuels progressively: e.g. 10% biofuel in 2025, rising to 30% in 2030. Year columns can be toggled off for vessels not operating that year.",
  },
  {
    id: "optimize",
    target: "tour-optimize-btn",
    tabTarget: "tour-tab-strategy",
    position: "bottom",
    title: "⚡ Optimize Fleet",
    icon: "⚡",
    body: "Once blend fuels are assigned, this button calculates the minimum blend shares needed across all voyages and years to meet FuelEU targets — at the lowest possible total fuel cost.\n\nIt uses a continuous-knapsack algorithm: cheapest CO₂-reduction per dollar is filled first. The result depends on your allocation method (proportional or optimal).",
  },
  {
    id: "allocation",
    target: "tour-alloc-toggle",
    tabTarget: "tour-tab-allocation",
    position: "bottom",
    title: "Allocation Toggle",
    icon: "⚡",
    body: "This is the master switch for allocation mode. It only affects mixed voyages (one EU port) where 50% of energy enters the compliance window.\n\nOptimal front-loads your cleanest fuels into that window — lowering attained GHG intensity and potentially eliminating the penalty. Intra-EU voyages are always 100% in scope regardless.",
  },
  {
    id: "tables",
    target: "tour-year-selector",
    tabTarget: "tour-tab-tables",
    position: "top",
    title: "Year Selector",
    icon: "📊",
    body: "Switch between scenario years here. The coloured dot next to each year shows compliance status at a glance: green = compliant, red = non-compliant (penalty applies), grey = no EU coverage.\n\nRed years with a multiplier (×1.1, ×1.2…) indicate escalating penalties under Art. 23 for consecutive non-compliance.",
  },
  {
    id: "analytics",
    target: "tour-chart-grid",
    tabTarget: "tour-tab-analytics",
    position: "top",
    title: "Four Analytics Charts",
    icon: "📈",
    body: "The full picture across all scenario years:\n• Top-left: GHG intensity vs FuelEU target by fuel\n• Top-right: Annual fuel cost vs no-blend reference\n• Bottom-left: Cost range + abatement cost (USD/tCO₂)\n• Bottom-right: Fuel share over time — by energy (default) or cost, via the toggle\n\nAll charts update live and reflect your chosen allocation method.",
  },
  {
    id: "footer-cases",
    target: "tour-footer-cases",
    tabTarget: "tour-tab-map",
    position: "top-fixed",
    title: "Load & Save Cases",
    icon: "💾",
    body: "📂 Load opens the preset library — choose a built-in demo fleet or reload a previously saved case to continue your work.\n\n💾 Save Case stores your current voyages, fuel strategy, and parameters under a custom name so you can return to it later.\n\nUse ⬇ Export / ⬆ Import in the footer to move cases between browsers or share with colleagues.",
  },
];

function TourOverlay({ stepIndex, onNext, onBack, onSkip, tourRefs }) {
  const step = TOUR_STEPS[stepIndex];
  const total = TOUR_STEPS.length;
  const [rect, setRect] = useState(null);
  const [tabRect, setTabRect] = useState(null);
  const tooltipRef = useRef(null);

  // Explicit palette — no CSS variables, works on any theme
  const C = {
    overlay:        "rgba(15, 30, 60, 0.55)",
    card:           "#ffffff",
    cardBorder:     "#d1d5db",
    cardShadow:     "0 12px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)",
    spotlightRing:  "#0d9488",            // teal-600
    spotlightGlow:  "rgba(13,148,136,0.18)",
    accent:         "#0d9488",            // teal-600
    accentLight:    "rgba(13,148,136,0.10)",
    accentBorder:   "rgba(13,148,136,0.4)",
    accentHover:    "rgba(13,148,136,0.18)",
    progressDone:   "rgba(13,148,136,0.45)",
    progressActive: "#0d9488",
    progressTodo:   "#e5e7eb",
    label:          "#6b7280",            // gray-500
    title:          "#111827",            // gray-900
    body:           "#374151",            // gray-700
    btnSecBorder:   "#d1d5db",
    btnSecColor:    "#374151",
    btnSecHover:    "#111827",
    skipColor:      "#9ca3af",            // gray-400
    skipHover:      "#6b7280",
    mono:           "'JetBrains Mono', 'Fira Mono', 'Consolas', monospace",
  };

  // Measure primary target + tab target on each step change
  useEffect(() => {
    function measure() {
      const el = step.target ? tourRefs.current[step.target] : null;
      if (el) setRect(el.getBoundingClientRect());
      else setRect(null);

      const tabEl = step.tabTarget ? tourRefs.current[step.tabTarget] : null;
      if (tabEl) setTabRect(tabEl.getBoundingClientRect());
      else setTabRect(null);
    }
    measure();
    const t = setTimeout(measure, 80);
    return () => clearTimeout(t);
    // tourRefs is a ref object (stable identity) — intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, step.target, step.tabTarget]);

  const PAD = 10;
  const top    = rect ? Math.max(0, rect.top    - PAD) : 0;
  const left   = rect ? Math.max(0, rect.left   - PAD) : 0;
  const right  = rect ? Math.min(window.innerWidth,  rect.right  + PAD) : window.innerWidth;
  // Do NOT clamp bottom to innerHeight — we need the full band even for footer elements
  const bottom = rect ? rect.bottom + PAD : window.innerHeight;

  // Tab highlight padding (tighter — just a glow ring around the tab button)
  const TAB_PAD = 4;
  const tabTop    = tabRect ? tabRect.top    - TAB_PAD : 0;
  const tabLeft   = tabRect ? tabRect.left   - TAB_PAD : 0;
  const tabRight  = tabRect ? tabRect.right  + TAB_PAD : 0;
  const tabBottom = tabRect ? tabRect.bottom + TAB_PAD : 0;

  function positionTooltip() {
    if (!rect) return { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
    const W = 340, GAP = 18;
    const vw = window.innerWidth, vh = window.innerHeight;
    if (step.position === "right") {
      const t = Math.min(rect.top, vh - 340);
      return { top: Math.max(12, t), left: right + GAP, width: W };
    }
    if (step.position === "left") {
      const t = Math.min(rect.top, vh - 340);
      return { top: Math.max(12, t), left: Math.max(12, left - W - GAP), width: W };
    }
    if (step.position === "bottom") {
      const l = Math.min(Math.max(12, rect.left + rect.width / 2 - W / 2), vw - W - 12);
      return { top: bottom + GAP, left: l, width: W };
    }
    if (step.position === "top") {
      const l = Math.min(Math.max(12, rect.left + rect.width / 2 - W / 2), vw - W - 12);
      return { top: Math.max(12, top - GAP - 380), left: l, width: W };
    }
    // "top-fixed": card always pinned to top of viewport, centered
    if (step.position === "top-fixed") {
      const l = Math.min(Math.max(12, vw / 2 - W / 2), vw - W - 12);
      return { top: 12, left: l, width: W };
    }
    return { top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: W };
  }

  const tooltipPos = positionTooltip();
  const isCentered = !rect;

  return (
    <>
      {/* Backdrop — four bands leaving a spotlight hole around primary target */}
      {rect ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 9100, pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: 0,      left: 0,     right: 0,           height: top,             background: C.overlay }} />
          <div style={{ position: "absolute", top: bottom, left: 0,     right: 0,           bottom: 0,               background: C.overlay }} />
          <div style={{ position: "absolute", top,         left: 0,     width: left,        height: bottom - top,    background: C.overlay }} />
          <div style={{ position: "absolute", top,         left: right, right: 0,           height: bottom - top,    background: C.overlay }} />
          {/* Primary spotlight ring */}
          <div style={{
            position: "absolute",
            top: top - 2, left: left - 2,
            width: right - left + 4, height: bottom - top + 4,
            borderRadius: 7,
            border: `2px solid ${C.spotlightRing}`,
            boxShadow: `0 0 0 4px ${C.spotlightGlow}`,
          }} />
          {/* Tab highlight ring — same style as primary spotlight */}
          {tabRect && (
            <div style={{
              position: "absolute",
              top: tabTop - 2, left: tabLeft - 2,
              width: tabRight - tabLeft + 4, height: tabBottom - tabTop + 4,
              borderRadius: 7,
              border: `2px solid ${C.spotlightRing}`,
              boxShadow: `0 0 0 4px ${C.spotlightGlow}`,
            }} />
          )}
        </div>
      ) : (
        <div style={{ position: "fixed", inset: 0, zIndex: 9100, background: C.overlay, pointerEvents: "none" }} />
      )}

      {/* Click-blocker — clicking anywhere outside the card advances to Next */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 9101, cursor: "pointer" }}
        onClick={() => onNext(total)}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          zIndex: 9102,
          ...tooltipPos,
          background: C.card,
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 12,
          boxShadow: C.cardShadow,
          padding: "22px 24px 20px",
          fontFamily: C.mono,
          ...(isCentered ? { top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 460 } : {}),
        }}
      >
        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
          {TOUR_STEPS.map((_, i) => (
            <div key={i} style={{
              height: 3, borderRadius: 2,
              flex: i === stepIndex ? 2.5 : 1,
              background: i < stepIndex ? C.progressDone : i === stepIndex ? C.progressActive : C.progressTodo,
              transition: "flex 0.3s, background 0.3s",
            }} />
          ))}
        </div>

        {/* Step label */}
        <div style={{ fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", color: C.accent, marginBottom: 8, fontFamily: C.mono }}>
          {stepIndex === 0 ? "Introduction" : `Step ${stepIndex} of ${total - 1}`}
        </div>

        {/* Icon + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>{step.icon}</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.title, letterSpacing: ".1px", lineHeight: 1.3 }}>
            {step.title}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "#f3f4f6", marginBottom: 14 }} />

        {/* Body */}
        <div style={{ fontSize: 12, lineHeight: 1.8, color: C.body, marginBottom: 20, whiteSpace: "pre-line" }}>
          {step.body}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <button
            onClick={onSkip}
            style={{
              background: "transparent", border: "none", color: C.skipColor,
              fontFamily: C.mono, fontSize: 10, letterSpacing: "1px",
              textTransform: "uppercase", cursor: "pointer", padding: "4px 0",
              transition: "color .15s",
            }}
            onMouseEnter={e => e.target.style.color = C.skipHover}
            onMouseLeave={e => e.target.style.color = C.skipColor}
          >
            Skip tour
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            {stepIndex > 0 && (
              <button
                onClick={onBack}
                style={{
                  background: "transparent",
                  border: `1px solid ${C.btnSecBorder}`,
                  borderRadius: 6, color: C.btnSecColor,
                  fontFamily: C.mono, fontSize: 11,
                  padding: "7px 16px", cursor: "pointer", transition: "all .15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = C.btnSecHover; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.btnSecBorder; e.currentTarget.style.color = C.btnSecColor; }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => onNext(total)}
              style={{
                background: C.accent,
                border: `1px solid ${C.accent}`,
                borderRadius: 6, color: "#ffffff",
                fontFamily: C.mono, fontSize: 11, fontWeight: 600,
                padding: "7px 20px", cursor: "pointer", transition: "all .15s",
                letterSpacing: ".3px",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#0f766e"; e.currentTarget.style.borderColor = "#0f766e"; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.accent; e.currentTarget.style.borderColor = C.accent; }}
            >
              {stepIndex === 0 ? "Start tour →" : stepIndex === total - 1 ? "Finish ✓" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function App({ onReady, onTourStart, onTourEnd, onOpenStartup, onOpenContact }) {

  const [route, dispatch] = useReducer(routeReducer, initialRouteState);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [selectedRouteIds, setSelectedRouteIds] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [routeNameInput, setRouteNameInput] = useState("");
  const [pendingRouteName, setPendingRouteName] = useState("");
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [routeToDeleteName, setRouteToDeleteName] = useState(null);
  const [fleetSummary, setFleetSummary] = useState(null);
  const [selectedScenarioYear, setSelectedScenarioYear] = useState(SCENARIO_YEARS[0]);
  const [activeTab, setActiveTab] = useState("map"); // "map" | "strategy" | "tables" | "analytics"
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [exploreMode, setExploreMode] = useState(false);
  const [forceMapControls, setForceMapControls] = useState(undefined);

  // ── SNACKBAR ──────────────────────────────────────────
  const [snackbar, setSnackbar] = useState(null); // { message, type: "success"|"error"|"info" }
  const snackbarTimer = useRef(null);


  

  

  function showSnackbar(message, type = "info") {
    if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
    setSnackbar({ message, type, id: Date.now() });
    snackbarTimer.current = setTimeout(() => setSnackbar(null), 3500);
  }

  // ── EDITABLE PARAMETERS ──────────────────────────────
  const DEFAULT_FUEL_PARAMS = Object.fromEntries(
  Object.entries(fuelConfig).map(([key, cfg]) => [
    key,
    {
      cost:                 cfg.cost.value,
      co2:                  cfg.co2.value,
      hfoEquivalentFactor:  cfg.hfoEquivalentFactor.value,
      lhvMJperT:            cfg.lhv.value * 1000,
      isRfnbo:              cfg.isRfnbo ?? false,
    },
  ])
);

  const DEFAULT_EU_FACTORS = {
      inter: EU_MARITIME_FACTORS.inter,
      mixed: EU_MARITIME_FACTORS.mixed,
      out: EU_MARITIME_FACTORS.out,
      // FuelEU Maritime non-compliance penalty (Art. 23, Reg. EU 2023/1805).
      // Charged per tonne of VLSFO equivalent of the GHG intensity shortfall.
      // Statutory rate: €2,400/tVLSFOeq. Editable in case of regulatory updates.
      fuelEuPenaltyPrice: 2400, // EUR/tVLSFOeq
      // EUR → USD conversion rate for display (penalty is calculated in EUR,
      // all other costs are in USD)
      eurToUsd: 1.08,
      // FuelEU Maritime intensity targets (gCO₂eq/MJ)
      // Baseline reference: 91.16 gCO₂eq/MJ (2020 reference value)
      yearlyTargets: {
        2025: 89.34,
        2030: 85.69,
        2035: 77.94,
        2040: 62.90,
        2045: 34.64,
        2050: 18.23,
      }
    };

  const [fuelParams, setFuelParams] = useState(DEFAULT_FUEL_PARAMS);
  const [euFactors, setEuFactors] = useState(DEFAULT_EU_FACTORS);
  const [activeFuelIds, setActiveFuelIds] = useState(DEFAULT_ACTIVE_FUEL_IDS);
  const [showFuelLibrary, setShowFuelLibrary] = useState(false);
  const [customFuels, setCustomFuels] = useState([]);
  const [co2InGCO2MJ, setCo2InGCO2MJ] = useState(true);

  // ── DIFF / MERGE HELPERS ───────────────────────────────────────
  // Only the user's modifications are persisted (saved/exported), never a
  // frozen full snapshot. That way, if the built-in defaults in data.js
  // change later, only what the user actually customised gets re-applied
  // on top of the fresh defaults.
  function diffFuelParams(params, defaults) {
    const out = {};
    Object.entries(params || {}).forEach(([fuelId, vals]) => {
      const def = defaults[fuelId];
      if (!def) {
        // Custom fuel — not part of built-in defaults, keep it whole.
        out[fuelId] = vals;
        return;
      }
      const fieldDiff = {};
      Object.entries(vals || {}).forEach(([field, val]) => {
        if (JSON.stringify(val) !== JSON.stringify(def[field])) {
          fieldDiff[field] = val;
        }
      });
      if (Object.keys(fieldDiff).length > 0) out[fuelId] = fieldDiff;
    });
    return out;
  }

  function mergeFuelParams(base, diff) {
    const merged = { ...base };
    Object.entries(diff || {}).forEach(([fuelId, fields]) => {
      merged[fuelId] = { ...(base[fuelId] || {}), ...fields };
    });
    return merged;
  }

  function diffEuFactors(factors, defaults) {
    const out = {};
    Object.entries(factors || {}).forEach(([key, val]) => {
      if (key === "yearlyTargets") {
        const yDiff = {};
        Object.entries(val || {}).forEach(([year, v]) => {
          if (v !== defaults.yearlyTargets?.[year]) yDiff[year] = v;
        });
        if (Object.keys(yDiff).length > 0) out.yearlyTargets = yDiff;
      } else if (JSON.stringify(val) !== JSON.stringify(defaults[key])) {
        out[key] = val;
      }
    });
    return out;
  }

  function mergeEuFactors(base, diff) {
    return {
      ...base,
      ...diff,
      yearlyTargets: {
        ...base.yearlyTargets,
        ...(diff?.yearlyTargets || {}),
      },
    };
  }

  function diffActiveFuelIds(ids, defaults) {
    const a = [...(ids || [])].sort();
    const b = [...(defaults || [])].sort();
    const same = a.length === b.length && a.every((v, i) => v === b[i]);
    return same ? undefined : ids;
  }

  // ── VOYAGE PATH HELPERS ─────────────────────────────────────────
  // Saved/exported voyages only keep the port IDs that make up the route.
  // The actual path (distance, coords, EU category flags, …) always comes
  // straight from data.js, rebuilt fresh — never stored or imported stale.
  function slimPorts(routePorts) {
    return (routePorts || []).map(p => ({ id: typeof p === "string" ? p : p.id }));
  }

  function buildSegmentsFromPortIds(portIds) {
    const segs = [];
    for (let i = 0; i < (portIds || []).length - 1; i++) {
      const from = portIds[i];
      const to = portIds[i + 1];
      const conn =
        connections.find(c => c.from === from && c.to === to) ||
        connections.find(c => c.from === to && c.to === from);
      if (conn) segs.push(conn);
    }
    return segs;
  }

  function hydrateRoute(route) {
    const portIds = (route.ports || []).map(p => (typeof p === "string" ? p : p.id));
    return {
      ...route,
      ports: slimPorts(portIds),
      segments: buildSegmentsFromPortIds(portIds),
    };
  }

  // ── SAVE / LOAD CASES ────────────────────────────────────────
  const LS_KEY = "fuelTool_savedCases";
  function readSavedCases() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); }
    catch { return []; }
  }
  function writeSavedCases(cases) {
    localStorage.setItem(LS_KEY, JSON.stringify(cases));
  }
  const [savedCases, setSavedCases] = useState(() => readSavedCases());
  const [fuelParamConflicts, setFuelParamConflicts] = useState(null);

  // ── SAVE CASE MODAL ──────────────────────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState("");
  const [saveOverwriteId, setSaveOverwriteId] = useState(null); // null = new

  // ── CLEAR ALL MODAL ──────────────────────────────────────────
  const [showClearModal, setShowClearModal] = useState(false);

  // ── FUEL ALLOCATION MODE ──────────────────────────────────────
  // true  = optimal: cleanest fuels fill the in-scope window first
  // false = proportional: each voyage's blend goes in pro-rata
  const DEFAULT_USE_OPTIMAL_ALLOCATION = false;
  const [useOptimalAllocation, setUseOptimalAllocation] = useState(DEFAULT_USE_OPTIMAL_ALLOCATION);

  // ── FUEL SHARE CHART METRIC ─────────────────────────────────────
  // "energy" = share of total energy delivered (MJ) per fuel — the physical mix.
  // "cost"   = share of total scenario fuel cost (USD) per fuel — the cost exposure.
  const [fuelShareMetric, setFuelShareMetric] = useState("energy");

  // ── GUIDED TOUR ───────────────────────────────────────────────
  const [tourStep, setTourStep] = useState(null); // null = hidden, 0-N = active
  const tourRefs = useRef({});
  const sidebarControlRef = useRef({});
  const tourSnapshot = useRef(null); // saved user state before tour overwrites it

  function snapshotUserData() {
    tourSnapshot.current = {
      savedRoutes,
      customFuels,
      activeFuelIds,
      fuelParams,
      euFactors,
      activeTab,
    };
  }

  function restoreUserData() {
    const snap = tourSnapshot.current;
    if (!snap) return;
    setSavedRoutes(snap.savedRoutes);
    setCustomFuels(snap.customFuels);
    setActiveFuelIds(snap.activeFuelIds);
    setFuelParams(snap.fuelParams);
    setEuFactors(snap.euFactors);
    setSelectedRouteIds(new Set());
    dispatch({ type: "RESET" });
    setActiveTab(snap.activeTab);
    tourSnapshot.current = null;
  }

  function loadPresetForTour() {
    const preset = PRESETS[0];
    if (!preset) return;
    applyPayload(preset);
    // Select all routes so they appear on the map
    setTimeout(() => {
      setSavedRoutes(prev => {
        setSelectedRouteIds(new Set(prev.map(r => r.id)));
        return prev;
      });
    }, 50);
    setActiveTab("strategy");
  }

  function clearFleetData() {
    setSavedRoutes([]);
    setCustomFuels([]);
    setActiveFuelIds(DEFAULT_ACTIVE_FUEL_IDS);
    setFuelParams(DEFAULT_FUEL_PARAMS);
    setEuFactors(DEFAULT_EU_FACTORS);
    setSelectedRouteIds(new Set());
    dispatch({ type: "RESET" });
    setActiveTab("map");
  }

  useEffect(() => {
    try {
      if (!localStorage.getItem("fuelTool_tourSeen")) {
        sidebarControlRef.current.collapseAllFuels?.();
        onTourStart?.();
        setTourStep(0);
      }
    } catch {}
    // Intentionally runs once on mount only — onTourStart is a stable callback from Root
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startTour() {
    sessionManager.logAnalytics({ event: 'view_tour' });
    snapshotUserData();
    clearFleetData();
    sidebarControlRef.current.collapseAllFuels?.();
    onTourStart?.();
    setTourStep(0);
  }
  function closeTour() {
    setTourStep(null);
    setForceMapControls(undefined);
    try { localStorage.setItem("fuelTool_tourSeen", "1"); } catch {}
    if (tourSnapshot.current) {
      restoreUserData();
    } else {
      clearFleetData();
    }
    onTourEnd?.();
  }
  // Tab to activate when arriving at each tour step index
  const TOUR_STEP_TABS = {
    1: "map", 2: "map", 3: "map",
    // steps 4=right-sidebar, 5=fuel-params, 6=manage-btn, 7=fuel-card, 8=fuel-dup: no tab switch
    9: "strategy", 10: "strategy",
    11: "allocation", 12: "tables", 13: "analytics", 14: "map",
  };

  function tourNext(total) {
    setTourStep(prev => {
      const next = prev + 1;
      if (next >= total) { closeTour(); return null; }
      if (prev === 0) loadPresetForTour();
      // Open map controls panel on entering step 3
      if (next === 3) setForceMapControls(true);
      // Close map controls panel when leaving step 3
      if (prev === 3) setForceMapControls(false);
      if (prev === 5) sidebarControlRef.current.collapseAllFuels?.();
      if (next === 7) setShowFuelLibrary(true);
      if (prev === 8) setShowFuelLibrary(false);
      if (TOUR_STEP_TABS[next]) setActiveTab(TOUR_STEP_TABS[next]);
      if (next === 5) sidebarControlRef.current.expandFirstFuel?.();
      return next;
    });
  }
  function tourBack() {
    setTourStep(s => {
      const prev = Math.max(0, s - 1);
      // Re-open map controls when going back to step 3
      if (prev === 3) setForceMapControls(true);
      // Close map controls when going back past step 3
      if (s === 3) setForceMapControls(false);
      if (prev === 5) sidebarControlRef.current.expandFirstFuel?.();
      if (s === 5) sidebarControlRef.current.collapseAllFuels?.();
      if (prev >= 7 && prev <= 8) setShowFuelLibrary(true);
      if (s === 7) setShowFuelLibrary(false);
      if (TOUR_STEP_TABS[prev]) setActiveTab(TOUR_STEP_TABS[prev]);
      return prev;
    });
  }

  // When tour reaches the year-selector step (index 12), scroll tables pane to top
  useEffect(() => {
    if (tourStep === 12) {
      // Use a small delay to let the tab switch + render complete first
      const t = setTimeout(() => {
        const pane = tourRefs.current["tour-tables-pane"];
        if (pane) pane.scrollTop = 0;
      }, 120);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourStep]);

  const allFuels = [...fuelLibrary, ...customFuels];

  function handleToggleFuel(fuel) {
    const isActive = activeFuelIds.includes(fuel.id);
    if (isActive) {
      setActiveFuelIds(prev => prev.filter(id => id !== fuel.id));
    } else {
      setActiveFuelIds(prev => [...prev, fuel.id]);
      setFuelParams(prev => {
        if (prev[fuel.id]) return prev;
        return {
          ...prev,
          [fuel.id]: {
            cost: fuel.cost.value,
            costLow: fuel.costLow,
            costHigh: fuel.costHigh,
            co2: fuel.co2.value,
            hfoEquivalentFactor: fuel.hfoEquivalentFactor.value,
            lhvMJperT: fuel.lhv.value * 1000,
          },
        };
      });
    }
  }

  function handleAddCustomFuel(fuel) {
    setCustomFuels(prev => [...prev, fuel]);
    setActiveFuelIds(prev => [...prev, fuel.id]);
    setFuelParams(prev => ({
      ...prev,
      [fuel.id]: {
        cost: fuel.cost.value,
        costLow: fuel.costLow,
        costHigh: fuel.costHigh,
        co2: fuel.co2.value,
        hfoEquivalentFactor: fuel.hfoEquivalentFactor.value,
        lhvMJperT: fuel.lhv.value * 1000,
      },
    }));
  }

  // Auto-recalculate whenever routes or fuel params change
  useEffect(() => {
    if (savedRoutes.length > 0) calculateFleetSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedRoutes, fuelParams, euFactors]);




  function convertToHFOEquivalent(fuelType, tons) {
    const factor = fuelParams[fuelType]?.hfoEquivalentFactor
      ?? fuelConfig[fuelType]?.hfoEquivalentFactor?.value
      ?? 1;
    return tons * factor;
  }

  function computeFuelCost(fuelType, tons) {
    const costPerTon = fuelParams[fuelType]?.cost
      ?? fuelConfig[fuelType]?.cost?.value
      ?? 0;
    return tons * costPerTon;
  }

  function computeCO2(fuelType, tons) {
    const co2PerTon = fuelParams[fuelType]?.co2
      ?? fuelConfig[fuelType]?.co2?.value
      ?? 0;
    return tons * co2PerTon;
  }


  function calculateBlendedFuels(targetYear) {
    const result = {
      fuels: {},
      totals: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0 }
    };

    savedRoutes.forEach(route => {
      if (!route.segments || !route.vessel) return;

      // ── Resolve scenario for this year ────────────────────────────
      // scenario.baseFuel   = the primary engine fuel for this year
      //                       (may differ from vessel.fuel.type if user changed it)
      // blend1/2 shares     = % of total voyage ENERGY delivered by those fuels
      // baseShare           = remainder, delivered by scenario.baseFuel
      // If blend1+blend2 = 100% → baseShare = 0 → zero tonnes of base fuel
      const scenario = route.scenarioTimeline
        ? resolveScenarioYear(route, targetYear)
        : {
            baseFuel:    route.vessel?.fuel?.type || "HFO",
            blend1Fuel:  "",
            blend1Share: 0,
            blend2Fuel:  "",
            blend2Share: 0
          };

      if (!scenario) return;

      const scenarioBaseFuel = scenario.baseFuel || route.vessel?.fuel?.type || "HFO";
      const blend1Share      = scenario.blend1Fuel ? Math.max(0, parseFloat(scenario.blend1Share) || 0) : 0;
      const blend2Share      = scenario.blend2Fuel ? Math.max(0, parseFloat(scenario.blend2Share) || 0) : 0;
      // Clamp so blends never exceed 100%
      const totalBlendShare  = Math.min(100, blend1Share + blend2Share);
      const baseShare        = 100 - totalBlendShare;

      // Allocations: each entry is { fuel, energyShare (%) }
      const allocations = [];
      if (baseShare > 0)
        allocations.push({ fuel: scenarioBaseFuel, energyShare: baseShare });
      if (scenario.blend1Fuel && blend1Share > 0)
        allocations.push({ fuel: scenario.blend1Fuel, energyShare: blend1Share });
      if (scenario.blend2Fuel && blend2Share > 0)
        allocations.push({ fuel: scenario.blend2Fuel, energyShare: blend2Share });

      const speed       = Number(route.vessel?.speed?.value || 0);
      const consumption = Number(route.vessel?.fuel?.consumption || 0);
      const trips       = Number(route.operations?.tripsPerYear || 1);
      if (speed <= 0 || consumption <= 0) return;

      // ── Energy reference: vessel hardware fuel × rated consumption ─
      // This gives us the FIXED energy demand of the voyage.
      // We always use the vessel's rated consumption (t/day) with the
      // VESSEL hardware fuel's LHV to get total MJ demand — because the
      // consumption figure on the vessel is calibrated to that fuel.
      const vesselFuel = route.vessel?.fuel?.type || "HFO";
      const vesselLhv  = fuelParams[vesselFuel]?.lhvMJperT
        ?? (fuelConfig[vesselFuel]?.lhv?.value * 1000)
        ?? 40400;

      // Classify the whole route once
      const routeEuCategory = getRouteEuCategory(route.segments);

      route.segments.forEach(segment => {
        const yearlyDistance   = Number(segment.distance || 0) * trips;
        // Tonnes of vessel-rated fuel that would be burned on this segment
        const vesselFuelTons   = (yearlyDistance / speed / 24) * consumption;
        // Fixed energy demand of this segment in MJ
        const totalEnergyMJ    = vesselFuelTons * vesselLhv;

        let region = routeEuCategory;

        // Distribute the energy demand across the allocated fuels
        allocations.forEach(a => {
          if (!a.fuel || a.energyShare <= 0) return;

          // MJ this fuel must deliver
          const fuelEnergyMJ = totalEnergyMJ * (a.energyShare / 100);

          // Tonnes of THIS fuel needed (its own LHV converts MJ → tonnes)
          const fuelLhv = fuelParams[a.fuel]?.lhvMJperT
            ?? (fuelConfig[a.fuel]?.lhv?.value * 1000)
            ?? 40400;
          const fuelTons = fuelEnergyMJ / fuelLhv;

          const hfoEquivalentFactor = fuelParams[a.fuel]?.hfoEquivalentFactor
            ?? fuelConfig[a.fuel]?.hfoEquivalentFactor?.value ?? 1;
          const co2Factor      = fuelParams[a.fuel]?.co2  ?? fuelConfig[a.fuel]?.co2?.value  ?? 0;
          const costFactor     = fuelParams[a.fuel]?.cost ?? fuelConfig[a.fuel]?.cost?.value ?? 0;
          const costLowFactor  = fuelParams[a.fuel]?.costLow  ?? costFactor;
          const costHighFactor = fuelParams[a.fuel]?.costHigh ?? costFactor;

          const hfoEquivalentTons = fuelTons * hfoEquivalentFactor;
          const co2               = fuelTons * co2Factor;
          const cost              = fuelTons * costFactor;
          const costLow           = fuelTons * costLowFactor;
          const costHigh          = fuelTons * costHighFactor;

          if (!result.fuels[a.fuel]) {
            result.fuels[a.fuel] = {
              inter: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, energyMJ: 0, rfnboBonusMJ: 0 },
              mixed: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, energyMJ: 0, rfnboBonusMJ: 0 },
              out:   { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, energyMJ: 0, rfnboBonusMJ: 0 },
              total: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, energyMJ: 0, rfnboBonusMJ: 0 },
              isRfnbo: fuelParams[a.fuel]?.isRfnbo ?? fuelConfig[a.fuel]?.isRfnbo ?? false,
            };
          }

          // RFNBO double-counting bonus: credited MJ = physical MJ (so total accounted = 2× physical)
          const isRfnbo = fuelParams[a.fuel]?.isRfnbo ?? fuelConfig[a.fuel]?.isRfnbo ?? false;
          const rfnboBonusMJ = (isRfnbo && targetYear <= 2030) ? fuelEnergyMJ : 0;

          const target = result.fuels[a.fuel];
          target[region].fuelTons          += fuelTons;
          target[region].hfoEquivalentTons += hfoEquivalentTons;
          target[region].co2               += co2;
          target[region].cost              += cost;
          target[region].costLow           += costLow;
          target[region].costHigh          += costHigh;
          target[region].energyMJ          += fuelEnergyMJ;
          target[region].rfnboBonusMJ      += rfnboBonusMJ;
          target.total.fuelTons            += fuelTons;
          target.total.hfoEquivalentTons   += hfoEquivalentTons;
          target.total.co2                 += co2;
          target.total.cost                += cost;
          target.total.costLow             += costLow;
          target.total.costHigh            += costHigh;
          target.total.energyMJ            += fuelEnergyMJ;
          target.total.rfnboBonusMJ        += rfnboBonusMJ;
          result.totals.fuelTons           += fuelTons;
          result.totals.hfoEquivalentTons  += hfoEquivalentTons;
          result.totals.co2                += co2;
          result.totals.cost               += cost;
          result.totals.energyMJ           = (result.totals.energyMJ ?? 0) + fuelEnergyMJ;
          result.totals.rfnboBonusMJ       = (result.totals.rfnboBonusMJ ?? 0) + rfnboBonusMJ;
        });
      });
    });

    return result;
  }


  function buildChartData() {

    return SCENARIO_YEARS.map(
      year => {

        const blended =
          calculateBlendedFuels(
            year
          );

        const row = { year };

        Object.entries(
          blended.fuels
        ).forEach(
          ([fuel, values]) => {

            row[`inter_${fuel}`] =
              values.inter
                .hfoEquivalentTons || 0;

            row[`mixed_${fuel}`] =
              values.mixed
                .hfoEquivalentTons || 0;

            row[`out_${fuel}`] =
              values.out
                .hfoEquivalentTons || 0;
          }
        );

        row.totalHFOeq =
          blended.totals
            .hfoEquivalentTons;

        row.totalCO2 =
          blended.totals.co2;

        row.totalCost =
          blended.totals.cost;

        return row;
      }
    );
  }

  const availableConnections = getAvailableConnections(route, connections);

  


  const KEEP_PREVIOUS = "__KEEP_PREVIOUS__";

  // Baseline fuels from fuelConfig — always available in every selector
  const BASELINE_FUELS = Object.entries(fuelConfig).map(([id, cfg]) => ({
    id,
    name: cfg.name || id,
    isBaseline: true,
  }));

  // Active library fuels (excludes fuelConfig keys which are not in fuelLibrary)
  const LIBRARY_FUELS = allFuels
    .filter(f => activeFuelIds.includes(f.id))
    .map(f => ({ id: f.id, name: f.name }));

  const SCENARIO_FUELS = [...BASELINE_FUELS, ...LIBRARY_FUELS];

  // Resolve display name for any fuel id (baseline or library)
  function fuelDisplayName(id) {
    const found = SCENARIO_FUELS.find(f => f.id === id);
    return found ? found.name : id;
  }

  // Renders fuel <option>s grouped: baseline fuels first, then active library fuels
  function renderFuelOptions() {
    return (
      <>
        <optgroup label="— Conventional —">
          {BASELINE_FUELS.map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </optgroup>
        {LIBRARY_FUELS.length > 0 && (
          <optgroup label="— Library —">
            {LIBRARY_FUELS.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </optgroup>
        )}
      </>
    );
  }


  const FUEL_COLORS = {
    HFO: "#ef4444",
    VLSFO: "#f97316",
    MGO: "#f59e0b",
    LNG: "#06b6d4",
    Methanol: "#8b5cf6",
    eMethanol: "#8b5cf6",
    Biofuel: "#22c55e",
    Ammonia: "#a855f7",
    Hydrogen: "#e2e8f0",
    'Synthetic LNG': "#a855f7",
  };


  const VESSEL_PRESETS = [
  {
    id: "feeder",
    name: "Feeder (1k TEU)",
    data: {
      type: "Container",
      capacity: { value: 1000, unit: "TEU" },
      speed: { value: 16, unit: "kn" },
      fuel: { type: "MGO", consumption: 25 }
    }
  },
  {
    id: "panamax",
    name: "Panamax (5k TEU)",
    data: {
      type: "Container",
      capacity: { value: 5000, unit: "TEU" },
      speed: { value: 19, unit: "kn" },
      fuel: { type: "VLSFO", consumption: 55 }
    }
  },
  {
    id: "postpanamax",
    name: "Post-Panamax (8k TEU)",
    data: {
      type: "Container",
      capacity: { value: 8000, unit: "TEU" },
      speed: { value: 20, unit: "kn" },
      fuel: { type: "VLSFO", consumption: 70 }
    }
  },
  {
    id: "ulcv",
    name: "ULCV (20k TEU)",
    data: {
      type: "Container",
      capacity: { value: 20000, unit: "TEU" },
      speed: { value: 22, unit: "kn" },
      fuel: { type: "HFO", consumption: 120 }
    }
  },
  {
    id: "nh3tank",
    name: "Ammonia tanker",
    data: {
      type: "tanker",
      capacity: { value: 33000, unit: "t" },
      speed: { value: 15, unit: "kn" },
      fuel: { type: "MGO", consumption: 36.7 }
    }
  },
  {
    id: "custom",
    name: "Custom",
    data: null
  }
];


  function createDefaultScenarioTimeline(baseFuel) {
      const timeline = {};

      SCENARIO_YEARS.forEach((year, index) => {
        timeline[year] = {
          baseFuel:
            index === 0
              ? baseFuel
              : KEEP_PREVIOUS,

          blend1Fuel:
            index === 0
              ? ""
              : KEEP_PREVIOUS,

          blend1Share:
            index === 0
              ? ""
              : KEEP_PREVIOUS,

          blend2Fuel:
            index === 0
              ? ""
              : KEEP_PREVIOUS,

          blend2Share:
            index === 0
              ? ""
              : KEEP_PREVIOUS,

          active: true,
        };
      });

      return timeline;
    }

  // ── ROUTE-LEVEL EU CATEGORY ──────────────────────────────────────────────
  // Classifies the WHOLE route (not per-segment) based on all its segments:
  //   "inter"  — every segment is Intra EU
  //   "mixed"  — at least one EU port involved (Extra EU)
  //   "out"    — every segment is out of scope
  function getRouteEuCategory(segments) {
    if (!segments || segments.length === 0) return "out";
    const hasInter = segments.some(s => s.is_inter_europe);
    const hasMixed = segments.some(s => s.is_mixed);
    const allInter = segments.every(s => s.is_inter_europe);
    if (allInter) return "inter";
    if (hasInter || hasMixed) return "mixed";
    return "out";
  }

  function calculateFleetSummary() {
  let summary = {
    miles: {
      inter: 0,
      mixed: 0,
      out: 0,
      total: 0
    },

    fuels: {}
  };

  savedRoutes.forEach(route => {
    
    console.log(route)
    const trips = Number(route.operations?.tripsPerYear || 0);

    if (!route.segments || !route.vessel) return;

    const speed = Number(route.vessel.speed?.value || 0);
    const fuelConsumption =
      Number(route.vessel.fuel?.consumption || 0);

    const fuelType =
      route.vessel.fuel?.type || "Unknown";

    if (!summary.fuels[fuelType]) {
      summary.fuels[fuelType] = {
        inter: 0,
        mixed: 0,
        out: 0,
        total: 0
      };
    }

    // Classify the whole route once, not per-segment
    const routeCategory = getRouteEuCategory(route.segments);

    route.segments.forEach(segment => {
      const miles = Number(segment.distance || 0) * trips;

      // MILES — all segments bucketed under the route-level category
      summary.miles[routeCategory] += miles;
      summary.miles.total += miles;

      // FUEL
      if (speed > 0) {
        const voyageDays =
          segment.distance / (speed * 24);

        const fuelUsed =
          voyageDays *
          fuelConsumption *
          trips;

        summary.fuels[fuelType][routeCategory] += fuelUsed;
        summary.fuels[fuelType].total += fuelUsed;
      }
    });
  });
  console.log(summary)
  setFleetSummary(summary);
}




  function computeScenarioFuelTable() {
  const blended = {
    fuels: {},
    totals: {
      fuelTons: 0,
      hfoEquivalentTons: 0,
      co2: 0,
      cost: 0
    }
  };

  savedRoutes.forEach(route => {
    if (!route.segments || !route.vessel) return;

    const scenario = resolveScenarioYear(route, selectedScenarioYear);
    if (!scenario) return;

    const trips           = Number(route.operations?.tripsPerYear || 0);
    const speed           = Number(route.vessel.speed?.value || 0);
    const baseConsumption = Number(route.vessel.fuel?.consumption || 0);

    if (speed <= 0 || baseConsumption <= 0) return;

    const baseFuel    = scenario.baseFuel || route.vessel.fuel?.type || "HFO";
    const blend1Share = scenario.blend1Fuel ? (parseFloat(scenario.blend1Share) || 0) : 0;
    const blend2Share = scenario.blend2Fuel ? (parseFloat(scenario.blend2Share) || 0) : 0;
    const baseShare   = Math.max(0, 100 - blend1Share - blend2Share);

    const allocations = [
      { fuel: baseFuel, energyShare: baseShare },
      ...(scenario.blend1Fuel && blend1Share > 0
        ? [{ fuel: scenario.blend1Fuel, energyShare: blend1Share }]
        : []),
      ...(scenario.blend2Fuel && blend2Share > 0
        ? [{ fuel: scenario.blend2Fuel, energyShare: blend2Share }]
        : []),
    ];

    // LHV of base fuel to compute total energy demand
    const baseLhv = fuelParams[baseFuel]?.lhvMJperT
      ?? (fuelConfig[baseFuel]?.lhv?.value * 1000)
      ?? 40400;

    // Classify the whole route once
    const routeEuCatScenario = getRouteEuCategory(route.segments);

    route.segments.forEach(segment => {
      const voyageDays     = Number(segment.distance || 0) / (speed * 24);
      const baseConsumptionTons = voyageDays * baseConsumption * trips;

      // Total energy demand in MJ
      const totalEnergyMJ = baseConsumptionTons * baseLhv;

      let category = routeEuCatScenario;

      allocations.forEach(a => {
        if (!a.fuel || a.energyShare <= 0) return;

        // Energy this fuel delivers (MJ)
        const fuelEnergyMJ = totalEnergyMJ * (a.energyShare / 100);

        // Actual tonnes needed
        const fuelLhv = fuelParams[a.fuel]?.lhvMJperT
          ?? (fuelConfig[a.fuel]?.lhv?.value * 1000)
          ?? 40400;

        const fuelTons = fuelEnergyMJ / fuelLhv;

        const hfoEquivalentFactor = fuelParams[a.fuel]?.hfoEquivalentFactor
          ?? fuelConfig[a.fuel]?.hfoEquivalentFactor?.value
          ?? 1;

        const co2Factor      = fuelParams[a.fuel]?.co2
          ?? fuelConfig[a.fuel]?.co2?.value
          ?? 0;

        const costFactor     = fuelParams[a.fuel]?.cost
          ?? fuelConfig[a.fuel]?.cost?.value
          ?? 0;
        const costLowFactor  = fuelParams[a.fuel]?.costLow  ?? costFactor;
        const costHighFactor = fuelParams[a.fuel]?.costHigh ?? costFactor;

        const hfoEquivalentTons = fuelTons * hfoEquivalentFactor;
        const co2               = fuelTons * co2Factor;
        const cost              = fuelTons * costFactor;
        const costLow           = fuelTons * costLowFactor;
        const costHigh          = fuelTons * costHighFactor;
        const isRfnbo           = fuelParams[a.fuel]?.isRfnbo ?? fuelConfig[a.fuel]?.isRfnbo ?? false;
        const rfnboBonusMJ      = (isRfnbo && selectedScenarioYear <= 2030) ? fuelEnergyMJ : 0;

        if (!blended.fuels[a.fuel]) {
          blended.fuels[a.fuel] = {
            inter: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, rfnboBonusMJ: 0 },
            mixed: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, rfnboBonusMJ: 0 },
            out:   { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, rfnboBonusMJ: 0 },
            total: { fuelTons: 0, hfoEquivalentTons: 0, co2: 0, cost: 0, costLow: 0, costHigh: 0, rfnboBonusMJ: 0 },
            isRfnbo,
          };
        }

        const target = blended.fuels[a.fuel];

        target[category].fuelTons          += fuelTons;
        target[category].hfoEquivalentTons += hfoEquivalentTons;
        target[category].co2               += co2;
        target[category].cost              += cost;
        target[category].costLow           += costLow;
        target[category].costHigh          += costHigh;
        target[category].rfnboBonusMJ      += rfnboBonusMJ;

        target.total.fuelTons          += fuelTons;
        target.total.hfoEquivalentTons += hfoEquivalentTons;
        target.total.co2               += co2;
        target.total.cost              += cost;
        target.total.costLow           += costLow;
        target.total.costHigh          += costHigh;
        target.total.rfnboBonusMJ      += rfnboBonusMJ;

        blended.totals.fuelTons          += fuelTons;
        blended.totals.hfoEquivalentTons += hfoEquivalentTons;
        blended.totals.co2               += co2;
        blended.totals.cost              += cost;
        blended.totals.rfnboBonusMJ       = (blended.totals.rfnboBonusMJ ?? 0) + rfnboBonusMJ;
      });
    });
  });

  return blended;
}



  function confirmDeleteRoute(routeId) {
      setRouteToDelete(routeId);
      setRouteToDeleteName(savedRoutes.find(r => r.id === routeId)?.name);
    }

    function handleDeleteConfirmed() {
      setSavedRoutes(prev =>
        prev.filter(r => r.id !== routeToDelete)
      );

      if (editingId === routeToDelete) {
        setEditingId(null);
      }

      setSelectedRouteIds(prev => {
        const next = new Set(prev);
        next.delete(routeToDelete);
        return next;
      });

      setRouteToDelete(null);
    }

    function handleDeleteCancel() {
      setRouteToDelete(null);
    }

  function exportFleet() {
    const payload = buildPayload();
    const dataStr = JSON.stringify(payload, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fleet-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function importFleet(file, inputEl) {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = JSON.parse(e.target.result);

        // ── Normalise to a versioned payload ──────────────────────────
        let payload;
        if (Array.isArray(parsed)) {
          // v1: bare routes array
          payload = { version: 1, routes: parsed };
        } else if (parsed && typeof parsed === "object" && Array.isArray(parsed.routes)) {
          payload = parsed;
        } else {
          throw new Error("Unrecognised file format — expected a fleet export JSON.");
        }

        // ── Routes ────────────────────────────────────────────────────
        // Rebuild the full path (segments) fresh from data.js using only
        // the stored port IDs — never trust frozen path data from the file.
        setSavedRoutes(payload.routes.map(hydrateRoute));

        // ── Custom fuels (must come before fuelParams so the params
        //    for custom fuels can be safely merged below) ───────────────
        const importedCustomFuels = payload.customFuels ?? [];
        setCustomFuels(importedCustomFuels);

        // ── activeFuelIds ─────────────────────────────────────────────
        if (payload.activeFuelIds) {
          setActiveFuelIds(payload.activeFuelIds);
        }

        // ── Allocation method (optimal vs proportional) ────────────────
        // Falls back to the default when the saved case didn't customise it.
        setUseOptimalAllocation(payload.useOptimalAllocation ?? DEFAULT_USE_OPTIMAL_ALLOCATION);

        // ── fuelParams: payload only contains the user's modified fields.
        //    Detect conflicts against the CURRENT built-in defaults (i.e.
        //    cases where data.js has since changed under the saved value) ─
        if (payload.fuelParams) {
          const COMPARED_FIELDS = ["co2", "cost", "hfoEquivalentFactor", "lhvMJperT"];
          const conflicts = [];
          const builtinIds = Object.keys(DEFAULT_FUEL_PARAMS);
          builtinIds.forEach(fuelId => {
            const importedFields = payload.fuelParams[fuelId];
            const defaults = DEFAULT_FUEL_PARAMS[fuelId];
            if (!importedFields || !defaults) return;
            COMPARED_FIELDS.forEach(field => {
              if (!(field in importedFields)) return;
              const impVal = importedFields[field];
              const defVal = defaults[field];
              if (impVal !== undefined && defVal !== undefined && Math.abs(impVal - defVal) > 1e-9) {
                conflicts.push({ fuelId, field, importedValue: impVal, defaultValue: defVal });
              }
            });
          });

          if (conflicts.length > 0) {
            // Store conflict info and show dialog — don't apply fuelParams yet
            setFuelParamConflicts({ conflicts, importedParams: payload.fuelParams });
            // Apply everything else first (routes, euFactors, activeFuelIds, customFuels)
          } else {
            // No conflicts — merge the modified fields onto fresh defaults
            setFuelParams(prev => mergeFuelParams({ ...DEFAULT_FUEL_PARAMS, ...prev }, payload.fuelParams));
          }
        }

        // ── euFactors: deep-merge imported diff over current defaults ──
        // Protects against old exports missing fuelEuPenaltyPrice, eurToUsd,
        // or any future field added to DEFAULT_EU_FACTORS.
        if (payload.euFactors) {
          setEuFactors(mergeEuFactors(DEFAULT_EU_FACTORS, payload.euFactors));
        }

        // ── Reset file input so same file can be re-imported ─────────
        if (inputEl) inputEl.value = "";

        const routeCount = payload.routes.length;
        showSnackbar(`Imported ${routeCount} voyage${routeCount !== 1 ? "s" : ""} successfully${payload.exportedAt ? ` (exported ${payload.exportedAt.slice(0, 10)})` : ""}.`, "success");

      } catch (err) {
        showSnackbar("Failed to load file: " + err.message, "error");
      }
    };
    reader.readAsText(file);
  }




  function buildPayload() {
    return {
      version: 4,
      exportedAt: new Date().toISOString(),
      // Voyages: only the port IDs are persisted — the path itself
      // (distance, coords, EU category, …) is rebuilt from data.js on load.
      routes: savedRoutes.map(r => ({
        ...r,
        ports: slimPorts(r.ports),
        segments: undefined,
      })),
      // Only what differs from the current built-in defaults is saved, so
      // future updates to data.js aren't shadowed by stale saved values.
      fuelParams: diffFuelParams(fuelParams, DEFAULT_FUEL_PARAMS),
      euFactors: diffEuFactors(euFactors, DEFAULT_EU_FACTORS),
      activeFuelIds: diffActiveFuelIds(activeFuelIds, DEFAULT_ACTIVE_FUEL_IDS),
      // Allocation method (optimal vs proportional) — only saved when it
      // differs from the default, same diff-only philosophy as everything else.
      useOptimalAllocation: useOptimalAllocation !== DEFAULT_USE_OPTIMAL_ALLOCATION
        ? useOptimalAllocation
        : undefined,
      customFuels,
    };
  }

  function openSaveCaseModal() {
    setSaveNameInput("");
    setSaveOverwriteId(null);
    setShowSaveModal(true);
  }

  function confirmSaveCase() {
    const name = saveNameInput.trim();
    if (!name && saveOverwriteId === null) return;
    const cases = readSavedCases();
    if (saveOverwriteId !== null) {
      // overwrite
      const idx = cases.findIndex(c => c.id === saveOverwriteId);
      if (idx !== -1) {
        cases[idx] = { ...cases[idx], ...buildPayload(), name: cases[idx].name, savedAt: new Date().toISOString() };
      }
    } else {
      cases.push({ id: Date.now().toString(), name, savedAt: new Date().toISOString(), ...buildPayload() });
    }
    writeSavedCases(cases);
    setSavedCases(cases);
    setShowSaveModal(false);
    showSnackbar(`Case "${saveOverwriteId !== null ? cases.find(c => c.id === saveOverwriteId)?.name : name}" saved.`, "success");
  }

  // ── UNSAVED CHANGES (drives Save Case button highlight) ─────────
  const hasUnsavedChanges =
    savedRoutes.length > 0 ||
    customFuels.length > 0 ||
    Object.keys(diffFuelParams(fuelParams, DEFAULT_FUEL_PARAMS)).length > 0 ||
    Object.keys(diffEuFactors(euFactors, DEFAULT_EU_FACTORS)).length > 0 ||
    !!diffActiveFuelIds(activeFuelIds, DEFAULT_ACTIVE_FUEL_IDS) ||
    useOptimalAllocation !== DEFAULT_USE_OPTIMAL_ALLOCATION;

  // Expose applyPayload to Root so the startup modal can load data after mount
  useEffect(() => {
    if (onReady) onReady(applyPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyPayload(payload) {
    if (!payload) return;
    const fakeFile = new Blob([JSON.stringify(payload)], { type: "application/json" });
    importFleet(fakeFile, null);
  }


  function updateTripsPerYear(routeId, value) {
    setSavedRoutes(prev =>
      prev.map(r =>
        r.id === routeId
          ? {
              ...r,
              operations: {
                ...r.operations,
                tripsPerYear: value
              }
            }
          : r
      )
    );
  }


  function openNewRouteModal() {
    setRouteNameInput("");
    setActiveTab("map");
    setShowModal(true);
  }

  function confirmNewRoute() {
    if (!routeNameInput.trim()) return;
    setPendingRouteName(routeNameInput);
    setSelectedRouteIds(new Set());
    dispatch({ type: "RESET" });
    dispatch({ type: "SET_BUILDING" });
    setShowModal(false);
  }

  function saveRoute() {
    if (route.status !== "finished") return;
    const newRoute = {
      id: Date.now(),
      name: pendingRouteName || "Unnamed Voyage",
      ports: slimPorts(route.ports),
      segments: buildSegmentsFromPortIds(route.ports.map(p => p.id)),
      vessel: {
        preset: "feeder",
        name: "",
        type: "Container",
        capacity: { value: 1000, unit: "TEU" },
        speed: { value: 16, unit: "kn" },
        fuel: { type: "MGO", consumption: 25 }
      },
      scenario: {
        alt1: "",
        share1: 50,
        alt2: "",
        share2: 50
      },
      scenarioTimeline:
        createDefaultScenarioTimeline("MGO"
        ),
      operations: {
        tripsPerYear: 1
      }
    };
    setSavedRoutes(prev => [...prev, newRoute]);
    setPendingRouteName("");
    dispatch({ type: "RESET" });
  }


  function updateScenarioTimeline(
    routeId,
    year,
    field,
    value
  ) {

    setSavedRoutes(prev =>
      prev.map(route => {
        if (route.id !== routeId) {
          return route;
        }

        const updatedRoute = {
          ...route,

          scenarioTimeline: {
            ...route.scenarioTimeline,

            [year]: {
              ...route.scenarioTimeline?.[year],

              [field]: value
            }
          }
        };

        // When the base fuel changes in the first scenario year, keep the
        // vessel fuel type in sync so the left sidebar always matches.
        if (field === "baseFuel" && year === SCENARIO_YEARS[0] && value && value !== "__KEEP_PREVIOUS__") {
          updatedRoute.vessel = {
            ...updatedRoute.vessel,
            fuel: {
              ...updatedRoute.vessel.fuel,
              type: value,
            },
          };
        }

        return updatedRoute;
      })
    );
  }




  // ── BLEND SHARE OPTIMIZER ─────────────────────────────────────────────────
  // For a given route, compute the minimum blend share(s) per active year
  // such that the whole fleet just meets the FuelEU intensity target.
  //
  // Strategy (cost-aware, greedy):
  //   1. For each year, compute "other routes" contribution to eligible
  //      CO₂ and MJ (everything except the current route, at current shares).
  //   2. Model the current route's eligible CO₂ and MJ as linear functions
  //      of the blend share(s).
  //   3. If already compliant at blend=0 → set shares to 0.
  //   4. With 1 blend fuel → binary-search for minimum s ∈ [0,100].
  //   5. With 2 blend fuels → rank by cost-effectiveness
  //      (cost-per-%-of-intensity-reduction), fill the cheaper one first,
  //      then top up with the second if needed.
  //   6. If 100% total blend still non-compliant → set total to 100.
  //
  // The function writes the result directly into savedRoutes via setSavedRoutes.
  // Replaces all KEEP_PREVIOUS fuel/share references with their actual resolved values.
  // Called before any optimize so the optimizer sees explicit fuel IDs everywhere.
  function materializeAllPrevious(routes) {
    return routes.map(r => {
      if (!r.scenarioTimeline) return r;
      const tl = JSON.parse(JSON.stringify(r.scenarioTimeline));
      SCENARIO_YEARS.forEach(year => {
        const entry = tl[year];
        if (!entry || entry.active === false) return;
        const idx = SCENARIO_YEARS.indexOf(year);

        function walkBack(field) {
          for (let i = idx; i >= 0; i--) {
            const v = tl[SCENARIO_YEARS[i]]?.[field];
            if (v !== undefined && v !== null && v !== KEEP_PREVIOUS) return v;
          }
          return "";
        }

        const patch = {};
        if (!entry.baseFuel || entry.baseFuel === KEEP_PREVIOUS)
          patch.baseFuel = walkBack("baseFuel") || r.vessel?.fuel?.type || "HFO";
        if (entry.blend1Fuel === KEEP_PREVIOUS)
          patch.blend1Fuel = walkBack("blend1Fuel");
        if (entry.blend2Fuel === KEEP_PREVIOUS)
          patch.blend2Fuel = walkBack("blend2Fuel");
        if (entry.blend1Share === KEEP_PREVIOUS)
          patch.blend1Share = walkBack("blend1Share") || "";
        if (entry.blend2Share === KEEP_PREVIOUS)
          patch.blend2Share = walkBack("blend2Share") || "";
        if (Object.keys(patch).length > 0)
          tl[year] = { ...entry, ...patch };
      });
      return { ...r, scenarioTimeline: tl };
    });
  }

  function optimizeBlendShares(routeId) {
    setSavedRoutes(prev => {
      // First materialize all KEEP_PREVIOUS references fleet-wide
      const materialized = materializeAllPrevious(prev);
      const routeIndex = materialized.findIndex(r => r.id === routeId);
      if (routeIndex === -1) return prev;

      const route = materialized[routeIndex];
      const newTimeline = { ...route.scenarioTimeline };

      SCENARIO_YEARS.forEach(year => {
        const yearEntry = newTimeline[year] ?? {};
        if (yearEntry.active === false) return;

        const baseFuel   = yearEntry.baseFuel   || route.vessel?.fuel?.type || "HFO";
        const blend1Fuel = yearEntry.blend1Fuel || "";
        const blend2Fuel = yearEntry.blend2Fuel || "";

        // Need at least one blend fuel to optimise
        if (!blend1Fuel && !blend2Fuel) return;

        const target = euFactors.yearlyTargets[year] ?? null;
        if (target === null) return;

        // ── helpers ──────────────────────────────────────────────────────
        function getFuelProps(fuelId) {
          const co2     = fuelParams[fuelId]?.co2  ?? fuelConfig[fuelId]?.co2?.value  ?? 0;
          const lhv     = fuelParams[fuelId]?.lhvMJperT ?? (fuelConfig[fuelId]?.lhv?.value * 1000) ?? 40400;
          const hfoF    = fuelParams[fuelId]?.hfoEquivalentFactor ?? fuelConfig[fuelId]?.hfoEquivalentFactor?.value ?? 1;
          const cost    = fuelParams[fuelId]?.cost ?? fuelConfig[fuelId]?.cost?.value ?? 0;
          const isRfnbo = fuelParams[fuelId]?.isRfnbo ?? fuelConfig[fuelId]?.isRfnbo ?? false;
          return { co2, lhv, hfoF, cost, isRfnbo };
        }

        // eslint-disable-next-line
        const baseProps  = getFuelProps(baseFuel);
        const b1Props    = blend1Fuel ? getFuelProps(blend1Fuel) : null;
        const b2Props    = blend2Fuel ? getFuelProps(blend2Fuel) : null;

        // Route physical constants for this year
        const speed       = Number(route.vessel?.speed?.value || 0);
        const consumption = Number(route.vessel?.fuel?.consumption || 0);
        const trips       = Number(route.operations?.tripsPerYear || 1);
        if (speed <= 0 || consumption <= 0) return;

        const vesselFuel = route.vessel?.fuel?.type || "HFO";
        const vesselLhv  = fuelParams[vesselFuel]?.lhvMJperT
          ?? (fuelConfig[vesselFuel]?.lhv?.value * 1000) ?? 40400;

        const routeEuCat    = getRouteEuCategory(route.segments);
        const coverageFactor = euFactors[routeEuCat] ?? 0;
        const hfoLhv         = 1000. * fuelConfig.VLSFO.lhv.value; // vlsfoLhv: hfoEqTons × 40,200 = true fuel MJ

        // Total route energy (MJ/yr) — fixed, doesn't depend on fuel choice
        let routeTotalEnergyMJ = 0;
        route.segments.forEach(seg => {
          const dist = Number(seg.distance || 0) * trips;
          routeTotalEnergyMJ += (dist / speed / 24) * consumption * vesselLhv;
        });

        if (routeTotalEnergyMJ <= 0) return;

        // ── "other routes" contribution at their CURRENT shares ───────────
        // We compute eligible CO₂ and MJ from ALL other routes as-is.
        let otherEligCO2 = 0;
        let otherEligMJ  = 0;

        prev.forEach(r => {
          if (r.id === routeId) return;
          if (!r.segments || !r.vessel) return;

          const sc = resolveScenarioYear(r, year);
          if (!sc) return;

          const rSpeed = Number(r.vessel?.speed?.value || 0);
          const rCons  = Number(r.vessel?.fuel?.consumption || 0);
          const rTrips = Number(r.operations?.tripsPerYear || 1);
          if (rSpeed <= 0 || rCons <= 0) return;

          const rVesselFuel = r.vessel?.fuel?.type || "HFO";
          const rVesselLhv  = fuelParams[rVesselFuel]?.lhvMJperT
            ?? (fuelConfig[rVesselFuel]?.lhv?.value * 1000) ?? 40400;

          const rBase   = sc.baseFuel || rVesselFuel;
          const rS1     = sc.blend1Fuel ? Math.max(0, parseFloat(sc.blend1Share) || 0) : 0;
          const rS2     = sc.blend2Fuel ? Math.max(0, parseFloat(sc.blend2Share) || 0) : 0;
          const rTotal  = Math.min(100, rS1 + rS2);
          const rBShare = 100 - rTotal;

          const rAllocations = [];
          if (rBShare > 0) rAllocations.push({ fuelId: rBase, share: rBShare });
          if (sc.blend1Fuel && rS1 > 0) rAllocations.push({ fuelId: sc.blend1Fuel, share: rS1 });
          if (sc.blend2Fuel && rS2 > 0) rAllocations.push({ fuelId: sc.blend2Fuel, share: rS2 });

          const rEuCat = getRouteEuCategory(r.segments);
          const rCovF  = euFactors[rEuCat] ?? 0;

          r.segments.forEach(seg => {
            const dist    = Number(seg.distance || 0) * rTrips;
            const totalMJ = (dist / rSpeed / 24) * rCons * rVesselLhv;

            rAllocations.forEach(a => {
              const fProps    = getFuelProps(a.fuelId);
              const fuelMJ    = totalMJ * (a.share / 100);
              const fuelTons  = fuelMJ / fProps.lhv;
              const hfoEqTons = fuelTons * fProps.hfoF;
              otherEligCO2   += fuelTons * fProps.co2 * rCovF;
              otherEligMJ    += hfoEqTons * hfoLhv * rCovF;
            });
          });
        });

        // ── route contribution as function of (s1%, s2%) ─────────────────
        // For a given allocation [{ fuelId, sharePercent }], returns { eligCO2, eligMJ }
        function routeContrib(s1Pct, s2Pct) {
          const total  = Math.min(100, s1Pct + s2Pct);
          const bShare = 100 - total;
          const allocs = [];
          if (bShare > 0)           allocs.push({ fuelId: baseFuel,   share: bShare });
          if (blend1Fuel && s1Pct > 0) allocs.push({ fuelId: blend1Fuel, share: s1Pct });
          if (blend2Fuel && s2Pct > 0) allocs.push({ fuelId: blend2Fuel, share: s2Pct });

          let eligCO2 = 0, eligMJ = 0;
          allocs.forEach(a => {
            const p       = getFuelProps(a.fuelId);
            const fuelMJ  = routeTotalEnergyMJ * (a.share / 100);
            const tons    = fuelMJ / p.lhv;
            eligCO2      += tons * p.co2 * coverageFactor;
            eligMJ       += (tons * p.hfoF) * hfoLhv * coverageFactor;
          });
          return { eligCO2, eligMJ };
        }

        // Fleet intensity given route uses (s1Pct, s2Pct)
        function fleetIntensity(s1Pct, s2Pct) {
          const { eligCO2, eligMJ } = routeContrib(s1Pct, s2Pct);
          const totalMJ  = otherEligMJ  + eligMJ;
          const totalCO2 = otherEligCO2 + eligCO2;
          if (totalMJ <= 0) return 0;
          return (totalCO2 / totalMJ) * 1_000_000;
        }

        // Already compliant with no blending?
        if (fleetIntensity(0, 0) <= target) {
          newTimeline[year] = { ...yearEntry, blend1Share: "0", blend2Share: "0" };
          return;
        }

        // Still non-compliant at full blend?
        if (fleetIntensity(blend1Fuel ? 100 : 0, blend2Fuel ? 100 : 0) > target) {
          // Can't comply — set 100% total, split proportionally by cost effectiveness
          if (blend1Fuel && blend2Fuel) {
            // Give priority to cheaper (per unit cost); equal split if same
            const eff1 = b1Props.cost > 0 ? b1Props.cost : 1;
            const eff2 = b2Props.cost > 0 ? b2Props.cost : 1;
            const total = eff1 + eff2;
            newTimeline[year] = {
              ...yearEntry,
              blend1Share: String(Math.round((eff2 / total) * 100)),
              blend2Share: String(Math.round((eff1 / total) * 100)),
            };
          } else if (blend1Fuel) {
            newTimeline[year] = { ...yearEntry, blend1Share: "100" };
          } else {
            newTimeline[year] = { ...yearEntry, blend2Share: "100" };
          }
          return;
        }

        // ── 1 blend fuel: binary search for minimum share ─────────────
        if (!blend2Fuel || !b2Props) {
          let lo = 0, hi = 100;
          for (let i = 0; i < 40; i++) {
            const mid = (lo + hi) / 2;
            if (fleetIntensity(mid, 0) <= target) hi = mid;
            else lo = mid;
          }
          const share = Math.ceil(hi * 10) / 10; // round up to 1 decimal
          newTimeline[year] = { ...yearEntry, blend1Share: String(share) };
          return;
        }
        if (!blend1Fuel || !b1Props) {
          let lo = 0, hi = 100;
          for (let i = 0; i < 40; i++) {
            const mid = (lo + hi) / 2;
            if (fleetIntensity(0, mid) <= target) hi = mid;
            else lo = mid;
          }
          const share = Math.ceil(hi * 10) / 10;
          newTimeline[year] = { ...yearEntry, blend2Share: String(share) };
          return;
        }

        // ── 2 blend fuels: cost-aware greedy ─────────────────────────
        // Measure how much intensity reduction 1% of each blend delivers
        // (at baseline = 0% blend). Use cost per unit intensity-drop to rank.
        const intensity0     = fleetIntensity(0, 0);
        const intensityAt1_1 = fleetIntensity(1, 0);
        const intensityAt1_2 = fleetIntensity(0, 1);
        const drop1per1pct   = intensity0 - intensityAt1_1; // gCO2/MJ per 1% of b1
        const drop2per1pct   = intensity0 - intensityAt1_2; // gCO2/MJ per 1% of b2

        // Cost per unit of intensity drop (lower = more efficient)
        // cost here is "extra fuel cost per % share" = costFactor * tons_per_1pct_MJ
        // We approximate: tons = (routeTotalEnergyMJ * 0.01) / lhv
        const tons1per1pct = (routeTotalEnergyMJ * 0.01) / b1Props.lhv;
        const tons2per1pct = (routeTotalEnergyMJ * 0.01) / b2Props.lhv;
        const costPerDrop1 = drop1per1pct > 0 ? (tons1per1pct * b1Props.cost) / drop1per1pct : Infinity;
        const costPerDrop2 = drop2per1pct > 0 ? (tons2per1pct * b2Props.cost) / drop2per1pct : Infinity;

        // Assign the more cost-effective blend first
        // eslint-disable-next-line
        const [primaryBlend, primaryField, secondaryBlend, secondaryField] =
          costPerDrop1 <= costPerDrop2
            ? [blend1Fuel, "blend1Share", blend2Fuel, "blend2Share"]
            : [blend2Fuel, "blend2Share", blend1Fuel, "blend1Share"];

        const isPrimaryBlend1 = primaryField === "blend1Share";

        // Binary-search: how much of the primary do we need (secondary at 0)?
        let lo = 0, hi = 100;
        for (let i = 0; i < 40; i++) {
          const mid = (lo + hi) / 2;
          const s1  = isPrimaryBlend1 ? mid : 0;
          const s2  = isPrimaryBlend1 ? 0 : mid;
          if (fleetIntensity(s1, s2) <= target) hi = mid;
          else lo = mid;
        }
        const primaryShare = Math.ceil(hi * 10) / 10;

        if (primaryShare <= 100) {
          // Primary alone is enough
          newTimeline[year] = {
            ...yearEntry,
            [primaryField]:   String(primaryShare),
            [secondaryField]: "0",
          };
        } else {
          // Need some secondary too — max out primary, binary-search secondary
          let lo2 = 0, hi2 = 100;
          for (let i = 0; i < 40; i++) {
            const mid = (lo2 + hi2) / 2;
            const s1  = isPrimaryBlend1 ? 100 : mid;
            const s2  = isPrimaryBlend1 ? mid : 100;
            if (fleetIntensity(s1, s2) <= target) hi2 = mid;
            else lo2 = mid;
          }
          const secondaryShare = Math.ceil(hi2 * 10) / 10;
          newTimeline[year] = {
            ...yearEntry,
            [primaryField]:   "100",
            [secondaryField]: String(Math.min(secondaryShare, 100)),
          };
        }
      });

      const updatedRoutes = [...materialized];
      updatedRoutes[routeIndex] = { ...route, scenarioTimeline: newTimeline };
      return updatedRoutes;
    });
  }

  // ── FLEET-WIDE BLEND SHARE OPTIMIZER ──────────────────────────────────────
  // Joint optimisation across all routes simultaneously.
  //
  // Mathematical structure (per year):
  //   Minimize  Σ_{route i, blend j}  costRate_ij × s_ij
  //   Subject to:
  //     fleet intensity (linear-fractional in all s_ij) ≤ target
  //     0 ≤ s_ij ≤ 100,  s_i1 + s_i2 ≤ 100
  //
  // Because the constraint (Σ CO2_i(s)) / (Σ MJ_i(s)) ≤ T is equivalent to
  // Σ CO2_i(s) − T/1e6 × Σ MJ_i(s) ≤ 0 (a single LINEAR inequality, since
  // every CO2_i and MJ_i is linear in s), and the objective is linear, this
  // is a tiny LP with a single coupling constraint + box/sum bounds.
  // The optimal solution is the CONTINUOUS KNAPSACK greedy: sort all
  // (route, blend-slot) candidates by cost-per-unit-intensity-reduction and
  // fill cheapest-first until compliance is reached.
  // Complexity: O(k log k) where k ≤ 10 routes × 2 slots × 6 years = 120.
  // Runs in < 1ms.
  function optimizeFleetBlendShares() {
    setSavedRoutes(prev => {
      // First materialize all KEEP_PREVIOUS references fleet-wide
      const materialized = materializeAllPrevious(prev);

      // Build a deep-cloned timeline map so we can mutate freely
      const newTimelines = {};
      materialized.forEach(r => {
        newTimelines[r.id] = JSON.parse(JSON.stringify(r.scenarioTimeline
          ?? createDefaultScenarioTimeline(r.vessel?.fuel?.type || "HFO")));
      });

      function getFuelProps(fuelId) {
        const co2     = fuelParams[fuelId]?.co2  ?? fuelConfig[fuelId]?.co2?.value  ?? 0;
        const lhv     = fuelParams[fuelId]?.lhvMJperT ?? (fuelConfig[fuelId]?.lhv?.value * 1000) ?? 40400;
        const hfoF    = fuelParams[fuelId]?.hfoEquivalentFactor ?? fuelConfig[fuelId]?.hfoEquivalentFactor?.value ?? 1;
        const cost    = fuelParams[fuelId]?.cost ?? fuelConfig[fuelId]?.cost?.value ?? 0;
        const isRfnbo = fuelParams[fuelId]?.isRfnbo ?? fuelConfig[fuelId]?.isRfnbo ?? false;
        return { co2, lhv, hfoF, cost, isRfnbo };
      }

      const hfoLhv = 1000. * fuelConfig.VLSFO.lhv.value; // vlsfoLhv: hfoEqTons × 40,200 = true fuel MJ

      SCENARIO_YEARS.forEach(year => {
        const target = euFactors.yearlyTargets[year] ?? null;
        if (target === null) return;

        // ── Build per-route physical constants for this year ────────────
        // routeData[i] = { id, totalEnergyMJ, coverageFactor, baseFuel,
        //                  blend1Fuel, blend2Fuel,
        //                  baseProps, b1Props, b2Props }
        const routeData = [];

        prev.forEach(r => {
          if (!r.segments || !r.vessel) return;
          const tl = newTimelines[r.id];
          if (!tl || tl[year]?.active === false) return;
          const yearEntry = tl[year] ?? {};
          if (yearEntry.active === false) return;

          const speed = Number(r.vessel?.speed?.value || 0);
          const cons  = Number(r.vessel?.fuel?.consumption || 0);
          const trips = Number(r.operations?.tripsPerYear || 1);
          if (speed <= 0 || cons <= 0) return;

          const vesselFuel = r.vessel?.fuel?.type || "HFO";
          const vesselLhv  = fuelParams[vesselFuel]?.lhvMJperT
            ?? (fuelConfig[vesselFuel]?.lhv?.value * 1000) ?? 40400;

          let totalEnergyMJ = 0;
          r.segments.forEach(seg => {
            const dist = Number(seg.distance || 0) * trips;
            totalEnergyMJ += (dist / speed / 24) * cons * vesselLhv;
          });
          if (totalEnergyMJ <= 0) return;

          const euCat          = getRouteEuCategory(r.segments);
          const coverageFactor = euFactors[euCat] ?? 0;
          // Use materialized yearEntry directly — fuels are already explicit after Step 1
          const baseFuel   = yearEntry.baseFuel   || vesselFuel;
          const blend1Fuel = yearEntry.blend1Fuel || "";
          const blend2Fuel = yearEntry.blend2Fuel || "";

          routeData.push({
            id: r.id,
            totalEnergyMJ,
            coverageFactor,
            baseFuel,
            blend1Fuel,
            blend2Fuel,
            baseProps:  getFuelProps(baseFuel),
            b1Props:    blend1Fuel ? getFuelProps(blend1Fuel) : null,
            b2Props:    blend2Fuel ? getFuelProps(blend2Fuel) : null,
          });
        });

        if (routeData.length === 0) return;

        // ── Compute fleet eligible CO2 and MJ at given shares ───────────
        // shares = Map<routeId, { s1: number, s2: number }>  (0–100)
        // Proportional mode: each voyage's blend enters scope pro-rata by coverageFactor
        function fleetMetricsProp(shares) {
          let totalEligCO2 = 0, totalEligMJ = 0;
          routeData.forEach(rd => {
            const { s1, s2 } = shares.get(rd.id) ?? { s1: 0, s2: 0 };
            const clampedTotal = Math.min(100, s1 + s2);
            const bShare = 100 - clampedTotal;
            const allocs = [];
            if (bShare > 0) allocs.push({ p: rd.baseProps, share: bShare });
            if (rd.blend1Fuel && s1 > 0) allocs.push({ p: rd.b1Props, share: s1 });
            if (rd.blend2Fuel && s2 > 0) allocs.push({ p: rd.b2Props, share: s2 });

            allocs.forEach(({ p, share }) => {
              const fuelMJ  = rd.totalEnergyMJ * (share / 100);
              const tons    = fuelMJ / p.lhv;
              totalEligCO2 += tons * p.co2 * rd.coverageFactor;
              const bonusMJ = (p.isRfnbo && year <= 2030) ? fuelMJ : 0;
              totalEligMJ  += (tons * p.hfoF * hfoLhv + bonusMJ * rd.coverageFactor) * rd.coverageFactor;
            });
          });
          return { totalEligCO2, totalEligMJ };
        }

        // Optimal mode: intra-EU fuel is fixed; mixed budget filled greedily,
        // cleanest fuel first. Mirrors computeFuelEuPenaltyOptimal logic.
        function fleetMetricsOptimal(shares) {
          let fixedEligCO2 = 0, fixedEligMJ = 0;
          let mixedBudgetMJ = 0;
          const mixedSegments = [];

          routeData.forEach(rd => {
            const { s1, s2 } = shares.get(rd.id) ?? { s1: 0, s2: 0 };
            const clampedTotal = Math.min(100, s1 + s2);
            const bShare = 100 - clampedTotal;
            const allocs = [];
            if (bShare > 0) allocs.push({ p: rd.baseProps, share: bShare });
            if (rd.blend1Fuel && s1 > 0) allocs.push({ p: rd.b1Props, share: s1 });
            if (rd.blend2Fuel && s2 > 0) allocs.push({ p: rd.b2Props, share: s2 });

            // Determine EU category from routeData (stored as coverageFactor; derive category)
            const euCat = rd.coverageFactor >= 1 ? "inter" : rd.coverageFactor > 0 ? "mixed" : "out";

            allocs.forEach(({ p, share }) => {
              const fuelMJ  = rd.totalEnergyMJ * (share / 100);
              const tons    = fuelMJ / p.lhv;
              const co2     = tons * p.co2;
              const hfoEqMJ = tons * p.hfoF * hfoLhv;
              // RFNBO: credited MJ = 2× physical (bonus added to denominator)
              const bonusMJ = (p.isRfnbo && year <= 2030) ? fuelMJ : 0;
              const accountedMJ = hfoEqMJ + bonusMJ;
              const co2PerMJ = accountedMJ > 0 ? co2 / accountedMJ : 0;

              if (euCat === "inter") {
                fixedEligCO2 += co2;
                fixedEligMJ  += accountedMJ;
              } else if (euCat === "mixed") {
                mixedBudgetMJ += accountedMJ * (euFactors.mixed ?? 0.5);
                mixedSegments.push({ mjAvailable: accountedMJ, co2PerMJ });
              }
            });
          });

          // Greedy fill mixed budget
          mixedSegments.sort((a, b) => a.co2PerMJ - b.co2PerMJ);
          let remaining = mixedBudgetMJ;
          let allocMJ = 0, allocCO2 = 0;
          for (const seg of mixedSegments) {
            if (remaining <= 0) break;
            const take = Math.min(seg.mjAvailable, remaining);
            allocMJ  += take;
            allocCO2 += take * seg.co2PerMJ;
            remaining -= take;
          }

          return {
            totalEligCO2: fixedEligCO2 + allocCO2,
            totalEligMJ:  fixedEligMJ  + allocMJ,
          };
        }

        // Use the allocation mode the user has selected
        function fleetMetrics(shares) {
          return useOptimalAllocation
            ? fleetMetricsOptimal(shares)
            : fleetMetricsProp(shares);
        }

        // ── Start: all blend shares at zero ─────────────────────────────
        const shares = new Map();
        routeData.forEach(rd => shares.set(rd.id, { s1: 0, s2: 0 }));

        const { totalEligCO2: initCO2, totalEligMJ: initMJ } = fleetMetrics(shares);
        if (initMJ <= 0) return; // no EU-covered routes for this year — nothing to optimise, leave shares untouched

        const initIntensity = (initCO2 / initMJ) * 1_000_000;
        if (initIntensity <= target) {
          // Fleet already compliant at 0% blend — zero out shares only for
          // EU-covered routes that have blend fuels — set shares to 0, leave fuels untouched.
          routeData.forEach(rd => {
            if (rd.coverageFactor === 0) return;
            const tl = newTimelines[rd.id];
            const existing = tl[year] ?? {};
            const patch = { ...existing };
            if (rd.blend1Fuel) patch.blend1Share = "0";
            if (rd.blend2Fuel) patch.blend2Share = "0";
            tl[year] = patch;
          });
          return;
        }

        // ── Build candidate list: one entry per (route × blend-slot) ────
        // Each candidate has a marginal efficiency = dI/ds at s=0 and
        // a marginal cost = extra cost per 1% shift.
        // We linearise at s=0 (valid because CO2 and MJ are linear in s).
        const candidates = [];
        routeData.forEach(rd => {
          [
            { slotFuel: rd.blend1Fuel, slotProps: rd.b1Props, slot: 1 },
            { slotFuel: rd.blend2Fuel, slotProps: rd.b2Props, slot: 2 },
          ].forEach(({ slotFuel, slotProps, slot }) => {
            if (!slotFuel || !slotProps) return;

            // Intensity change when this slot goes from 0% → 1%:
            // Replace 1% of base with 1% of blend.
            // ΔeligCO2 per 1% = covF × (tons_blend × co2_blend − tons_base × co2_base)
            // where tons = (totalEnergyMJ × 0.01) / lhv
            const deltaEnergyMJ = rd.totalEnergyMJ * 0.01;
            const tonsBlend     = deltaEnergyMJ / slotProps.lhv;
            const tonsBase      = deltaEnergyMJ / rd.baseProps.lhv;
            const deltaCO2      = rd.coverageFactor * (tonsBlend * slotProps.co2 - tonsBase * rd.baseProps.co2);
            const deltaMJ       = rd.coverageFactor * (tonsBlend * slotProps.hfoF - tonsBase * rd.baseProps.hfoF) * hfoLhv;

            // Fleet intensity change per 1% shift (linearised; negative = good)
            // dI/ds ≈ (totalEligCO2 + deltaCO2) / (totalEligMJ + deltaMJ) − initIntensity
            const newI = ((initCO2 + deltaCO2) / (initMJ + deltaMJ)) * 1_000_000;
            const intensityDropPer1pct = initIntensity - newI; // > 0 means blend lowers intensity

            if (intensityDropPer1pct <= 0) return; // blend is dirtier than base — skip

            // Extra cost per 1% blend = (tonsBlend × costBlend − tonsBase × costBase)
            const extraCostPer1pct = tonsBlend * slotProps.cost - tonsBase * rd.baseProps.cost;

            // Cost-effectiveness = extra cost / intensity drop (lower = cheaper compliance)
            const costEfficiency = extraCostPer1pct / intensityDropPer1pct;

            candidates.push({
              routeId: rd.id,
              slot,            // 1 or 2
              slotFuel,
              intensityDropPer1pct,
              extraCostPer1pct,
              costEfficiency,
            });
          });
        });

        if (candidates.length === 0) return; // no useful blends

        // Sort cheapest-per-unit-drop first
        candidates.sort((a, b) => a.costEfficiency - b.costEfficiency);

        // ── Greedy continuous knapsack fill ─────────────────────────────
        // Current state: shares (all zero). Fill each candidate up to its
        // max allowable share, rechecking compliance after each.
        // Max share for a slot = 100 minus whatever the OTHER slot already has.
        function getMaxShare(routeId, slot) {
          const cur = shares.get(routeId) ?? { s1: 0, s2: 0 };
          const other = slot === 1 ? cur.s2 : cur.s1;
          return Math.max(0, 100 - other);
        }

        for (const cand of candidates) {
          const maxShare = getMaxShare(cand.routeId, cand.slot);
          if (maxShare <= 0) continue;

          // How much of this candidate do we need to hit compliance exactly?
          // Binary search the minimum additional share in [0, maxShare].
          let lo = 0, hi = maxShare;
          for (let iter = 0; iter < 40; iter++) {
            const mid = (lo + hi) / 2;
            const testShares = new Map(shares);
            const cur = testShares.get(cand.routeId) ?? { s1: 0, s2: 0 };
            testShares.set(cand.routeId, {
              s1: cand.slot === 1 ? cur.s1 + mid : cur.s1,
              s2: cand.slot === 2 ? cur.s2 + mid : cur.s2,
            });
            const { totalEligCO2: tCO2, totalEligMJ: tMJ } = fleetMetrics(testShares);
            const I = tMJ > 0 ? (tCO2 / tMJ) * 1_000_000 : Infinity;
            if (I <= target) hi = mid;
            else lo = mid;
          }

          // Apply hi (minimum share needed from this candidate)
          const addShare = Math.ceil(hi * 10) / 10; // round up to 1 decimal
          const cur = shares.get(cand.routeId) ?? { s1: 0, s2: 0 };
          shares.set(cand.routeId, {
            s1: cand.slot === 1 ? Math.min(100, cur.s1 + addShare) : cur.s1,
            s2: cand.slot === 2 ? Math.min(100, cur.s2 + addShare) : cur.s2,
          });

          // Re-check fleet compliance with updated shares
          const { totalEligCO2: tCO2, totalEligMJ: tMJ } = fleetMetrics(shares);
          const currentIntensity = tMJ > 0 ? (tCO2 / tMJ) * 1_000_000 : Infinity;
          if (currentIntensity <= target) break; // done — compliance reached
        }

        // ── Write results back into timeline ────────────────────────────
        // Only write shares — never fuel names. Fuels stay KEEP_PREVIOUS so
        // the cascade from year[0] is preserved. User changes fuel once in
        // year[0] and all years inherit it automatically.
        routeData.forEach(rd => {
          if (!rd.blend1Fuel && !rd.blend2Fuel) return;
          if (rd.coverageFactor === 0) return;

          const { s1, s2 } = shares.get(rd.id) ?? { s1: 0, s2: 0 };
          const tl = newTimelines[rd.id];
          const existing = tl[year] ?? {};
          const patch = { ...existing };

          if (rd.blend1Fuel) patch.blend1Share = String(s1);
          if (rd.blend2Fuel) patch.blend2Share = String(s2);

          tl[year] = patch;
        });
      }); // end SCENARIO_YEARS loop

      // Apply updated timelines to routes
      return materialized.map(r =>
        newTimelines[r.id]
          ? { ...r, scenarioTimeline: newTimelines[r.id] }
          : r
      );
    });
    showSnackbar("Fleet blend optimisation complete — shares updated for all active years.", "success");
  }

  function handleFinishRoute() {
    // finish building
    dispatch({ type: "FINISH" });

    // prevent empty saves
    if (!route.ports.length) return;

    const newRoute = {
      id: Date.now(),
      name: pendingRouteName || "Unnamed Voyage",
      ports: slimPorts(route.ports),
      segments: buildSegmentsFromPortIds(route.ports.map(p => p.id)),
      vessel: {
        preset: "feeder",
        name: "",
        type: "Container",
        capacity: { value: 1000, unit: "TEU" },
        speed: { value: 16, unit: "kn" },
        fuel: { type: "MGO", consumption: 25 }
      },
      scenario: {
        alt1: "",
        share1: 50,
        alt2: "",
        share2: 50
      },
      scenarioTimeline:
        createDefaultScenarioTimeline("MGO"
        ),
      operations: {
        tripsPerYear: 1
      }
    };

    setSavedRoutes(prev => [...prev, newRoute]);
    setPendingRouteName("");

    // reset builder
    dispatch({ type: "RESET" });
  }

  function updateVesselPreset(routeId, presetId) {
      setSavedRoutes(prev =>
        prev.map(r => {
          if (r.id !== routeId) return r;

          const preset = VESSEL_PRESETS.find(p => p.id === presetId);
          if (!preset) return r;

          const newFuelType = preset.data?.fuel?.type;
          const newConsumption = preset.data?.fuel?.consumption;

          // If preset defines a fuel type different from current, rescale consumption by LHV ratio
          let presetData = preset.data || {};
          if (newFuelType && newFuelType !== r.vessel.fuel.type && newConsumption === undefined) {
            const getLhv = id => fuelParams[id]?.lhvMJperT ?? ((fuelConfig[id]?.lhv?.value ?? 40.5) * 1000);
            const lhvOld = getLhv(r.vessel.fuel.type);
            const lhvNew = getLhv(newFuelType);
            const rescaled = lhvNew > 0 ? parseFloat((r.vessel.fuel.consumption * lhvOld / lhvNew).toFixed(2)) : r.vessel.fuel.consumption;
            presetData = { ...presetData, fuel: { ...presetData.fuel, consumption: rescaled } };
          }

          return {
            ...r,
            vessel: {
              ...r.vessel,
              preset: presetId,
              ...presetData,
            },
            ...(newFuelType ? {
              scenarioTimeline: {
                ...r.scenarioTimeline,
                [SCENARIO_YEARS[0]]: {
                  ...r.scenarioTimeline?.[SCENARIO_YEARS[0]],
                  baseFuel: newFuelType,
                },
              }
            } : {}),
          };
        })
      );
    }

  function updateVesselField(routeId, path, value) {
    setSavedRoutes(prev =>
      prev.map(r => {
        if (r.id !== routeId) return r;

        const updated = { ...r.vessel };

        if (path === "capacity") updated.capacity.value = value;
        if (path === "speed") updated.speed.value = value;
        if (path === "fuelConsumption") updated.fuel.consumption = value;
        if (path === "fuelType") {
          const oldFuelId = r.vessel.fuel.type;
          const newFuelId = value;
          // Get LHV for old and new fuel (sidebar override first, then fuelConfig)
          const getLhv = id => fuelParams[id]?.lhvMJperT ?? ((fuelConfig[id]?.lhv?.value ?? 40.5) * 1000);
          const lhvOld = getLhv(oldFuelId);
          const lhvNew = getLhv(newFuelId);
          // Rescale consumption to maintain same energy output: cons_new = cons_old × (lhvOld / lhvNew)
          const oldCons = r.vessel.fuel.consumption;
          const newCons = lhvNew > 0 ? parseFloat((oldCons * lhvOld / lhvNew).toFixed(2)) : oldCons;
          updated.fuel.type = newFuelId;
          updated.fuel.consumption = newCons;
          // Also set the first scenario year's base fuel to match
          return {
            ...r,
            vessel: updated,
            scenarioTimeline: {
              ...r.scenarioTimeline,
              [SCENARIO_YEARS[0]]: {
                ...r.scenarioTimeline?.[SCENARIO_YEARS[0]],
                baseFuel: value,
              },
            },
          };
        }
        if (path === "name") updated.name = value;
        if (path === "type") updated.type = value;

        return { ...r, vessel: updated };
      })
    );
  }


  // Lightweight check: was a blend fuel picked for this route/year, regardless
  // of whether a share % was entered yet? Mirrors the same KEEP_PREVIOUS
  // walk-back as resolveScenarioYear, but — unlike it — does NOT discard the
  // fuel when its share is empty/zero (that guard is only meant to protect
  // the actual blend calculations from a stray zero-share leaking in).
  function yearHasBlendFuelSelected(route, year) {
    const timeline = route.scenarioTimeline || {};
    const yearIndex = SCENARIO_YEARS.indexOf(year);
    if (yearIndex === -1) return false;
    if (timeline[year]?.active === false) return false;

    function resolveFuelField(field) {
      for (let i = yearIndex; i >= 0; i--) {
        const value = timeline?.[SCENARIO_YEARS[i]]?.[field];
        if (value !== undefined && value !== null && value !== KEEP_PREVIOUS) {
          return value;
        }
      }
      return "";
    }

    return !!resolveFuelField("blend1Fuel") || !!resolveFuelField("blend2Fuel");
  }

  function resolveScenarioYear(
      route,
      targetYear
    ) {

      const timeline =
        route.scenarioTimeline || {};

      const yearIndex =
        SCENARIO_YEARS.indexOf(targetYear);

      if (yearIndex === -1) {
        return null;
      }

      // If explicitly deactivated, treat as if this route doesn't run this year
      if (timeline[targetYear]?.active === false) {
        return null;
      }

      const resolved = {};

      function resolveField(field) {

        for (
          let i = yearIndex;
          i >= 0;
          i--
        ) {

          const year =
            SCENARIO_YEARS[i];

          const value =
            timeline?.[year]?.[field];

          if (
            value !== undefined && value !== null &&
            value !== KEEP_PREVIOUS
          ) {
            return value;
          }
        }

        return "";
      }

      
          resolved.baseFuel =
            resolveField("baseFuel");
      

      // resolved.baseFuel =resolveField("baseFuel");

      /* BLEND 1 — fuel and share resolved independently */
      const rawBlend1 = timeline?.[targetYear]?.blend1Fuel;

      if (rawBlend1 === KEEP_PREVIOUS) {
        // Walk back to find the last explicitly set fuel (including "" = None)
        resolved.blend1Fuel = "";
        for (let i = yearIndex - 1; i >= 0; i--) {
          const prev = timeline?.[SCENARIO_YEARS[i]];
          if (prev && prev.blend1Fuel !== undefined && prev.blend1Fuel !== null && prev.blend1Fuel !== KEEP_PREVIOUS) {
            resolved.blend1Fuel = prev.blend1Fuel;
            break;
          }
        }
        // Share: only resolve if fuel is non-empty; otherwise discard
        if (resolved.blend1Fuel) {
          const ownShare = timeline?.[targetYear]?.blend1Share;
          resolved.blend1Share = (ownShare && ownShare !== KEEP_PREVIOUS)
            ? ownShare
            : resolveField("blend1Share");
        } else {
          resolved.blend1Share = "";
        }
      } else {
        resolved.blend1Fuel = resolveField("blend1Fuel");
        // Share: only resolve if fuel is non-empty; otherwise discard
        if (resolved.blend1Fuel) {
          const ownShare = timeline?.[targetYear]?.blend1Share;
          resolved.blend1Share = (!ownShare || ownShare === KEEP_PREVIOUS)
            ? resolveField("blend1Share")
            : ownShare;
        } else {
          resolved.blend1Share = "";
        }
      }

      /* BLEND 2 — fuel and share resolved independently */
      const rawBlend2 = timeline?.[targetYear]?.blend2Fuel;

      if (rawBlend2 === KEEP_PREVIOUS) {
        // Walk back to find the last explicitly set fuel (including "" = None)
        resolved.blend2Fuel = "";
        for (let i = yearIndex - 1; i >= 0; i--) {
          const prev = timeline?.[SCENARIO_YEARS[i]];
          if (prev && prev.blend2Fuel !== undefined && prev.blend2Fuel !== null && prev.blend2Fuel !== KEEP_PREVIOUS) {
            resolved.blend2Fuel = prev.blend2Fuel;
            break;
          }
        }
        // Share: only resolve if fuel is non-empty; otherwise discard
        if (resolved.blend2Fuel) {
          const ownShare = timeline?.[targetYear]?.blend2Share;
          resolved.blend2Share = (ownShare && ownShare !== KEEP_PREVIOUS)
            ? ownShare
            : resolveField("blend2Share");
        } else {
          resolved.blend2Share = "";
        }
      } else {
        resolved.blend2Fuel = resolveField("blend2Fuel");
        // Share: only resolve if fuel is non-empty; otherwise discard
        if (resolved.blend2Fuel) {
          const ownShare = timeline?.[targetYear]?.blend2Share;
          resolved.blend2Share = (!ownShare || ownShare === KEEP_PREVIOUS)
            ? resolveField("blend2Share")
            : ownShare;
        } else {
          resolved.blend2Share = "";
        }
      }

      // Final guard: if share is empty or zero, discard the fuel entirely
      // This prevents a named fuel with no share from leaking into calculations
      if (!resolved.blend1Share || parseFloat(resolved.blend1Share) <= 0) {
        resolved.blend1Fuel  = "";
        resolved.blend1Share = "";
      }
      if (!resolved.blend2Share || parseFloat(resolved.blend2Share) <= 0) {
        resolved.blend2Fuel  = "";
        resolved.blend2Share = "";
      }

      return resolved;
    }


  const selectedRoutes =
    route.status === "idle"
      ? savedRoutes.filter(r => selectedRouteIds.has(r.id))
      : [];

  

    const blendedFuels =
    computeScenarioFuelTable();
    console.log(blendedFuels)


  console.log(buildChartData());



  // Part of calculation
  function renderScenarioConfiguration() {

  return (

      <div
          style={{
            paddingRight: 8,
            minHeight: 0
          }}
        >

        <div className="modal-body">

          {/* ================================================= */}
          {/* ANNUAL DISTANCE */}
          {/* ================================================= */}

          <div className="section-header">
            <span className="section-label">
              Annual Distance
            </span>

            <div className="section-line" />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              marginBottom: 24
            }}
          >
            <span>Intra EU</span>
            <span>
              {fleetSummary.miles.inter.toFixed(0)} nm
            </span>

            <span>Extra EU</span>
            <span>
              {fleetSummary.miles.mixed.toFixed(0)} nm
            </span>

            <span>Out of Scope</span>
            <span>
              {fleetSummary.miles.out.toFixed(0)} nm
            </span>

            <span style={{ color: "var(--teal-300)" }}>
              Total
            </span>

            <span style={{ color: "var(--teal-300)" }}>
              {fleetSummary.miles.total.toFixed(0)} nm
            </span>
          </div>


          {/* ================================================= */}
          {/* FUEL BLENDING */}
          {/* ================================================= */}

          <div className="section-header" ref={el => tourRefs.current["tour-blend-section"] = el}>
            <span className="section-label">
              Fuel Blending Timeline
            </span>

            <div className="section-line" />

            {/* Allocation mode toggle — mirror of the Allocation tab switch */}
            <div
              title="Toggle allocation method used for FuelEU penalty calculations — also controls how ⚡ Optimize Fleet targets compliance"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: useOptimalAllocation ? "rgba(29,158,117,0.08)" : "rgba(148,163,184,0.06)",
                border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.35)" : "rgba(148,163,184,0.2)"}`,
                borderRadius: 4, padding: "3px 8px", flexShrink: 0, cursor: "pointer",
                transition: "all 0.15s",
              }}
              onClick={() => setUseOptimalAllocation(v => !v)}
            >
              <span style={{
                fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px",
                textTransform: "uppercase", color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
              }}>
                {useOptimalAllocation ? "⚡ optimal" : "⇌ proportional"}
              </span>
              <div style={{
                width: 28, height: 16, borderRadius: 8, position: "relative",
                background: useOptimalAllocation ? "#1D9E75" : "rgba(148,163,184,0.3)",
                border: `1px solid ${useOptimalAllocation ? "#0F6E56" : "rgba(148,163,184,0.4)"}`,
                transition: "background .2s, border .2s", flexShrink: 0,
              }}>
                <div style={{
                  position: "absolute", top: 2, left: useOptimalAllocation ? 13 : 2,
                  width: 10, height: 10, borderRadius: "50%",
                  background: "#fff", transition: "left .2s",
                }} />
              </div>
            </div>

            {/* Global reset button — resets all voyages' blend years 2030–2050 to first-year values */}
            {savedRoutes.length > 0 && (
              <button
                title="Reset all voyages: set all years 2030–2050 to ↩ Previous fuel and clear blend shares"
                onClick={() => {
                  setSavedRoutes(prev => prev.map(r => {
                    const firstYear = SCENARIO_YEARS[0];
                    const first = r.scenarioTimeline?.[firstYear] ?? {};
                    const updated = { ...r.scenarioTimeline };
                    SCENARIO_YEARS.slice(1).forEach(year => {
                      updated[year] = {
                        ...updated[year],
                        baseFuel:    KEEP_PREVIOUS,
                        blend1Fuel:  first.blend1Fuel  || KEEP_PREVIOUS,
                        blend1Share: first.blend1Share || "",
                        blend2Fuel:  first.blend2Fuel  || KEEP_PREVIOUS,
                        blend2Share: first.blend2Share || "",
                      };
                    });
                    return { ...r, scenarioTimeline: updated };
                  }));
                }}
                style={{
                  background: "rgba(148,163,184,0.06)",
                  border: "1px solid rgba(148,163,184,0.25)",
                  borderRadius: 4,
                  color: "var(--slate-400)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  padding: "3px 10px",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(148,163,184,0.12)"; e.currentTarget.style.borderColor = "rgba(148,163,184,0.5)"; e.currentTarget.style.color = "var(--slate-300)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(148,163,184,0.06)"; e.currentTarget.style.borderColor = "rgba(148,163,184,0.25)"; e.currentTarget.style.color = "var(--slate-400)"; }}
              >
                ↺ Reset All to 2025
              </button>
            )}
            {(() => {
              const canOptimize = savedRoutes.some(r =>
                SCENARIO_YEARS.some(year => yearHasBlendFuelSelected(r, year))
              );
              return (
                <button
                  ref={el => tourRefs.current["tour-optimize-btn"] = el}
                  title={
                    canOptimize
                      ? `Jointly optimise all voyages' blend shares across all years to meet FuelEU targets at minimum total fuel cost — using ${useOptimalAllocation ? "optimal (cleanest-first)" : "proportional"} allocation`
                      : "Select a blend fuel for at least one voyage/year first"
                  }
                  onClick={optimizeFleetBlendShares}
                  disabled={!canOptimize}
                  style={{
                    background: canOptimize ? "rgba(251,191,36,0.08)" : "rgba(148,163,184,0.06)",
                    border: `1px solid ${canOptimize ? "rgba(251,191,36,0.45)" : "rgba(148,163,184,0.2)"}`,
                    borderRadius: 4,
                    color: canOptimize ? "#fbbf24" : "var(--slate-400)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    cursor: canOptimize ? "pointer" : "not-allowed",
                    opacity: canOptimize ? 1 : 0.5,
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (canOptimize) e.currentTarget.style.background = "rgba(251,191,36,0.15)"; }}
                  onMouseLeave={e => { if (canOptimize) e.currentTarget.style.background = "rgba(251,191,36,0.08)"; }}
                >
                  ⚡ Optimize Fleet
                </button>
              );
            })()}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              marginBottom: 32
            }}
          >

            {savedRoutes.map(route => {

              const timeline =
                route.scenarioTimeline ||
                createDefaultScenarioTimeline(
                  route.scenario.baseFuel || route.vessel.fuel.type
                );

              const resolvedTimeline = {};

                SCENARIO_YEARS.forEach(year => {
                  resolvedTimeline[year] =
                    resolveScenarioYear(
                      route,
                      year
                    );
                });

              return (
                <div
                  key={route.id}
                  ref={savedRoutes.indexOf(route) === 0 ? el => tourRefs.current["tour-first-voyage-card"] = el : null}
                  style={{
                    border:
                      "1px solid rgba(45,212,191,0.12)",
                    borderRadius: 4,
                    padding: 14
                  }}
                >

                  {/* ROUTE TITLE + OPTIMIZE BUTTON */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        color: "var(--teal-300)"
                      }}
                    >
                      {route.name}
                    </div>

                    {/* Per-route optimize button hidden — use ⚡ Optimize Fleet instead */}
                    {false && (() => {
                      const hasBlend = SCENARIO_YEARS.some(year => {
                        if (timeline[year]?.active === false) return false;
                        const res = resolvedTimeline[year];
                        return (res?.blend1Fuel && res.blend1Fuel !== "") ||
                               (res?.blend2Fuel && res.blend2Fuel !== "");
                      });
                      if (!hasBlend) return null;
                      return (
                        <button
                          title="Calculate the minimum blend share(s) needed to comply with FuelEU intensity targets for each year, minimising extra fuel cost"
                          onClick={() => optimizeBlendShares(route.id)}
                          style={{
                            background: "rgba(45,212,191,0.08)",
                            border: "1px solid rgba(45,212,191,0.4)",
                            borderRadius: 4,
                            color: "var(--teal-300)",
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            letterSpacing: "0.8px",
                            textTransform: "uppercase",
                            padding: "4px 10px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(45,212,191,0.16)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(45,212,191,0.08)"; }}
                        >
                          ⚡ Optimize Blend
                        </button>
                      );
                    })()}
                  </div>

                  {/* YEARS */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        `180px repeat(${SCENARIO_YEARS.length}, 1fr) 28px`,
                      gap: 10,
                      alignItems: "center",
                    }}
                  >

                    {/* HEADER */}
                    <div />
                    {SCENARIO_YEARS.map(year => {
                      const isActive = timeline[year]?.active !== false;
                      return (
                        <div
                          key={year}
                          style={{
                            textAlign: "center",
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: isActive ? "var(--teal-300)" : "var(--slate-400)",
                            opacity: isActive ? 1 : 0.45,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span>{year}</span>
                          <button
                            title={isActive ? "Deactivate this year" : "Activate this year"}
                            onClick={() =>
                              updateScenarioTimeline(route.id, year, "active", !isActive)
                            }
                            style={{
                              background: isActive
                                ? "rgba(45,212,191,0.12)"
                                : "rgba(148,163,184,0.08)",
                              border: isActive
                                ? "1px solid rgba(45,212,191,0.35)"
                                : "1px solid rgba(148,163,184,0.2)",
                              borderRadius: 3,
                              color: isActive ? "var(--teal-300)" : "var(--slate-400)",
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              letterSpacing: "0.5px",
                              padding: "2px 6px",
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {isActive ? "✓ ON" : "✗ OFF"}
                          </button>
                        </div>
                      );
                    })}
                    <div />{/* reset column header — empty */}

                    {/* BASE FUEL */}
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11
                      }}
                    >
                      Base Fuel
                    </div>

                    {SCENARIO_YEARS.map(year => {
                      const isFirstYear = year === SCENARIO_YEARS[0];
                      const isActive    = timeline[year]?.active !== false;
                      const storedVal   = timeline[year]?.baseFuel;
                      const displayVal  = (!storedVal || storedVal === KEEP_PREVIOUS)
                        ? "__PREVIOUS__"
                        : storedVal;
                      return (
                        <select
                          key={year}
                          className="vessel-input"
                          disabled={!isActive}
                          style={{ opacity: isActive ? 1 : 0.3 }}
                          value={displayVal}
                          onChange={e =>
                            updateScenarioTimeline(
                              route.id, year, "baseFuel",
                              e.target.value === "__PREVIOUS__" ? null : e.target.value
                            )
                          }
                        >
                          {!isFirstYear && (
                            <option value="__PREVIOUS__">↩ Previous</option>
                          )}
                          {isFirstYear && (
                            <option value="__PREVIOUS__" disabled>— select —</option>
                          )}
                          {renderFuelOptions()}
                          {/* Fallback: render stored base fuel if not in standard options */}
                          {displayVal && displayVal !== "__PREVIOUS__" &&
                            !SCENARIO_FUELS.find(f => f.id === displayVal) && (
                            <option value={displayVal}>{displayVal}</option>
                          )}
                        </select>
                      );
                    })}
                    <div />{/* base fuel — no reset button */}

                    {/* BLEND 1 */}
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                      Blend Fuel 1
                    </div>

                    {SCENARIO_YEARS.map(year => {
                      const isFirstYear = year === SCENARIO_YEARS[0];
                      const isActive    = timeline[year]?.active !== false;
                      const storedFuel  = timeline[year]?.blend1Fuel;
                      const isKeep      = storedFuel === KEEP_PREVIOUS;
                      const displayFuel = isKeep ? "__PREVIOUS__" : (storedFuel || "");
                      const displayShare = (() => {
                        const raw = timeline[year]?.blend1Share;
                        return (!raw || raw === KEEP_PREVIOUS) ? "" : raw;
                      })();
                      return (
                        <div key={year} style={{ display: "flex", gap: 4, opacity: isActive ? 1 : 0.3, alignItems: "center" }}>
                          <select
                            className="vessel-input"
                            disabled={!isActive}
                            value={displayFuel}
                            style={{ flex: 1 }}
                            onChange={e => {
                              const val = e.target.value;
                              updateScenarioTimeline(route.id, year, "blend1Fuel",
                                val === "__PREVIOUS__" ? KEEP_PREVIOUS : val);
                            }}
                          >
                            <option value="">None</option>
                            {!isFirstYear && <option value="__PREVIOUS__">↩ Previous</option>}
                            {renderFuelOptions()}
                            {/* Fallback: render stored fuel if not in standard options */}
                            {displayFuel && displayFuel !== "__PREVIOUS__" &&
                              !SCENARIO_FUELS.find(f => f.id === displayFuel) && (
                              <option value={displayFuel}>{displayFuel}</option>
                            )}
                          </select>
                          <div style={{ position: "relative", width: 72, flexShrink: 0 }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="vessel-input"
                              style={{ paddingRight: 20, width: "100%" }}
                              disabled={!isActive || !displayFuel || displayFuel === "__PREVIOUS__"}
                              value={displayShare}
                              onChange={e => {
                                const value = e.target.value.replace(",", ".");
                                if (value === "" || /^\d*\.?\d*$/.test(value))
                                  updateScenarioTimeline(route.id, year, "blend1Share", value);
                              }}
                            />
                            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--slate-400)" }}>%</span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Reset button — Blend 1 */}
                    <button
                      title="Reset all years to first year: sets fuel = ↩ Previous and clears share for years 2030–2050"
                      onClick={() => {
                        SCENARIO_YEARS.slice(1).forEach(year => {
                          updateScenarioTimeline(route.id, year, "blend1Fuel", KEEP_PREVIOUS);
                          updateScenarioTimeline(route.id, year, "blend1Share", "");
                        });
                      }}
                      style={{
                        background: "none", border: "1px solid rgba(148,163,184,0.25)",
                        borderRadius: 4, padding: "3px 4px", cursor: "pointer",
                        color: "var(--slate-400)", fontSize: 13, lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--teal-400)"; e.currentTarget.style.color = "var(--teal-300)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.25)"; e.currentTarget.style.color = "var(--slate-400)"; }}
                    >
                      ↺
                    </button>

                    {/* BLEND 2 */}
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                      Blend Fuel 2
                    </div>

                    {SCENARIO_YEARS.map(year => {
                      const isFirstYear = year === SCENARIO_YEARS[0];
                      const isActive    = timeline[year]?.active !== false;
                      const storedFuel  = timeline[year]?.blend2Fuel;
                      const isKeep      = storedFuel === KEEP_PREVIOUS;
                      const displayFuel = isKeep ? "__PREVIOUS__" : (storedFuel || "");
                      const displayShare = (() => {
                        const raw = timeline[year]?.blend2Share;
                        return (!raw || raw === KEEP_PREVIOUS) ? "" : raw;
                      })();
                      return (
                        <div key={year} style={{ display: "flex", gap: 4, opacity: isActive ? 1 : 0.3, alignItems: "center" }}>
                          <select
                            className="vessel-input"
                            disabled={!isActive}
                            value={displayFuel}
                            style={{ flex: 1 }}
                            onChange={e => {
                              const val = e.target.value;
                              updateScenarioTimeline(route.id, year, "blend2Fuel",
                                val === "__PREVIOUS__" ? KEEP_PREVIOUS : val);
                            }}
                          >
                            <option value="">None</option>
                            {!isFirstYear && <option value="__PREVIOUS__">↩ Previous</option>}
                            {renderFuelOptions()}
                            {/* Fallback: render stored fuel if not in standard options */}
                            {displayFuel && displayFuel !== "__PREVIOUS__" &&
                              !SCENARIO_FUELS.find(f => f.id === displayFuel) && (
                              <option value={displayFuel}>{displayFuel}</option>
                            )}
                          </select>
                          <div style={{ position: "relative", width: 72, flexShrink: 0 }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="vessel-input"
                              style={{ paddingRight: 20, width: "100%" }}
                              disabled={!isActive || !displayFuel || displayFuel === "__PREVIOUS__"}
                              value={displayShare}
                              onChange={e => {
                                const value = e.target.value.replace(",", ".");
                                if (value === "" || /^\d*\.?\d*$/.test(value))
                                  updateScenarioTimeline(route.id, year, "blend2Share", value);
                              }}
                            />
                            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--slate-400)" }}>%</span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Reset button — Blend 2 */}
                    <button
                      title="Reset all years to first year: sets fuel = ↩ Previous and clears share for years 2030–2050"
                      onClick={() => {
                        SCENARIO_YEARS.slice(1).forEach(year => {
                          updateScenarioTimeline(route.id, year, "blend2Fuel", KEEP_PREVIOUS);
                          updateScenarioTimeline(route.id, year, "blend2Share", "");
                        });
                      }}
                      style={{
                        background: "none", border: "1px solid rgba(148,163,184,0.25)",
                        borderRadius: 4, padding: "3px 4px", cursor: "pointer",
                        color: "var(--slate-400)", fontSize: 13, lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--teal-400)"; e.currentTarget.style.color = "var(--teal-300)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.25)"; e.currentTarget.style.color = "var(--slate-400)"; }}
                    >
                      ↺
                    </button>

                  </div>

                </div>
              );
            })}
          </div>


          

        </div>
      </div>

    );
  }



  // Part of calculation
  function renderScenarioTables() {

    const blended = computeScenarioFuelTable();
    const { baselineCO2, baselineCost } = buildFleetBaseline(selectedScenarioYear);

    // Use the escalated (consecutive non-compliance) penalty for the selected year
    const allYearPenalties = computeAllYearPenalties();
    const yearPenaltyData  = allYearPenalties.get(selectedScenarioYear);
    // eslint-disable-next-line
    const { penaltyUsd: etsPenalty, penaltyEur: etsPenaltyEur, actualIntensity, targetIntensity, excessCO2, excessVLSFO, isCompliant, multiplier: penaltyMultiplier, basePenaltyEur, consecutiveYears: consecutiveNonCompliantYears } = yearPenaltyData;

    const blendedFuelEntries = Object.entries(blended.fuels);

    // RFNBO-adjusted intensities for both allocation modes (shown as sub-text on FuelEU card)
    const blendedForYear = calculateBlendedFuels(selectedScenarioYear);
    const hasAnyRfnbo = Object.values(blendedForYear.fuels).some(v => v.isRfnbo);
    // const rfnboPropResult = hasAnyRfnbo
    //   ? computeFuelEuPenalty(blendedForYear.fuels, selectedScenarioYear)
    //   : null;
    // const rfnboOptResult = hasAnyRfnbo
    //   ? computeFuelEuPenaltyOptimal(blendedForYear.fuels, selectedScenarioYear)
    //   : null;

    const blendedTotalFuelTons = blendedFuelEntries.reduce((s, [, v]) => s + v.total.fuelTons, 0);
    const blendedTotalCO2      = blendedFuelEntries.reduce((s, [, v]) => s + v.total.co2, 0);
    const blendedTotalCost     = blendedFuelEntries.reduce((s, [, v]) => s + v.total.cost, 0);
    const blendedTotalCostLow  = blendedFuelEntries.reduce((s, [, v]) => s + (v.total.costLow ?? v.total.cost), 0);
    const blendedTotalCostHigh = blendedFuelEntries.reduce((s, [, v]) => s + (v.total.costHigh ?? v.total.cost), 0);
    const blendedTotalHFOeq    = blendedFuelEntries.reduce((s, [, v]) => s + v.total.hfoEquivalentTons, 0);
    const blendedTotalAllIn    = blendedTotalCost + etsPenalty;

    const fmtT   = v => v.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const fmtUSD = v => v !== null ? "$" + v.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "—";

    // ── PER-VOYAGE SUMMARY (per single trip, baseline vessel fuel & speed) ─
    const voyageSummaries = savedRoutes.map(route => {
      const speed       = Number(route.vessel?.speed?.value || 0);
      const consumption = Number(route.vessel?.fuel?.consumption || 0);
      const trips       = Number(route.operations?.tripsPerYear || 1);
      const vesselFuel  = route.vessel?.fuel?.type || "HFO";

      let totalMiles    = 0;
      let totalDays     = 0;
      let totalFuelTons = 0;
      let totalHFOeq    = 0;

      if (speed > 0 && consumption > 0 && route.segments) {
        route.segments.forEach(seg => {
          const dist  = Number(seg.distance || 0);
          const miles = dist;
          const days  = dist / (speed * 24);
          const fuel  = days * consumption;
          const hfoF  = fuelParams[vesselFuel]?.hfoEquivalentFactor
            ?? fuelConfig[vesselFuel]?.hfoEquivalentFactor?.value
            ?? 1;
          totalMiles    += miles;
          totalDays     += days;
          totalFuelTons += fuel;
          totalHFOeq    += fuel * hfoF;
        });
      }

      const euCat = getRouteEuCategory(route.segments);

      return {
        id:       route.id,
        name:     route.name,
        fuel:     vesselFuel,
        trips,
        speed,
        miles:    totalMiles,
        days:     totalDays,
        fuelTons: totalFuelTons,
        hfoEq:    totalHFOeq,
        euCat,
      };
    });

    // Abatement cost helpers — null when no active routes for this year
    const deltaCO2 = baselineCO2 !== null ? (baselineCO2 - blendedTotalCO2) : 0;
    const abatBase = (baselineCO2 !== null && deltaCO2 > 0.01) ? Math.round((blendedTotalCost     - baselineCost) / deltaCO2) : null;
    const abatLow  = (baselineCO2 !== null && deltaCO2 > 0.01) ? Math.round((blendedTotalCostLow  - baselineCost) / deltaCO2) : null;
    const abatHigh = (baselineCO2 !== null && deltaCO2 > 0.01) ? Math.round((blendedTotalCostHigh - baselineCost) / deltaCO2) : null;

    function fmtAbat(v) {
      if (v === null) return "—";
      return (v >= 0 ? "+" : "") + "$" + Math.abs(v).toLocaleString() + "/tCO₂";
    }
    function abatColor(v) {
      if (v === null) return "var(--slate-400)";
      return v <= 0 ? "#22c55e" : "#f87171";
    }

    function TableSection({ label, children }) {
      return (
        <div style={{ marginBottom: 32 }}>
          <div className="section-header" style={{ marginBottom: 12 }}>
            <span className="section-label">{label}</span>
            <div className="section-line" />
          </div>
          {children}
        </div>
      );
    }

    function DataTable({ headers, rows, totalsRow }) {
      return (
        <table style={{
          width: "100%", borderCollapse: "collapse",
          fontFamily: "var(--font-mono)", fontSize: 11,
        }}>
          <thead>
            <tr style={{ color: "var(--teal-300)", borderBottom: "1px solid rgba(45,212,191,0.2)" }}>
              {headers.map((h, i) => (
                <th key={i} align={i === 0 ? "left" : "right"} style={{ padding: "0 0 8px", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ borderBottom: "1px solid rgba(148,163,184,0.07)" }}>
                {row.map((cell, ci) => (
                  <td key={ci} align={ci === 0 ? "left" : "right"} style={{ padding: "7px 0", color: ci === 0 ? "var(--slate-200)" : "var(--slate-300)" }}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
            {totalsRow && (
              <tr style={{ borderTop: "1px solid rgba(45,212,191,0.25)", fontWeight: 600 }}>
                {totalsRow.map((cell, ci) => (
                  <td key={ci} align={ci === 0 ? "left" : "right"} style={{ padding: "8px 0", color: "var(--teal-300)" }}>
                    {cell}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      );
    }

    // Pre-compute compliance and escalated penalty for all years so tabs can show indicators
    const yearCompliance = {};
    const allPenaltiesForTabs = computeAllYearPenalties();
    SCENARIO_YEARS.forEach(year => {
      const p = allPenaltiesForTabs.get(year);
      yearCompliance[year] = { isCompliant: p.isCompliant, hasExposure: p.actualIntensity !== null, multiplier: p.avgMultiplier ?? 1 };
    });

    return (
      <div>

        {/* ── VOYAGE SUMMARY TABLE ── */}
        <TableSection label="Voyage Summary — per trip (baseline)">
          <table style={{
            width: "100%", borderCollapse: "collapse",
            fontFamily: "var(--font-mono)", fontSize: 11,
          }}>
            <thead>
              <tr style={{ color: "var(--teal-300)", borderBottom: "1px solid rgba(45,212,191,0.2)" }}>
                {[
                  { label: "Voyage",                   align: "left"   },
                  { label: "EU Scope",                  align: "center" },
                  { label: "Fuel",                     align: "center" },
                  { label: "Trips / yr",               align: "center" },
                  { label: "Distance (nm)",            align: "center" },
                  { label: "Travel Time (days)",       align: "center" },
                  { label: "Fuel Consumed (t)",        align: "center" },
                  { label: "HFO Equivalent (tHFOeq)",  align: "center" },
                ].map(({ label, align }) => (
                  <th key={label} style={{ padding: "0 8px 8px", fontWeight: 600, textAlign: align }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {voyageSummaries.map(vs => {
                const euScopeConfig = {
                  inter: { label: "Intra EU",   bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.4)",  text: "#67e8f9" },
                  mixed: { label: "Extra EU",    bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", text: "#fcd34d" },
                  out:   { label: "Out of Scope", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.25)", text: "#94a3b8" },
                }[vs.euCat] || { label: "—", bg: "transparent", border: "transparent", text: "#94a3b8" };
                return (
                <tr key={vs.id} style={{ borderBottom: "1px solid rgba(148,163,184,0.07)" }}>
                  <td style={{ padding: "7px 8px", color: "var(--slate-200)", textAlign: "left" }}>{vs.name}</td>
                  <td style={{ padding: "7px 8px", textAlign: "center" }}>
                    <span title={vs.euCat === "inter" ? "Both ports in EU — Intra EU (100% coverage)" : vs.euCat === "mixed" ? "One port in EU — Extra EU (50% coverage)" : "No EU ports — out of scope"} style={{
                      display: "inline-block",
                      fontFamily: "var(--font-mono)",
                      fontSize: 9,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      padding: "3px 7px",
                      borderRadius: 4,
                      background: euScopeConfig.bg,
                      border: `1px solid ${euScopeConfig.border}`,
                      color: euScopeConfig.text,
                      whiteSpace: "nowrap",
                    }}>
                      {euScopeConfig.label}
                    </span>
                  </td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>{fuelDisplayName(vs.fuel)}</td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>{vs.trips}</td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>
                    {vs.miles > 0 ? fmtT(vs.miles) : "—"}
                  </td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>
                    {vs.days > 0 ? vs.days.toFixed(1) : "—"}
                  </td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>
                    {vs.fuelTons > 0 ? fmtT(vs.fuelTons) + " t" : "—"}
                  </td>
                  <td style={{ padding: "7px 8px", color: "var(--slate-300)", textAlign: "center" }}>
                    {vs.hfoEq > 0 ? fmtT(vs.hfoEq) + " tHFOeq" : "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)" }}>
            Values are per single trip · multiply by Trips/yr for annual totals · uses baseline vessel speed &amp; consumption
          </div>
        </TableSection>

        {/* Year tabs — sticky below the tab title header */}
        <div ref={el => tourRefs.current["tour-year-selector"] = el} style={{
          position: "sticky",
          top: 72,
          zIndex: 9,
          background: "#ffffff",
          paddingTop: 10,
          paddingBottom: 0,
          marginBottom: 24,
          borderBottom: "2px solid #e2e8f0",
        }}>
          <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
            {SCENARIO_YEARS.map(year => {
              const isSelected = year === selectedScenarioYear;
              const { isCompliant, hasExposure, multiplier } = yearCompliance[year];
              const dotColor = !hasExposure ? "#cbd5e1" : isCompliant ? "#22c55e" : "#ef4444";
              const dotTitle = !hasExposure ? "No EU coverage"
                : isCompliant ? "Compliant"
                : multiplier > 1 ? `Non-compliant · ×${multiplier.toFixed(1)} escalated penalty`
                : "Non-compliant";

              return (
                <button
                  key={year}
                  onClick={() => setSelectedScenarioYear(year)}
                  style={{
                    position: "relative",
                    padding: "8px 14px 10px",
                    border: "none",
                    borderBottom: isSelected ? "2px solid #0891b2" : "2px solid transparent",
                    marginBottom: -2,
                    background: "transparent",
                    cursor: "pointer",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 400,
                    color: isSelected ? "#0891b2" : "#64748b",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    transition: "color 0.15s",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.color = "#0891b2"; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.color = "#64748b"; }}
                >
                  {year}
                  {/* compliance dot with optional escalation badge */}
                  <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <span
                      title={dotTitle}
                      style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: dotColor,
                        display: "block",
                        boxShadow: hasExposure ? `0 0 0 2px ${dotColor}33` : "none",
                      }}
                    />
                    {!isCompliant && multiplier > 1 && (
                      <span style={{ fontSize: 8, color: "#ef4444", fontWeight: 700, lineHeight: 1 }}>
                        ×{multiplier.toFixed(1)}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── FLEET SUMMARY FOR SELECTED YEAR ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 32,
        }}>
          {[
            {
              label: "Total Fuel Cost (per year)",
              value: fmtUSD(blendedTotalCost),
              sub: baselineCost !== null
                ? (() => { const d = blendedTotalCost - baselineCost; return (d >= 0 ? "+" : "−") + fmtUSD(Math.abs(d)) + " vs baseline"; })()
                : "baseline n/a",
              color: "var(--teal-300)",
            },
            {
              label: "Total CO₂",
              value: fmtT(blendedTotalCO2) + " t",
              sub: baselineCO2 !== null
                ? (() => { const d = blendedTotalCO2 - baselineCO2; return (d >= 0 ? "+" : "−") + fmtT(Math.abs(d)) + " t vs baseline"; })()
                : "baseline n/a",
              color: blendedTotalCO2 <= (baselineCO2 ?? Infinity) ? "#22c55e" : "#f87171",
            },
            {
              label: "FuelEU Status",
              value: isCompliant ? "✓ Compliant" : "✗ Non-compliant",
              sub: isCompliant
                ? (actualIntensity !== null ? actualIntensity.toFixed(2) + " gCO₂/MJ" : "")
                : (actualIntensity !== null ? actualIntensity.toFixed(2) + " vs " + (targetIntensity?.toFixed(2) ?? "—") + " gCO₂/MJ target" : ""),
              color: isCompliant ? "#22c55e" : "#ef4444",
            },
            {
              label: "All-in Cost (incl. penalty, per year)",
              value: fmtUSD(blendedTotalAllIn),
              sub: etsPenalty > 0
                ? `€${Math.round(etsPenaltyEur).toLocaleString()} (${fmtUSD(etsPenalty)}) FuelEU penalty`
                : "no FuelEU penalty",
              color: etsPenalty > 0 ? "#f87171" : "var(--teal-300)",
            },
          ].map(({ label, value, sub, color }) => (
            <div key={label} style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(45,212,191,0.12)",
              borderRadius: 6,
              padding: "14px 16px",
            }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--slate-400)", marginBottom: 6 }}>
                {label}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 600, color, marginBottom: 4 }}>
                {value}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)" }}>
                {sub}
              </div>
            </div>
          ))}
        </div>

        {/* ── FUEL CONSUMPTION ── */}
        <TableSection label="Fuel Consumption — Baseline (t)">
          <DataTable
            headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
            rows={Object.entries(fleetSummary.fuels).map(([fuel, v]) => [
              fuelDisplayName(fuel),
              fmtT(v.inter) + " t",
              fmtT(v.mixed) + " t",
              fmtT(v.out)   + " t",
              fmtT(v.total) + " t",
            ])}
          />
        </TableSection>

        <TableSection label={`Fuel Consumption — Scenario ${selectedScenarioYear} (t/year)`}>
          <DataTable
            headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
            rows={blendedFuelEntries.map(([fuel, v]) => [
              fuelDisplayName(fuel),
              fmtT(v.inter.fuelTons) + " t",
              fmtT(v.mixed.fuelTons) + " t",
              fmtT(v.out.fuelTons)   + " t",
              fmtT(v.total.fuelTons) + " t",
            ])}
            totalsRow={["Total", "", "", "", fmtT(blendedTotalFuelTons) + " t"]}
          />
          {hasAnyRfnbo && selectedScenarioYear <= 2030 && <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 9, color: "#2dd4bf" }}>⚡ Physical & allocated quantities only — RFNBO 2× energy credit applied separately in GHG intensity calculation</div>}
        </TableSection>

        <TableSection label={`HFO Equivalent — Scenario ${selectedScenarioYear} (tHFOeq/year)`}>
          <DataTable
            headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
            rows={blendedFuelEntries.map(([fuel, v]) => [
              fuelDisplayName(fuel),
              fmtT(v.inter.hfoEquivalentTons) + " tHFOeq",
              fmtT(v.mixed.hfoEquivalentTons) + " tHFOeq",
              fmtT(v.out.hfoEquivalentTons)   + " tHFOeq",
              fmtT(v.total.hfoEquivalentTons) + " tHFOeq",
            ])}
            totalsRow={["Total", "", "", "", fmtT(blendedTotalHFOeq) + " tHFOeq"]}
          />
          {hasAnyRfnbo && selectedScenarioYear <= 2030 && <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 9, color: "#2dd4bf" }}>⚡ Physical & allocated quantities only — RFNBO 2× energy credit applied separately in GHG intensity calculation</div>}
        </TableSection>

        {/* ── CO2 EMISSIONS ── */}
        <TableSection label="CO₂ Emissions — Baseline vs Scenario (tCO₂)">
          <DataTable
            headers={[
              "",
              <span title="CO₂ emissions if the fleet kept running on its baseline fuel only — no scenario blend applied.">No-Blend Reference</span>,
              `Scenario ${selectedScenarioYear}`,
              "Δ Avoided",
            ]}
            rows={[
              [
                "Total CO₂",
                baselineCO2 !== null ? fmtT(baselineCO2) + " t" : "—",
                fmtT(blendedTotalCO2) + " t",
                (() => {
                  if (baselineCO2 === null) return "—";
                  const avoided = baselineCO2 - blendedTotalCO2;
                  const sign = avoided >= 0 ? "−" : "+";
                  const color = avoided >= 0 ? "#22c55e" : "#ef4444";
                  return <span style={{ color }}>{sign} {fmtT(Math.abs(avoided))} t</span>;
                })(),
              ],
              [
                "Reduction %",
                "—",
                "—",
                (() => {
                  if (baselineCO2 <= 0) return "—";
                  const pct = ((baselineCO2 - blendedTotalCO2) / baselineCO2) * 100;
                  const color = pct >= 0 ? "#22c55e" : "#ef4444";
                  return <span style={{ color }}>{pct >= 0 ? "−" : "+"}{Math.abs(pct).toFixed(1)}%</span>;
                })(),
              ],
            ]}
          />
          <div style={{ marginTop: 16 }}>
            <DataTable
              headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
              rows={blendedFuelEntries.map(([fuel, v]) => [
                fuelDisplayName(fuel),
                fmtT(v.inter.co2) + " t",
                fmtT(v.mixed.co2) + " t",
                fmtT(v.out.co2)   + " t",
                fmtT(v.total.co2) + " t",
              ])}
              totalsRow={["Total", "", "", "", fmtT(blendedTotalCO2) + " t"]}
            />
          </div>
          {hasAnyRfnbo && selectedScenarioYear <= 2030 && <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 9, color: "#2dd4bf" }}>⚡ Physical CO₂ only — RFNBO 2× energy credit reduces GHG intensity by inflating the MJ denominator, not by reducing CO₂ mass</div>}
        </TableSection>

        {/* ── ENERGY (MJ) ── */}
        {(() => {
          const vlsfoLhv = 1000. * fuelConfig.VLSFO.lhv.value; // MJ/t — same reference as penalty calculation
          // Raw MJ by region (hfoEqTons × vlsfoLhv = true fuel MJ)
          const toMJ = (hfoEqTons) => hfoEqTons * vlsfoLhv;
          const fmtMJ = v => {
            const abs = Math.abs(v);
            const sign = v < 0 ? "−" : "";
            if (abs >= 1e12) return sign + (abs / 1e12).toFixed(2) + " EJ";
            if (abs >= 1e9)  return sign + (abs / 1e9).toFixed(2)  + " TJ";
            if (abs >= 1e6)  return sign + (abs / 1e6).toFixed(2)  + " GJ";
            if (abs >= 1e3)  return sign + (abs / 1e3).toFixed(1)  + " MJ (×10³)";
            return sign + abs.toFixed(0) + " MJ";
          };

          const rows = blendedFuelEntries.flatMap(([fuel, v]) => {
            const interMJ = toMJ(v.inter.hfoEquivalentTons);
            const mixedMJ = toMJ(v.mixed.hfoEquivalentTons);
            const outMJ   = toMJ(v.out.hfoEquivalentTons);
            const totalMJ = toMJ(v.total.hfoEquivalentTons);
            const fuelRow = [fuelDisplayName(fuel), fmtMJ(interMJ), fmtMJ(mixedMJ), fmtMJ(outMJ), fmtMJ(totalMJ)];
            if (!v.isRfnbo || selectedScenarioYear > 2030) return [fuelRow];
            // RFNBO fuel: bonus = same as physical HFO-equivalent energy (2× counting)
            const bonusInter = interMJ;
            const bonusMixed = mixedMJ;
            const bonusOut   = outMJ;
            const bonusTotal = totalMJ;
            const bonusRow = [
              <span style={{ color: "#2dd4bf", paddingLeft: 10 }}>⚡ {fuelDisplayName(fuel)} RFNBO credit (physical)</span>,
              fmtMJ(bonusInter), fmtMJ(bonusMixed), fmtMJ(bonusOut),
              <span style={{ color: "#2dd4bf" }}>+{fmtMJ(bonusTotal)}</span>,
            ];
            return [fuelRow, bonusRow];
          });

          const totalInterMJ = blendedFuelEntries.reduce((s, [, v]) => s + toMJ(v.inter.hfoEquivalentTons), 0);
          const totalMixedMJ = blendedFuelEntries.reduce((s, [, v]) => s + toMJ(v.mixed.hfoEquivalentTons), 0);
          const totalOutMJ   = blendedFuelEntries.reduce((s, [, v]) => s + toMJ(v.out.hfoEquivalentTons), 0);
          const totalAllMJ   = totalInterMJ + totalMixedMJ + totalOutMJ;
          const rfnboApplies = selectedScenarioYear <= 2030;
          const totalRfnboBonus = rfnboApplies ? blendedFuelEntries.reduce((s, [, v]) => s + (v.isRfnbo ? toMJ(v.total.hfoEquivalentTons) : 0), 0) : 0;

          // Coverage-weighted eligible MJ (what the penalty calculation uses)
          const cf = { inter: euFactors.inter ?? 1, mixed: euFactors.mixed ?? 0.5, out: euFactors.out ?? 0 };
          const eligInterMJ = totalInterMJ * cf.inter;
          const eligMixedMJ = totalMixedMJ * cf.mixed;
          const eligOutMJ   = totalOutMJ   * cf.out;

          // RFNBO bonus in eligible section — only applicable for 2025 and 2030 blocks
          const rfnboInterBonus = rfnboApplies ? blendedFuelEntries.reduce((s, [, v]) => s + (v.isRfnbo ? toMJ(v.inter.hfoEquivalentTons) * cf.inter : 0), 0) : 0;
          const rfnboMixedBonus = rfnboApplies ? blendedFuelEntries.reduce((s, [, v]) => s + (v.isRfnbo ? toMJ(v.mixed.hfoEquivalentTons) * cf.mixed : 0), 0) : 0;
          const rfnboOutBonus   = rfnboApplies ? blendedFuelEntries.reduce((s, [, v]) => s + (v.isRfnbo ? toMJ(v.out.hfoEquivalentTons)   * cf.out   : 0), 0) : 0;
          const rfnboTotalBonus = rfnboInterBonus + rfnboMixedBonus + rfnboOutBonus;
          const hasRfnbo = rfnboTotalBonus > 0;

          const eligTotalMJ = eligInterMJ + eligMixedMJ + eligOutMJ + rfnboTotalBonus;

          // Eligible CO₂ per coverage zone
          const eligInterCO2 = blendedFuelEntries.reduce((s, [, v]) => s + v.inter.co2 * cf.inter, 0);
          const eligMixedCO2 = blendedFuelEntries.reduce((s, [, v]) => s + v.mixed.co2 * cf.mixed, 0);
          const eligOutCO2   = blendedFuelEntries.reduce((s, [, v]) => s + v.out.co2   * cf.out,   0);
          const eligCO2      = eligInterCO2 + eligMixedCO2 + eligOutCO2;
          const intensityCheck = eligTotalMJ > 0 ? (eligCO2 / eligTotalMJ) * 1_000_000 : null;

          return (
            <TableSection label={`Energy — Scenario ${selectedScenarioYear} (MJ/year, hfoEq × 40,200 MJ/t)`}>
              <DataTable
                headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
                rows={rows}
                totalsRow={[
                  "Total",
                  fmtMJ(totalInterMJ),
                  fmtMJ(totalMixedMJ),
                  fmtMJ(totalOutMJ),
                  hasRfnbo
                    ? <span>{fmtMJ(totalAllMJ)} <span style={{ color: "#2dd4bf" }}>(+{fmtMJ(totalRfnboBonus)} RFNBO)</span></span>
                    : fmtMJ(totalAllMJ),
                ]}
              />
              <div style={{ marginTop: 14 }}>
                <DataTable
                  headers={["", "Intra EU", "Extra EU", "Out of Scope", "Eligible Total"]}
                  rows={[
                    [
                      <span style={{ color: "var(--slate-400)", fontSize: 10 }}>
                        Coverage factor
                      </span>,
                      `× ${cf.inter.toFixed(1)}`,
                      `× ${cf.mixed.toFixed(1)}`,
                      `× ${cf.out.toFixed(1)}`,
                      "—",
                    ],
                    [
                      "Eligible MJ",
                      fmtMJ(eligInterMJ),
                      fmtMJ(eligMixedMJ),
                      fmtMJ(eligOutMJ),
                      fmtMJ(eligTotalMJ - rfnboTotalBonus),
                    ],
                    ...(hasRfnbo ? [[
                      <span style={{ color: "#2dd4bf" }}>
                        ⚡ RFNBO 2× credit (eligible, coverage-weighted)
                      </span>,
                      fmtMJ(rfnboInterBonus),
                      fmtMJ(rfnboMixedBonus),
                      fmtMJ(rfnboOutBonus),
                      <span style={{ color: "#2dd4bf" }}>+{fmtMJ(rfnboTotalBonus)}</span>,
                    ]] : []),
                    ...(hasRfnbo ? [[
                      <strong>Eligible MJ (incl. RFNBO)</strong>,
                      "",
                      "",
                      "",
                      <strong>{fmtMJ(eligTotalMJ)}</strong>,
                    ]] : []),
                    [
                      "Eligible CO₂",
                      fmtT(eligInterCO2) + " t",
                      fmtT(eligMixedCO2) + " t",
                      fmtT(eligOutCO2)   + " t",
                      fmtT(eligCO2) + " t",
                    ],
                    [
                      <strong>GHG Intensity</strong>,
                      "",
                      "",
                      "",
                      intensityCheck !== null
                        ? <span style={{ color: intensityCheck <= (euFactors.yearlyTargets[selectedScenarioYear] ?? Infinity) ? "#22c55e" : "#ef4444" }}>
                            {intensityCheck.toFixed(2)} gCO₂/MJ
                          </span>
                        : "—",
                    ],
                  ]}
                />
              </div>
              <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)" }}>
                MJ = hfoEquivalentTons × 40,200 (VLSFO LHV) · Eligible = coverage-weighted · same basis as FuelEU penalty
                {hasRfnbo && selectedScenarioYear <= 2030 && <span style={{ color: "#2dd4bf" }}> · ⚡ RFNBO fuels counted as 2× physical energy in denominator</span>}
                {" · "}
                <span style={{ color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8" }}>
                  {useOptimalAllocation ? "⚡ optimal allocation" : "⇌ proportional allocation"}
                </span>
              </div>
            </TableSection>
          );
        })()}
        <TableSection label={`Fuel Cost — Baseline vs Scenario (USD/year, for ${selectedScenarioYear})`}>
          {/* Summary with Low / Expected / High scenario price range */}
          <DataTable
            headers={[
              "",
              <span title="Cost if the fleet kept running on its baseline fuel only — no scenario blend applied.">No-Blend Reference</span>,
              <span title="Scenario cost using each fuel's low price assumption (from the fuel parameters' Low / Expected / High range).">Low cost</span>,
              <span title="Scenario cost using each fuel's expected (central) price assumption — the value used everywhere else in the tool.">Expected cost</span>,
              <span title="Scenario cost using each fuel's high price assumption (from the fuel parameters' Low / Expected / High range).">High cost</span>,
            ]}
            rows={[
              [
                "Fuel Cost",
                fmtUSD(baselineCost),
                fmtUSD(blendedTotalCostLow),
                fmtUSD(blendedTotalCost),
                fmtUSD(blendedTotalCostHigh),
              ],
              [
                <span title="FuelEU Maritime penalty (Art. 23, Reg. 2023/1805): €2,400/tVLSFOeq of intensity shortfall, converted to USD. Escalates +10% per consecutive non-compliant year.">
                  FuelEU Penalty
                  {!isCompliant && penaltyMultiplier > 1 && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: "#f97316", fontWeight: 600 }}>×{penaltyMultiplier.toFixed(1)}</span>
                  )}
                  {isCompliant
                    ? <span style={{ marginLeft: 6, color: "#22c55e", fontSize: 10 }}>✓ compliant</span>
                    : <span style={{ marginLeft: 6, color: "#ef4444", fontSize: 10 }}>✗ {excessVLSFO.toFixed(1)} tVLSFO excess · €{Math.round(etsPenaltyEur).toLocaleString()}</span>
                  }
                </span>,
                <span style={{ color: "var(--slate-400)", fontSize: 10 }}>not modelled</span>,
                fmtUSD(etsPenalty),
                fmtUSD(etsPenalty),
                fmtUSD(etsPenalty),
              ],
              [
                <strong>Total (incl. penalty)</strong>,
                fmtUSD(baselineCost),
                fmtUSD(blendedTotalCostLow + etsPenalty),
                fmtUSD(blendedTotalAllIn),
                fmtUSD(blendedTotalCostHigh + etsPenalty),
              ],
              [
                "Δ vs No-Blend Reference",
                "—",
                baselineCost !== null ? (() => { const d = blendedTotalCostLow  + etsPenalty - baselineCost; return <span style={{ color: d <= 0 ? "#22c55e" : "#f87171" }}>{d >= 0 ? "+" : "−"}{fmtUSD(Math.abs(d))}</span>; })() : "—",
                baselineCost !== null ? (() => { const d = blendedTotalAllIn - baselineCost; return <span style={{ color: d <= 0 ? "#22c55e" : "#f87171" }}>{d >= 0 ? "+" : "−"}{fmtUSD(Math.abs(d))}</span>; })() : "—",
                baselineCost !== null ? (() => { const d = blendedTotalCostHigh + etsPenalty - baselineCost; return <span style={{ color: d <= 0 ? "#22c55e" : "#f87171" }}>{d >= 0 ? "+" : "−"}{fmtUSD(Math.abs(d))}</span>; })() : "—",
              ],
              [
                "Abatement Cost",
                "—",
                <span style={{ color: abatColor(abatLow)  }}>{fmtAbat(abatLow)}</span>,
                <span style={{ color: abatColor(abatBase) }}>{fmtAbat(abatBase)}</span>,
                <span style={{ color: abatColor(abatHigh) }}>{fmtAbat(abatHigh)}</span>,
              ],
              ...(deltaCO2 <= 0.01 && baselineCO2 !== null ? [[
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "#f87171", opacity: 0.8, fontStyle: "italic" }}>
                  ⚠ Scenario emits more CO₂ than no-blend reference — abatement cost not applicable
                </span>,
                "", "", "", "",
              ]] : []),
            ]}
          />
          {/* Per-fuel breakdown */}
          <div style={{ marginTop: 16 }}>
            <DataTable
              headers={["Fuel", "Intra EU", "Extra EU", "Out of Scope", "Total"]}
              rows={blendedFuelEntries.map(([fuel, v]) => [
                fuelDisplayName(fuel),
                fmtUSD(v.inter.cost),
                fmtUSD(v.mixed.cost),
                fmtUSD(v.out.cost),
                fmtUSD(v.total.cost),
              ])}
              totalsRow={["Fuel Cost", "", "", "", fmtUSD(blendedTotalCost)]}
            />
          </div>
          
        </TableSection>

        {/* ── FUELEU PENALTY ESCALATION ── */}
        <TableSection label="FuelEU Penalty — Escalation Schedule (Art. 23 Reg. 2023/1805)">
          {(() => {
            const fmtEUR = v => v > 0 ? "€" + Math.round(v).toLocaleString() : "—";
            const penalties = computeAllYearPenalties();

            const totalEscalatedEur = SCENARIO_YEARS.reduce((s, y) => s + (penalties.get(y).penaltyEur ?? 0), 0);

            return (
              <>
                {/* Allocation mode indicator */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px",
                    textTransform: "uppercase", padding: "3px 8px", borderRadius: 4,
                    background: useOptimalAllocation ? "rgba(29,158,117,0.12)" : "rgba(148,163,184,0.1)",
                    border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.4)" : "rgba(148,163,184,0.25)"}`,
                    color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
                  }}>
                    {useOptimalAllocation ? "⚡ optimal allocation" : "⇌ proportional allocation"}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)" }}>
                    {useOptimalAllocation
                      ? "For mixed voyages (50% scope), cleanest fuels fill the in-scope window first — change in Allocation tab"
                      : "For mixed voyages (50% scope), each fuel enters scope pro-rata — change in Allocation tab"}
                  </span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: "var(--teal-300)", borderBottom: "1px solid rgba(45,212,191,0.2)" }}>
                      <th align="left"  style={{ padding: "0 0 8px", fontWeight: 600 }}>Year</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Actual (gCO₂/MJ)</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Target (gCO₂/MJ)</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Excess (tVLSFO)</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Annual Base Penalty</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Avg Multiplier</th>
                      <th align="right" style={{ padding: "0 0 8px", fontWeight: 600 }}>Annual Penalty (escalated)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SCENARIO_YEARS.map(year => {
                      const p = penalties.get(year);
                      const isSelected = year === selectedScenarioYear;
                      return (
                        <tr
                          key={year}
                          onClick={() => setSelectedScenarioYear(year)}
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "rgba(8,145,178,0.06)" : "transparent",
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          <td style={{ padding: "7px 0", color: isSelected ? "#0891b2" : "var(--slate-200)", fontFamily: "var(--font-mono)", fontSize: 11 }}>{year}</td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: !p.actualIntensity ? "var(--slate-400)" : p.isCompliant ? "#22c55e" : "#ef4444" }}>
                            {p.actualIntensity ? p.actualIntensity.toFixed(2) : "—"}
                          </td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-300)" }}>
                            {p.targetIntensity?.toFixed(2) ?? "—"}
                          </td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: p.isCompliant ? "#22c55e" : "#ef4444" }}>
                            {p.isCompliant ? "✓" : `${p.excessVLSFO.toFixed(1)} t`}
                          </td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-300)" }}>
                            {p.isCompliant ? "—" : fmtEUR(p.basePenaltyEur)}
                          </td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: p.consecutiveYears > 1 ? "#f97316" : "var(--slate-400)" }}>
                            {p.isCompliant
                              ? "—"
                              : <span>
                                  <span style={{ fontWeight: 600 }}>×{(p.avgMultiplier ?? p.firstMultiplier ?? 1).toFixed(2)}</span>
                                  {p.blockYears > 1 && <span style={{ opacity: 0.6, fontSize: 10 }}> (×{p.firstMultiplier.toFixed(1)}→×{p.lastMultiplier.toFixed(1)})</span>}
                                </span>
                            }
                          </td>
                          <td align="right" style={{ padding: "7px 0", fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, color: p.penaltyEur > 0 ? "#ef4444" : "#22c55e" }}>
                            {p.isCompliant ? "✓ compliant" : fmtEUR(p.penaltyEur)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "1px solid rgba(45,212,191,0.25)" }}>
                      <td colSpan={5} style={{ padding: "8px 0", color: "var(--slate-400)", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        All penalties are annual (per year). Multiplier = avg over 5-cal-year block (×1.0 yr1, +0.1/yr). Resets on compliant year.
                      </td>
                      <td align="right" style={{ padding: "8px 0", color: "var(--teal-300)", fontWeight: 600, fontFamily: "var(--font-mono)", fontSize: 11 }}>Sum (annual)</td>
                      <td align="right" style={{ padding: "8px 0", color: totalEscalatedEur > 0 ? "#ef4444" : "#22c55e", fontWeight: 600, fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        €{Math.round(totalEscalatedEur).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </>
            );
          })()}
        </TableSection>

      </div>
    );
  }



// ── BASELINE helper — module-level so both renderScenarioTables and
//    renderScenarioGraphsCO2 can call it during App's render cycle ──────────
// targetYear: optional. When provided, routes with active===false for that year
// are excluded so that the baseline reflects only routes actually operating.
// When omitted (undefined), all routes are included (used for the static
// baseline reference line on charts — "what if nothing changed").
function buildFleetBaseline(targetYear) {
  let baselineCO2  = 0;
  let baselineCost = 0;
  let totalEligibleMJ  = 0;
  let totalEligibleCO2 = 0;
  let activeRouteCount = 0;

  savedRoutes.forEach(route => {
    if (!route.segments || !route.vessel) return;

    if (targetYear !== undefined) {
      const yearActive = route.scenarioTimeline?.[targetYear]?.active;
      if (yearActive === false) return;
    }

    const speed       = Number(route.vessel?.speed?.value || 0);
    const consumption = Number(route.vessel?.fuel?.consumption || 0);
    const trips       = Number(route.operations?.tripsPerYear || 1);
    const vesselFuel  = route.vessel?.fuel?.type || "HFO";
    if (speed <= 0 || consumption <= 0) return;

    activeRouteCount++;

    // Classify the whole route once
    const routeEuCatBaseline = getRouteEuCategory(route.segments);

    route.segments.forEach(segment => {
      const yearlyDistance = Number(segment.distance || 0) * trips;
      const fuelTons       = (yearlyDistance / speed / 24) * consumption;

      const region = routeEuCatBaseline;
      const coverageFactor = euFactors[region];

      const hfoEq = convertToHFOEquivalent(vesselFuel, fuelTons);
      const co2   = computeCO2(vesselFuel, fuelTons);
      const cost  = computeFuelCost(vesselFuel, fuelTons);

      baselineCO2  += co2;
      baselineCost += cost;
      // Use vlsfoLhv (40,200) so hfoEqTons × vlsfoLhv = true fuel MJ — consistent with computeFuelEuPenalty
      totalEligibleMJ  += hfoEq * 1000. * fuelConfig.VLSFO.lhv.value * coverageFactor;
      totalEligibleCO2 += co2 * coverageFactor;
    });
  });

  // No active routes → return null so charts show a gap, not a misleading 0
  if (activeRouteCount === 0) {
    return { baselineCO2: null, baselineCost: null, baselineIntensity: null };
  }

  const baselineIntensity = totalEligibleMJ > 0
    ? (totalEligibleCO2 / totalEligibleMJ) * 1_000_000
    : null;

  return { baselineCO2, baselineCost, baselineIntensity };
}

// ── FuelEU Maritime penalty (Art. 23, Reg. EU 2023/1805) ──────────────────
// Charged only when the fleet's GHG intensity EXCEEDS the yearly target.
//
// Formula (step by step):
//   1. actualIntensity (gCO₂/MJ) = totalEligibleCO₂ (tCO₂) / totalEligibleMJ × 1,000,000
//   2. excessCO₂ (tCO₂)  = (actualIntensity − target) / 1,000,000 × totalEligibleMJ
//   3. excessVLSFO (tVLSFO) = excessCO₂ / vlsfoCO2Factor   (VLSFO emits 3.114 tCO₂/t)
//   4. penalty (EUR)        = excessVLSFO × fuelEuPenaltyPrice (€2,400/tVLSFO)
//   5. penalty (USD)        = penalty (EUR) × eurToUsd
//
// Statutory rate: €2,400/tVLSFOeq (Art. 23, Reg. 2023/1805).
//
// Returns { penaltyEur, penaltyUsd, actualIntensity, targetIntensity,
//           excessCO2, excessVLSFO, isCompliant }
function computeFuelEuPenalty(blendedFuels, year) {
  const target             = euFactors.yearlyTargets[year] ?? null;
  const fuelEuPenaltyPrice = euFactors.fuelEuPenaltyPrice ?? 2400; // EUR/tVLSFO
  const eurToUsd           = euFactors.eurToUsd ?? 1.08;
  const vlsfoLhv           = fuelParams["VLSFO"]?.lhvMJperT ?? (fuelConfig.VLSFO.lhv.value * 1000); // MJ/t — LHV of VLSFO from fuel params

  // totalEligibleMJ: actual voyage energy in MJ, coverage-weighted.
  // hfoEquivalentFactor = LHV_fuel / LHV_VLSFO (40,200), so:
  //   hfoEqTons × vlsfoLhv = fuelTons × (LHV_fuel/40,200) × 40,200 = fuelTons × LHV_fuel  (true MJ)
  let totalEligibleMJ  = 0;
  let totalEligibleCO2 = 0;
  Object.values(blendedFuels).forEach(values => {
    const eligHFOeq =
      (values.inter?.hfoEquivalentTons ?? 0) * (euFactors.inter ?? 1) +
      (values.mixed?.hfoEquivalentTons ?? 0) * (euFactors.mixed ?? 0.5) +
      (values.out?.hfoEquivalentTons   ?? 0) * (euFactors.out  ?? 0);
    const eligCO2 =
      (values.inter?.co2 ?? 0) * (euFactors.inter ?? 1) +
      (values.mixed?.co2 ?? 0) * (euFactors.mixed ?? 0.5) +
      (values.out?.co2   ?? 0) * (euFactors.out  ?? 0);
    // RFNBO: double-counting bonus added to MJ denominator (only for 2025 and 2030 blocks)
    const rfnboBonusMJ = (values.isRfnbo && year <= 2030)
      ? ((values.inter?.rfnboBonusMJ ?? 0) * (euFactors.inter ?? 1) +
         (values.mixed?.rfnboBonusMJ ?? 0) * (euFactors.mixed ?? 0.5) +
         (values.out?.rfnboBonusMJ   ?? 0) * (euFactors.out  ?? 0))
      : 0;

    totalEligibleMJ  += eligHFOeq * vlsfoLhv + rfnboBonusMJ;
    totalEligibleCO2 += eligCO2;
  });

  if (totalEligibleMJ <= 0 || target === null) {
    return { penaltyEur: 0, penaltyUsd: 0, actualIntensity: null, targetIntensity: target, excessCO2: 0, excessVLSFO: 0, isCompliant: true };
  }

  const actualIntensity = (totalEligibleCO2 / totalEligibleMJ) * 1_000_000; // gCO₂/MJ
  const isCompliant     = actualIntensity <= target;

  if (isCompliant) {
    return { penaltyEur: 0, penaltyUsd: 0, actualIntensity, targetIntensity: target, excessCO2: 0, excessVLSFO: 0, isCompliant: true };
  }

  // excessCO2: total CO₂ above the limit (tCO₂)
  const excessCO2   = ((actualIntensity - target) / 1_000_000) * totalEligibleMJ;
  // excessVLSFO: excess CO₂ ÷ (actual intensity × VLSFO LHV) — gives VLSFO-equivalent tonnes
  const excessVLSFO = excessCO2 / ((actualIntensity / 1_000_000) * vlsfoLhv);
  const penaltyEur  = excessVLSFO * fuelEuPenaltyPrice;
  const penaltyUsd  = penaltyEur * eurToUsd;


  return { penaltyEur, penaltyUsd, actualIntensity, targetIntensity: target, excessCO2, excessVLSFO, isCompliant };
}

// ── ESCALATING PENALTY ACROSS ALL YEARS ───────────────────────────────────
// Art. 23(2) Reg. 2023/1805: the penalty increases by 10% for each consecutive
// non-compliant calendar year. The 1st non-compliant year already gets ×1.1,
// the 2nd gets ×1.2, etc. Scenario years are 5 years apart, so each block covers
// multiple calendar years, each with its own multiplier.
//
// The effective penalty for a scenario block is the SUM of each calendar
// year's penalty within that block. If a block starts with the streak at N,
// the calendar years within it get multipliers:
//   ×(1.1 + N×0.1), ×(1.1 + (N+1)×0.1), ..., ×(1.1 + (N+gap-1)×0.1)
// and the block's total penalty = basePenaltyPerYear × Σ(those multipliers).
//
// Example: all years non-compliant, streak=0 entering 2025:
//   2025 block: cal year 2025 only  → ×1.0                              = ×1.0  (avg ×1.0)
//   2030 block: cal years 2030-2034 → ×1.1+×1.2+×1.3+×1.4+×1.5        = ×6.5  (avg ×1.3) wait — 2025 block=1yr so streak=1 entering 2030
//   In practice: 2025 block covers 1 cal year (streak 0→1), 2030 covers 5 (streak 1→6), etc.
//
// A compliant block resets the streak to 0.
//
// Returns a Map<year, { ...penaltyResult, basePenaltyEur, basePenaltyUsd,
//                        multiplierSum, firstMultiplier, lastMultiplier,
//                        blockYears, consecutiveYears }>
// ── OPTIMAL ALLOCATION VARIANT ────────────────────────────────────────────────
// Instead of applying coverage factors pro-rata per fuel per region,
// we sort all eligible (non-out) fuel segments by WtW GHG intensity
// (lowest first) and fill the in-scope energy budget from cleanest to dirtiest.
// The budget is: Σ (fuelMJ_intra × 1.0 + fuelMJ_mixed × 0.5).
// Returns the same shape as computeFuelEuPenalty.
//
// KEY REGULATION INSIGHT (Reg. EU 2023/1805 Art. 2 & 4, ESSF SAPS WS1):
//   • Intra-EU voyages (both ports EU, scopeFactor = 1.0):
//       100% of energy is ALWAYS in scope — no allocation choice.
//       Every tonne burned enters the GHG intensity denominator exactly as-is.
//   • Mixed voyages (one EU port, scopeFactor = 0.5):
//       Only 50% of total voyage energy is counted, BUT the company can choose
//       WHICH 50%. Optimal = fill that budget with the cleanest fuels first,
//       drawing from the full physical energy burned on those voyages.
//   • Out-of-scope (scopeFactor = 0): never counted, regardless of fuel.
//
// Algorithm:
//   1. Intra-EU contribution: fixed — all inter fuel enters scope at full MJ & CO2.
//   2. Mixed budget: total = Σ(mixed_voyage_totalMJ) × 0.5 (the 50% window).
//      Fill this budget greedily, sorted by co2PerMJ ascending (cleanest first),
//      drawing from the FULL physical MJ of each fuel on mixed voyages.
function computeFuelEuPenaltyOptimal(blendedFuels, year) {
  const target             = euFactors.yearlyTargets[year] ?? null;
  const fuelEuPenaltyPrice = euFactors.fuelEuPenaltyPrice ?? 2400; // EUR/tVLSFO
  const eurToUsd           = euFactors.eurToUsd ?? 1.08;
  const vlsfoLhv           = fuelParams["VLSFO"]?.lhvMJperT ?? (fuelConfig.VLSFO.lhv.value * 1000);

  // ── Step 1: intra-EU (fixed) ──────────────────────────────────────────────
  // These must enter scope 100% — no allocation freedom here.
  let fixedEligibleMJ  = 0;
  let fixedEligibleCO2 = 0;

  // ── Step 2: mixed (allocatable) ──────────────────────────────────────────
  // Budget = Σ mixedHfoEqTons × vlsfoLhv × 0.5 (the in-scope window for mixed voyages)
  // Available per fuel = its full physical MJ burned on mixed voyages (undiscounted)
  let mixedBudgetMJ = 0;
  const mixedSegments = []; // { fuelKey, mjAvailable, co2PerMJ }

  Object.entries(blendedFuels).forEach(([fuelKey, values]) => {
    // ── Intra-EU: fixed contribution ──────────────────────────────────────
    const interHfoEq = values.inter?.hfoEquivalentTons ?? 0;
    const interMJ    = interHfoEq * vlsfoLhv;
    const interCO2   = values.inter?.co2 ?? 0;
    // RFNBO: bonus MJ added to denominator for intra-EU (only 2025 and 2030 blocks)
    const interRfnboBonus = (values.isRfnbo && year <= 2030) ? (values.inter?.rfnboBonusMJ ?? 0) : 0;
    fixedEligibleMJ  += (interMJ + interRfnboBonus) * (euFactors.inter ?? 1);
    fixedEligibleCO2 += interCO2 * (euFactors.inter ?? 1);

    // ── Mixed: allocatable ────────────────────────────────────────────────
    const mixedHfoEq      = values.mixed?.hfoEquivalentTons ?? 0;
    const rfnboMixedBonus = (values.isRfnbo && year <= 2030) ? (values.mixed?.rfnboBonusMJ ?? 0) : 0;
    const physicalMixedMJ = mixedHfoEq * vlsfoLhv + rfnboMixedBonus;
    const mixedCO2        = values.mixed?.co2 ?? 0;
    if (physicalMixedMJ <= 0) return;

    const co2PerMJ = mixedCO2 / (physicalMixedMJ || 1);
    mixedSegments.push({ fuelKey, mjAvailable: physicalMixedMJ, co2PerMJ });

    mixedBudgetMJ += physicalMixedMJ * (euFactors.mixed ?? 0.5);
  });

  const totalInScopeBudgetMJ = fixedEligibleMJ + mixedBudgetMJ;

  if (totalInScopeBudgetMJ <= 0 || target === null) {
    return { penaltyEur: 0, penaltyUsd: 0, actualIntensity: null, targetIntensity: target, excessCO2: 0, excessVLSFO: 0, isCompliant: true };
  }

  // ── Step 3: fill mixed budget greedily, cleanest fuel first ──────────────
  mixedSegments.sort((a, b) => a.co2PerMJ - b.co2PerMJ);

  let remainingMixedBudget = mixedBudgetMJ;
  let allocatedMixedMJ  = 0;
  let allocatedMixedCO2 = 0;

  for (const seg of mixedSegments) {
    if (remainingMixedBudget <= 0) break;
    const take = Math.min(seg.mjAvailable, remainingMixedBudget);
    allocatedMixedMJ  += take;
    allocatedMixedCO2 += take * seg.co2PerMJ;
    remainingMixedBudget -= take;
  }

  // ── Step 4: combine fixed + allocated mixed ───────────────────────────────
  const totalEligibleMJ  = fixedEligibleMJ  + allocatedMixedMJ;
  const totalEligibleCO2 = fixedEligibleCO2 + allocatedMixedCO2;

  if (totalEligibleMJ <= 0) {
    return { penaltyEur: 0, penaltyUsd: 0, actualIntensity: null, targetIntensity: target, excessCO2: 0, excessVLSFO: 0, isCompliant: true };
  }

  const actualIntensity = (totalEligibleCO2 / totalEligibleMJ) * 1_000_000; // gCO2/MJ
  const isCompliant     = actualIntensity <= target;

  if (isCompliant) {
    return { penaltyEur: 0, penaltyUsd: 0, actualIntensity, targetIntensity: target, excessCO2: 0, excessVLSFO: 0, isCompliant: true };
  }

  const excessCO2   = ((actualIntensity - target) / 1_000_000) * totalEligibleMJ;
  const excessVLSFO = excessCO2 / ((actualIntensity / 1_000_000) * vlsfoLhv);
  const penaltyEur  = excessVLSFO * fuelEuPenaltyPrice;
  const penaltyUsd  = penaltyEur * eurToUsd;

  return { penaltyEur, penaltyUsd, actualIntensity, targetIntensity: target, excessCO2, excessVLSFO, isCompliant };
}

function computeAllYearPenalties() {
  const results = new Map();
  // streak = total consecutive non-compliant calendar years before current block.
  // Art. 23(2): 1st non-compliant year → ×1.0, 2nd → ×1.1, 3rd → ×1.2, etc.
  let streak = 0;

  SCENARIO_YEARS.forEach((year, idx) => {
    const blended = calculateBlendedFuels(year);
    // Use the selected allocation mode
    const base = useOptimalAllocation
      ? computeFuelEuPenaltyOptimal(blended.fuels, year)
      : computeFuelEuPenalty(blended.fuels, year);

    const prevYear = idx > 0 ? SCENARIO_YEARS[idx - 1] : year - 5;
    const gapYears = year - prevYear;
    const blockYears = gapYears;

    if (base.isCompliant) {
      streak = 0;
      results.set(year, {
        ...base,
        basePenaltyEur: 0,
        basePenaltyUsd: 0,
        multiplierSum: 1,
        firstMultiplier: 1,
        lastMultiplier: 1,
        blockYears,
        consecutiveYears: 0,
      });
    } else {
      // Sum multipliers for each calendar year in this block.
      // Art. 23(2): 1st non-compliant year → ×1.0, 2nd → ×1.1, 3rd → ×1.2, etc.
      // Calendar year k within block (k=0..blockYears-1): multiplier = 1.0 + (streak + k) × 0.1
      let multiplierSum = 0;
      for (let k = 0; k < blockYears; k++) {
        multiplierSum += 1.0 + (streak + k) * 0.1;
      }
      const firstMultiplier = 1.0 + streak * 0.1;
      const lastMultiplier  = 1.0 + (streak + blockYears - 1) * 0.1;
      // Average multiplier over the block — used for annual cost figures
      // so charts and tables (which show yearly costs) stay consistent
      const avgMultiplier   = multiplierSum / blockYears;

      const eurToUsd = euFactors.eurToUsd ?? 1.08;
      // Effective ANNUAL penalty = annual base × average multiplier
      const escalatedEur = base.penaltyEur * avgMultiplier;
      const escalatedUsd = escalatedEur * eurToUsd;

      results.set(year, {
        ...base,
        basePenaltyEur: base.penaltyEur,
        basePenaltyUsd: base.penaltyUsd,
        penaltyEur: escalatedEur,
        penaltyUsd: escalatedUsd,
        multiplierSum,
        avgMultiplier,
        firstMultiplier,
        lastMultiplier,
        blockYears,
        consecutiveYears: streak + blockYears,
      });

      streak += blockYears;
    }
  });

  return results;
}

// Replaces ResponsiveContainer — measures its own DOM width via ResizeObserver
// and passes exact pixel dimensions to children, so Recharts never sees 0-width.
function ChartBox({ height, children }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0) setWidth(Math.floor(w));
    });
    ro.observe(el);
    // Also read immediately in case already sized
    const w = el.getBoundingClientRect().width;
    if (w > 0) setWidth(Math.floor(w));
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrapRef} style={{ width: "100%", height }}>
      {width ? children(width, height) : null}
    </div>
  );
}

function renderScenarioGraphsCO2() {

  // ── shared formatting helpers ──────────────────────────────────────────
  const TICK_STYLE = { fontSize: 11, fill: "#94a3b8", fontFamily: "var(--font-mono)" };
  const TOOLTIP_STYLE = {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 4,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "#0f172a",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  };
  const fmtUSD = v => v >= 1_000_000
    ? `$${(v / 1_000_000).toFixed(0)}M`
    : v >= 1_000
    ? `$${(v / 1_000).toFixed(0)}k`
    : `$${Math.round(v)}`;
  function ChartTitle({ label }) {
    return (
      <div className="section-header" style={{ marginBottom: 6 }}>
        <span className="section-label">{label}</span>
        <div className="section-line" />
      </div>
    );
  }

  // ── DATA BUILDERS ──────────────────────────────────────────────────────

  // Chart 1: CO₂ intensity
  function buildCO2Data() {
    return SCENARIO_YEARS.map(year => {
      const blended = calculateBlendedFuels(year);
      const { baselineIntensity } = buildFleetBaseline(year);
      const row = { year, baseline: baselineIntensity };
      const fuelEligible = {};

      if (useOptimalAllocation) {
        const penResult = computeFuelEuPenaltyOptimal(blended.fuels, year);
        let totalEligibleMJ = 0;
        let totalEligibleCO2 = 0;
        Object.entries(blended.fuels).forEach(([fuel, values]) => {
          const eligHFOeq =
            values.inter.hfoEquivalentTons * euFactors.inter +
            values.mixed.hfoEquivalentTons * euFactors.mixed +
            values.out.hfoEquivalentTons   * euFactors.out;
          // RFNBO: bonus MJ = physical MJ × coverage factor (double-counting credit)
          const rfnboBonusMJ = (values.isRfnbo && year <= 2030)
            ? (values.inter.rfnboBonusMJ * euFactors.inter +
               values.mixed.rfnboBonusMJ * euFactors.mixed +
               values.out.rfnboBonusMJ   * euFactors.out)
            : 0;
          const eligMJ  = eligHFOeq * 1000. * fuelConfig.VLSFO.lhv.value + rfnboBonusMJ;
          const eligCO2 =
            values.inter.co2 * euFactors.inter +
            values.mixed.co2 * euFactors.mixed +
            values.out.co2   * euFactors.out;
          fuelEligible[fuel] = { eligMJ, eligCO2 };
          totalEligibleMJ  += eligMJ;
          totalEligibleCO2 += eligCO2;
        });
        const scaleFactor = penResult.actualIntensity !== null && totalEligibleMJ > 0
          ? (penResult.actualIntensity / ((totalEligibleCO2 / totalEligibleMJ) * 1_000_000))
          : 1;
        Object.entries(fuelEligible).forEach(([fuel, { eligCO2 }]) => {
          const val = totalEligibleMJ > 0 ? (eligCO2 * scaleFactor / totalEligibleMJ) * 1_000_000 : 0;
          row[`${fuel}_neg`] = val < 0 ? val : 0;
          row[`${fuel}_pos`] = val > 0 ? val : 0;
        });
        row.euTarget = euFactors.yearlyTargets[year] ?? null;
      } else {
        let totalEligibleMJ  = 0;
        // eslint-disable-next-line
        let totalEligibleCO2 = 0;
        Object.entries(blended.fuels).forEach(([fuel, values]) => {
          const eligHFOeq =
            values.inter.hfoEquivalentTons * euFactors.inter +
            values.mixed.hfoEquivalentTons * euFactors.mixed +
            values.out.hfoEquivalentTons   * euFactors.out;
          const rfnboBonusMJ = (values.isRfnbo && year <= 2030)
            ? (values.inter.rfnboBonusMJ * euFactors.inter +
               values.mixed.rfnboBonusMJ * euFactors.mixed +
               values.out.rfnboBonusMJ   * euFactors.out)
            : 0;
          const eligCO2 =
            values.inter.co2 * euFactors.inter +
            values.mixed.co2 * euFactors.mixed +
            values.out.co2   * euFactors.out;
          const eligMJ = eligHFOeq * 1000. * fuelConfig.VLSFO.lhv.value + rfnboBonusMJ;
          fuelEligible[fuel] = { eligMJ, eligCO2 };
          totalEligibleMJ  += eligMJ;
          totalEligibleCO2 += eligCO2;
        });
        Object.entries(fuelEligible).forEach(([fuel, { eligCO2 }]) => {
          const val = totalEligibleMJ > 0 ? (eligCO2 / totalEligibleMJ) * 1_000_000 : 0;
          row[`${fuel}_neg`] = val < 0 ? val : 0;
          row[`${fuel}_pos`] = val > 0 ? val : 0;
        });
        row.euTarget = euFactors.yearlyTargets[year] ?? null;
      }
      return row;
    });
  }

  // Chart 2: Scenario cost stacked bars (base fuel cost + FuelEU penalty when non-compliant)
  function buildCostBarsData() {
    const allPenalties = computeAllYearPenalties();
    return SCENARIO_YEARS.map(year => {
      const blended = calculateBlendedFuels(year);
      const { baselineCost } = buildFleetBaseline(year);
      const { penaltyUsd: penalty } = allPenalties.get(year);
      const row = { year, baselineCost: baselineCost !== null ? Math.round(baselineCost) : null };
      Object.entries(blended.fuels).forEach(([fuel, values]) => {
        row[`cost_${fuel}`] = Math.round(values.total.cost);
      });
      row.etsPenalty = Math.round(penalty);
      return row;
    });
  }

  // Chart 3: Cost scenario range (low/base/high total) + abatement cost range
  function buildRangeData() {
    const allPenalties = computeAllYearPenalties();
    return SCENARIO_YEARS.map(year => {
      const blended = calculateBlendedFuels(year);
      const { baselineCO2, baselineCost } = buildFleetBaseline(year);
      const { penaltyUsd: penalty } = allPenalties.get(year);
      let scenarioCost = 0, scenarioCostLow = 0, scenarioCostHigh = 0, scenarioCO2 = 0;
      Object.entries(blended.fuels).forEach(([, values]) => {
        scenarioCost     += values.total.cost;
        scenarioCostLow  += values.total.costLow  ?? values.total.cost;
        scenarioCostHigh += values.total.costHigh ?? values.total.cost;
        scenarioCO2      += values.total.co2;
      });
      // All-in: fuel cost + FuelEU penalty (only charged when non-compliant)
      const allInBase = scenarioCost     + penalty;
      const allInLow  = scenarioCostLow  + penalty;
      const allInHigh = scenarioCostHigh + penalty;
      const deltaCO2 = baselineCO2 !== null ? (baselineCO2 - scenarioCO2) : 0;
      // Abatement cost is only meaningful when the scenario actually reduces CO2 vs baseline
      const hasAbat = baselineCO2 !== null && deltaCO2 > 0.01;
      return {
        year,
        baselineCost: baselineCost !== null ? Math.round(baselineCost) : null,
        scenarioCostLow:   Math.round(allInLow),
        scenarioCostBase:  Math.round(allInBase),
        scenarioCostHigh:  Math.round(allInHigh),
        etsPenalty:        Math.round(penalty),
        costBandBase:  Math.round(allInLow),
        costBandWidth: Math.round(allInHigh - allInLow),
        abatLow:  hasAbat ? Math.round((allInLow  - baselineCost) / deltaCO2) : null,
        abatBase: hasAbat ? Math.round((allInBase - baselineCost) / deltaCO2) : null,
        abatHigh: hasAbat ? Math.round((allInHigh - baselineCost) / deltaCO2) : null,
        abatBandBase:  hasAbat ? Math.round((allInLow  - baselineCost) / deltaCO2) : null,
        abatBandWidth: hasAbat ? Math.round((allInHigh - allInLow) / deltaCO2) : null,
      };
    });
  }

  // Chart 4: Fuel share over time — by energy delivered (MJ, default) or by
  // total scenario cost (USD), per fuel, per year. Energy share shows the
  // physical fuel mix; cost share shows which fuel dominates spend exposure.
  function buildFuelShareData(metric) {
    const rows = SCENARIO_YEARS.map(year => {
      const blended = calculateBlendedFuels(year);
      const valueKey = metric === "cost" ? "cost" : "energyMJ";
      const total = Object.values(blended.fuels).reduce((s, v) => s + v.total[valueKey], 0);
      const row = { year };
      Object.entries(blended.fuels).forEach(([fuel, values]) => {
        row[fuel] = total > 0 ? parseFloat(((values.total[valueKey] / total) * 100).toFixed(1)) : 0;
      });
      return row;
    });

    // Collect every fuel key that appears in any year, then backfill
    // missing years with 0 so Recharts renders a flat zero instead of a gap.
    const allFuelKeys = Array.from(new Set(
      rows.flatMap(r => Object.keys(r).filter(k => k !== "year"))
    ));
    rows.forEach(row => {
      allFuelKeys.forEach(fuel => {
        if (row[fuel] === undefined) row[fuel] = 0;
      });
    });

    return rows;
  }

  const co2Data       = buildCO2Data();
  const costBarsData  = buildCostBarsData();
  const rangeData     = buildRangeData();
  const fuelShareData = buildFuelShareData(fuelShareMetric);

  const co2FuelKeys = Array.from(new Set(
    co2Data.flatMap(r =>
      Object.keys(r)
        .filter(k => k.endsWith("_pos"))
        .map(k => k.slice(0, -4))
    )
  ));

  // Pure positive geometry for stacking.
  // co2NegOffset = global max of abs(sum of negatives) across all years — used as the floor.
  // All bars are positive heights. Zero line sits at co2NegOffset in data space.
  // Y-axis labels subtract co2NegOffset so zero reads "0".
  // Baseline line = co2NegOffset + net (net = sum of all fuel contributions, signed).
  const co2NegOffset = Math.max(0, ...co2Data.map(row =>
    co2FuelKeys.reduce((sum, f) => sum + Math.abs(Math.min(0, row[`${f}_neg`] ?? 0)), 0)
  ));

  const co2DataStacked = co2Data.map(row => {
    const enriched = { year: row.year };
    // Per-year abs negative sum
    const yearNegAbs = co2FuelKeys.reduce((sum, f) => sum + Math.abs(Math.min(0, row[`${f}_neg`] ?? 0)), 0);
    // Transparent padding bar: fills from 0 up to where this year's neg stack starts
    // so years with fewer negatives still anchor visually at the zero line
    enriched._negPad = co2NegOffset - yearNegAbs;
    // Neg bars: absolute value, stacked on top of padding
    co2FuelKeys.forEach(f => {
      enriched[`${f}_neg`] = Math.abs(Math.min(0, row[`${f}_neg`] ?? 0));
    });
    // Pos bars on top
    co2FuelKeys.forEach(f => {
      enriched[`${f}_pos`] = Math.max(0, row[`${f}_pos`] ?? 0);
    });
    // Net = signed sum of all contributions
    const net = co2FuelKeys.reduce((sum, f) =>
      sum + (row[`${f}_neg`] ?? 0) + (row[`${f}_pos`] ?? 0), 0);
    // Baseline line = original HFO reference intensity, shifted into data space
    enriched.baseline = row.baseline != null ? row.baseline + co2NegOffset : null;
    enriched.scenarioNet = co2NegOffset + net;
    enriched.euTarget = row.euTarget != null ? row.euTarget + co2NegOffset : null;
    return enriched;
  });

  const co2YMax = Math.max(120 + co2NegOffset, ...co2DataStacked.map(row =>
    co2FuelKeys.reduce((sum, f) => sum + (row[`${f}_neg`] ?? 0) + (row[`${f}_pos`] ?? 0), 0)
  ));
  const co2Domain = [0, Math.ceil(co2YMax / 10) * 10];
  const costFuelKeys = Array.from(new Set(
    costBarsData.flatMap(r => Object.keys(r).filter(k => k.startsWith("cost_")))
  ));
  const shareFuelKeys = Array.from(new Set(
    fuelShareData.flatMap(r => Object.keys(r).filter(k => k !== "year"))
  ));

  // Y-axis domain helpers
  const rangeAbatVals = rangeData.flatMap(d => [d.abatLow, d.abatHigh]).filter(v => v !== null && isFinite(v));
  const abatMin = rangeAbatVals.length ? Math.min(0, Math.floor(Math.min(...rangeAbatVals) / 500) * 500) : 0;
  const abatMax = rangeAbatVals.length ? Math.ceil(Math.max(...rangeAbatVals) / 500) * 500 : 2000;

  // Phantom right YAxis — invisible, same width as chart 3's right axis so all
  // four charts have identical plot-area width and x-axes align perfectly.

  // ── DASHBOARD LAYOUT ─────────────────────────────────────────────
  // Controls which chart goes in each quadrant of the 2×2 grid below.
  // Valid values: "fuelShare" | "fuelCost" | "co2Intensity" | "abatement"
  // Just change the values here to rearrange the dashboard.
  const CHART_GRID_LAYOUT = {
    topLeft:     "fuelShare",
    topRight:    "fuelCost",
    bottomLeft:  "co2Intensity",
    bottomRight: "abatement",
  };

  const charts = {
    // Fuel Share — energy vs cost toggle
    fuelShare: (
      <div className="tab-card" key="fuelShare">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <ChartTitle label={fuelShareMetric === "cost" ? "Fuel Cost Exposure — Share by Fuel" : "Fuel Energy Mix — Share by Fuel"} />
          <div
            title="Toggle whether the share below each fuel is measured by energy delivered (MJ) or by scenario cost (USD)"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: fuelShareMetric === "cost" ? "rgba(29,158,117,0.08)" : "rgba(148,163,184,0.06)",
              border: `1px solid ${fuelShareMetric === "cost" ? "rgba(29,158,117,0.35)" : "rgba(148,163,184,0.2)"}`,
              borderRadius: 4, padding: "3px 8px", flexShrink: 0, cursor: "pointer",
              transition: "all 0.15s",
            }}
            onClick={() => setFuelShareMetric(m => m === "cost" ? "energy" : "cost")}
          >
            <span style={{
              fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px",
              textTransform: "uppercase", color: fuelShareMetric === "cost" ? "#5DCAA5" : "#94a3b8",
            }}>
              {fuelShareMetric === "cost" ? "$ cost share" : "⚡ energy share"}
            </span>
            <div style={{
              width: 28, height: 16, borderRadius: 8, position: "relative",
              background: fuelShareMetric === "cost" ? "#1D9E75" : "rgba(148,163,184,0.3)",
              border: `1px solid ${fuelShareMetric === "cost" ? "#0F6E56" : "rgba(148,163,184,0.4)"}`,
              transition: "background .2s, border .2s", flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: 2, left: fuelShareMetric === "cost" ? 13 : 2,
                width: 10, height: 10, borderRadius: "50%",
                background: "#fff", transition: "left .2s",
              }} />
            </div>
          </div>
        </div>
        <p className="chart-subtitle">
          {fuelShareMetric === "cost"
            ? "100% stacked area = share of total scenario cost attributable to each fuel over time. Reveals which fuel drives cost and when the mix shifts."
            : "100% stacked area = share of total energy delivered (MJ) by each fuel over time. Reveals the physical fuel mix, independent of price."}
        </p>
        <div style={{ width: "100%", height: 420 }}>
          <ChartBox height={420}>{(w, h) => (
            <AreaChart width={w} height={h} data={fuelShareData} margin={{ top: 10, right: 10, left: 32, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="year" tick={TICK_STYLE} />
              <YAxis yAxisId="left" orientation="left" tick={TICK_STYLE} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={v => `${Math.round(v)}%`}
                label={{ value: fuelShareMetric === "cost" ? "% of cost" : "% of energy", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#94a3b8", fontSize: 11, fontFamily: "var(--font-mono)" } }} />
              <YAxis yAxisId="right" orientation="right" width={80} tick={false} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (value === null || value === undefined || value === 0) return null;
                  return [`${value.toFixed(1)}%`, fuelDisplayName(name)];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "var(--font-mono)", fontSize: 10 }}
                formatter={name => fuelDisplayName(name)}
              />
              {shareFuelKeys.map(fuel => (
                <Area key={fuel} yAxisId="left" type="monotone" dataKey={fuel} stackId="share"
                  fill={FUEL_COLORS[fuel] || "#8884d8"}
                  stroke={FUEL_COLORS[fuel] || "#8884d8"}
                  fillOpacity={0.85}
                  name={fuel}
                />
              ))}
            </AreaChart>
          )}</ChartBox>
        </div>
      </div>

    ),

    // Annual Fuel Cost — Scenario vs No-Blend Reference
    fuelCost: (
      <div className="tab-card" key="fuelCost">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <ChartTitle label="Annual Fuel Cost — Scenario vs No-Blend Reference" />
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase",
            padding: "2px 7px", borderRadius: 4, flexShrink: 0, marginLeft: 8,
            background: useOptimalAllocation ? "rgba(29,158,117,0.12)" : "rgba(148,163,184,0.1)",
            border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.4)" : "rgba(148,163,184,0.25)"}`,
            color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
          }}>
            {useOptimalAllocation ? "⚡ optimal" : "⇌ proportional"}
          </span>
        </div>
        <p className="chart-subtitle">
          Stacked bars = scenario fuel cost by fuel + FuelEU penalty (only when intensity exceeds yearly target) · Grey = no-blend reference
        </p>
        <div style={{ width: "100%", height: 420 }}>
          <ChartBox height={420}>{(w, h) => (
            <ComposedChart width={w} height={h} data={costBarsData} margin={{ top: 10, right: 10, left: 32, bottom: 20 }} barCategoryGap="18%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="year" tick={TICK_STYLE} />
              <YAxis yAxisId="left" orientation="left" tick={TICK_STYLE} tickFormatter={fmtUSD}
                label={{ value: "USD/yr", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#94a3b8", fontSize: 11, fontFamily: "var(--font-mono)" } }} />
              <YAxis yAxisId="right" orientation="right" width={80} tick={false} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (value === null || value === undefined || value === 0) return null;
                  if (name === "No-Blend Reference Cost") return [fmtUSD(value), name];
                  if (name === "FuelEU Penalty") return [fmtUSD(value), "FuelEU Penalty (excess only)"];
                  return [fmtUSD(value), fuelDisplayName(name.replace("cost_", ""))];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "var(--font-mono)", fontSize: 10 }}
                formatter={name => {
                  if (name === "FuelEU Penalty" || name === "No-Blend Reference Cost") return name;
                  return name.startsWith("cost_") ? fuelDisplayName(name.replace("cost_", "")) : name;
                }}
              />
              {costFuelKeys.map(key => {
                const fuel = key.replace("cost_", "");
                return <Bar key={key} yAxisId="left" dataKey={key} stackId="c" fill={FUEL_COLORS[fuel] || "#8884d8"} name={key} />;
              })}
              <Bar yAxisId="left" dataKey="etsPenalty" stackId="c" fill="rgba(239,68,68,0.7)" name="FuelEU Penalty" />
              <Line yAxisId="left" type="monotone" dataKey="baselineCost" name="No-Blend Reference Cost" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3, fill: "#94a3b8" }} connectNulls={false} />
            </ComposedChart>
          )}</ChartBox>
        </div>
      </div>

    ),

    // EU CO₂ Intensity vs FuelEU Target
    co2Intensity: (
      <div className="tab-card" key="co2Intensity">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <ChartTitle label="EU CO₂ Intensity vs FuelEU Target" />
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase",
            padding: "2px 7px", borderRadius: 4, flexShrink: 0, marginLeft: 8,
            background: useOptimalAllocation ? "rgba(29,158,117,0.12)" : "rgba(148,163,184,0.1)",
            border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.4)" : "rgba(148,163,184,0.25)"}`,
            color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
          }}>
            {useOptimalAllocation ? "⚡ optimal" : "⇌ proportional"}
          </span>
        </div>
        <p className="chart-subtitle">
          Stacked bars = blended scenario (gCO₂/MJ) · Hatched = emission credits · Grey = no-blend reference · Dashed = FuelEU limit
        </p>
        <div style={{ width: "100%", height: 420 }}>
          <ChartBox height={420}>{(w, h) => (
            <ComposedChart width={w} height={h} data={co2DataStacked} margin={{ top: 10, right: 10, left: 32, bottom: 20 }} barCategoryGap="18%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="year" tick={TICK_STYLE} />
              <YAxis yAxisId="left" orientation="left" tick={TICK_STYLE} domain={co2Domain}
                tickFormatter={v => Number.isFinite(v) ? Math.round(v - co2NegOffset) : v}
                label={{ value: "gCO₂/MJ", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#94a3b8", fontSize: 11, fontFamily: "var(--font-mono)" } }} />
              <YAxis yAxisId="right" orientation="right" width={80} tick={false} axisLine={false} tickLine={false} />
              <ReferenceLine yAxisId="left" y={co2NegOffset} stroke="#000000" strokeWidth={2} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (value === null || value === undefined || value === 0) return null;
                  if (name === "") return null;
                  if (name === "FuelEU Target" || name === "No-Blend Reference" || name === "Scenario Net")
                    return [(value - co2NegOffset).toFixed(2) + " gCO₂/MJ", name];
                  const isCredit = name.endsWith(" (credit)");
                  const baseName = isCredit ? name.slice(0, -9) : name;
                  const label = isCredit
                    ? `${fuelDisplayName(baseName)} — emission credit`
                    : fuelDisplayName(baseName);
                  return [(isCredit ? -value : value).toFixed(3) + " gCO₂/MJ", label];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "var(--font-mono)", fontSize: 10 }}
                formatter={name => {
                  if (name === "" ) return null;
                  if (name === "No-Blend Reference" || name === "FuelEU Target") return name;
                  const isCredit = name.endsWith(" (credit)");
                  const baseName = isCredit ? name.slice(0, -9) : name;
                  return isCredit ? `${fuelDisplayName(baseName)} (credit)` : fuelDisplayName(baseName);
                }}
              />
              {/* Transparent padding bar — anchors stack to zero line for years with no/fewer negatives */}
              <Bar yAxisId="left" dataKey="_negPad" stackId="s" fill="transparent" stroke="none" legendType="none" tooltipType="none" name="" />
              {co2FuelKeys.map(fuel => {
                const color = FUEL_COLORS[fuel] || "#8884d8";
                const patternId = `hatch-${fuel}`;
                return (
                  <Bar key={`${fuel}_neg`} yAxisId="left" dataKey={`${fuel}_neg`} stackId="s"
                    name={`${fuel} (credit)`}
                    shape={(props) => {
                      const { x, y, width, height } = props;
                      if (!height || height === 0) return null;
                      const rectY = Math.min(y, y + height);
                      const rectH = Math.abs(height);
                      return (
                        <g>
                          <defs>
                            <pattern id={patternId} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                              <rect width="6" height="6" fill={color} fillOpacity={0.15} />
                              <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth={2.5} strokeOpacity={0.7} />
                            </pattern>
                          </defs>
                          <rect x={x} y={rectY} width={width} height={rectH}
                            fill={`url(#${patternId})`} stroke={color} strokeWidth={1} />
                        </g>
                      );
                    }}
                  />
                );
              })}
              {co2FuelKeys.map(fuel => (
                <Bar key={`${fuel}_pos`} yAxisId="left" dataKey={`${fuel}_pos`} stackId="s"
                  fill={FUEL_COLORS[fuel] || "#8884d8"} name={fuel} />
              ))}
              <Line yAxisId="left" type="monotone" dataKey="baseline" name="No-Blend Reference" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3, fill: "#94a3b8" }} connectNulls={false} />
              <Line yAxisId="left" type="monotone" dataKey="scenarioNet" name="Scenario Net" stroke="#2dd4bf" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: "#2dd4bf" }} connectNulls />
              <Line yAxisId="left" type="monotone" dataKey="euTarget" name="FuelEU Target" stroke="#f97316" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: "#f97316" }} connectNulls />
            </ComposedChart>
          )}</ChartBox>
        </div>
      </div>

    ),

    // Total Cost Range & Abatement Cost
    abatement: (
      <div className="tab-card" key="abatement">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <ChartTitle label="Total Cost Range & Abatement Cost (incl. EU ETS)" />
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase",
            padding: "2px 7px", borderRadius: 4, flexShrink: 0, marginLeft: 8,
            background: useOptimalAllocation ? "rgba(29,158,117,0.12)" : "rgba(148,163,184,0.1)",
            border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.4)" : "rgba(148,163,184,0.25)"}`,
            color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
          }}>
            {useOptimalAllocation ? "⚡ optimal" : "⇌ proportional"}
          </span>
        </div>
        <p className="chart-subtitle">
          Teal = total scenario cost range (fuel + FuelEU penalty if non-compliant) · Amber = abatement cost (USD/tCO₂ avoided) · Shaded = low–high spread
          {rangeAbatVals.length === 0 && (
            <span style={{ display: "block", marginTop: 4, color: "#f87171", fontFamily: "var(--font-mono)", fontSize: 9 }}>
              ⚠ Abatement cost unavailable — scenario CO₂ exceeds no-blend reference for all years
            </span>
          )}
        </p>
        <div style={{ width: "100%", height: 420 }}>
          <ChartBox height={420}>{(w, h) => (
            <ComposedChart width={w} height={h} data={rangeData} margin={{ top: 10, right: 10, left: 32, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="year" tick={TICK_STYLE} />
              <YAxis yAxisId="cost" orientation="left" tick={TICK_STYLE} tickFormatter={fmtUSD}
                label={{ value: "USD/yr", angle: -90, position: "insideLeft", offset: 10, style: { textAnchor: "middle", fill: "#2dd4bf", fontSize: 11, fontFamily: "var(--font-mono)" } }} />
              <YAxis yAxisId="abat" orientation="right" width={80} domain={[abatMin, abatMax]}
                tick={{ ...TICK_STYLE, fill: "#fbbf24" }}
                tickFormatter={v => `$${Math.round(v)}/t`}
                label={{ value: "USD/tCO₂", angle: 90, position: "insideRight", offset: 10, style: { textAnchor: "middle", fill: "#fbbf24", fontSize: 11, fontFamily: "var(--font-mono)" } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  if (value === null || value === undefined) return null;
                  if (name === "Cost Range" || name === "Abat Range" ||
                      name === "Cost Low"   || name === "Abat Low") return null;
                  if (name.startsWith("Abat")) return [`$${value.toLocaleString()} /tCO₂`, name];
                  if (name === "Abatement Cost") return [`$${value.toLocaleString()} /tCO₂`, name];
                  return [fmtUSD(value), name];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "var(--font-mono)", fontSize: 10 }} />
              <Area yAxisId="cost" type="monotone" dataKey="costBandBase" name="Cost Low"
                stroke="rgba(45,212,191,0.5)" strokeWidth={1} strokeDasharray="4 3"
                fill="transparent" legendType="none" dot={false} connectNulls={false}
                stackId="costband" />
              <Area yAxisId="cost" type="monotone" dataKey="costBandWidth" name="Cost Range"
                stroke="rgba(45,212,191,0.5)" strokeWidth={1} strokeDasharray="4 3"
                fill="rgba(45,212,191,0.15)" legendType="none" dot={false} connectNulls={false}
                stackId="costband" />
              <Line yAxisId="cost" type="monotone" dataKey="scenarioCostBase" name="Blending cost"
                stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3, fill: "#2dd4bf" }} connectNulls={false} />
              <Line yAxisId="cost" type="monotone" dataKey="baselineCost" name="No-Blend Reference Cost"
                stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 2, fill: "#94a3b8" }} connectNulls={false} />
              <Area yAxisId="abat" type="monotone" dataKey="abatBandBase" name="Abat Low"
                stroke="rgba(251,191,36,0.4)" strokeWidth={1} strokeDasharray="4 3"
                fill="transparent" legendType="none" dot={false} connectNulls={false}
                stackId="abatband" />
              <Area yAxisId="abat" type="monotone" dataKey="abatBandWidth" name="Abat Range"
                stroke="rgba(251,191,36,0.4)" strokeWidth={1} strokeDasharray="4 3"
                fill="rgba(251,191,36,0.12)" legendType="none" dot={false} connectNulls={false}
                stackId="abatband" />
              <Line yAxisId="abat" type="monotone" dataKey="abatBase" name="Abatement Cost"
                stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3, fill: "#fbbf24" }} connectNulls={false} />
            </ComposedChart>
          )}</ChartBox>
        </div>
      </div>
    ),
  };

  return (
    <div className="chart-grid-2x2" ref={el => tourRefs.current["tour-chart-grid"] = el}>
      {charts[CHART_GRID_LAYOUT.topLeft]}
      {charts[CHART_GRID_LAYOUT.topRight]}
      {charts[CHART_GRID_LAYOUT.bottomLeft]}
      {charts[CHART_GRID_LAYOUT.bottomRight]}
    </div>
  );
}


  return (
    <div className="app-shell">
      <style>{`
        @keyframes snackbar-in {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      {/* ── LEFT SIDEBAR ── */}
      <aside className={"sidebar" + (leftOpen ? "" : " collapsed")} ref={el => tourRefs.current["tour-left-sidebar"] = el}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="36" height="36" style={{ display: "block", flexShrink: 0 }}>
                <rect width="512" height="512" rx="96" fill="#0d9488"/>
                <path d="M128 288 L384 288 L352 352 L160 352 Z" fill="#ffffff" opacity="0.95"/>
                <rect x="196" y="192" width="148" height="96" rx="12" fill="#ffffff" opacity="0.95"/>
                <rect x="228" y="112" width="84" height="84" rx="10" fill="#ffffff" opacity="0.88"/>
                <rect x="296" y="60" width="36" height="60" rx="6" fill="#ffffff" opacity="0.72"/>
                <path d="M308 52 Q302 32 310 16" fill="none" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" opacity="0.4"/>
                <path d="M320 48 Q316 28 326 12" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" opacity="0.25"/>
                <ellipse cx="360" cy="100" rx="72" ry="44" fill="#4ade80" opacity="0.95" transform="rotate(-40 360 100)"/>
                <line x1="332" y1="128" x2="388" y2="72" stroke="#ffffff" strokeWidth="4" opacity="0.6" strokeLinecap="round"/>
                <line x1="352" y1="114" x2="370" y2="96" stroke="#ffffff" strokeWidth="2.5" opacity="0.4" strokeLinecap="round"/>
                <line x1="362" y1="100" x2="376" y2="86" stroke="#ffffff" strokeWidth="2.5" opacity="0.4" strokeLinecap="round"/>
                <path d="M100 370 Q150 354 200 370 Q250 386 300 370 Q350 354 412 370" fill="none" stroke="#67e8f9" strokeWidth="8" strokeLinecap="round" opacity="0.7"/>
                <path d="M128 394 Q176 380 224 394 Q272 408 320 394 Q368 380 400 394" fill="none" stroke="#67e8f9" strokeWidth="6" strokeLinecap="round" opacity="0.4"/>
              </svg>
            </div>
            <div>
              <div className="sidebar-title">FuelEU Planner</div>
              <div className="sidebar-subtitle">Fleet Emissions Compliance</div>
            </div>
          </div>
        </div>

        <div className="sidebar-body">
          {/* Active route breadcrumb */}
          {/*<div>
            <div className="section-header">
              <span className="section-label">Voyage Editing</span>
              <div className="section-line" />
            </div>

            <div className={`route-breadcrumb ${route.ports.length > 0 ? "active" : ""}`}>
              {route.ports.map((p, i, arr) => {
                const name = ports.find(port => port.id === p.id)?.name || p.id;

                return (
                  <span key={i}>
                    <span className="breadcrumb-port">
                      {name}
                    </span>
                    {i < arr.length - 1 && (
                      <span className="breadcrumb-arrow"> › </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>*/}

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {route.status === "finished" && (
              <button className="btn btn-save" onClick={saveRoute}>
                ✦ Save Voyage
              </button>
            )}
            {route.status !== "idle" && (
              <button
                className="btn btn-ghost"
                onClick={() => dispatch({ type: "RESET" })}
                style={{ borderColor: "rgba(239,68,68,0.4)", color: "#f87171" }}
              >
                ✕ Cancel
              </button>
            )}
          </div>

          {/* Explore Mode Toggle */}
          <button
            ref={el => tourRefs.current["tour-explore-btn"] = el}
            onClick={() => setExploreMode(v => !v)}
            title={exploreMode ? "Exit explore mode and return to voyage building" : "Explore port connections on the map"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              width: "100%",
              padding: "8px 12px",
              borderRadius: 6,
              border: `1px solid ${exploreMode ? "#7c3aed" : "#e2e8f0"}`,
              background: exploreMode ? "rgba(139,92,246,0.08)" : "#f8fafc",
              color: exploreMode ? "#7c3aed" : "#475569",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: exploreMode ? 600 : 400,
              cursor: "pointer",
              letterSpacing: "0.3px",
              transition: "all 0.15s",
            }}
          >
            <span>{exploreMode ? "🔍" : "🔍"}</span>
            <span>{exploreMode ? "Exit Explore Mode" : "Explore Port Connections"}</span>
            {exploreMode && (
              <span style={{
                marginLeft: "auto",
                background: "#8b5cf6",
                color: "#fff",
                fontSize: 8,
                padding: "1px 5px",
                borderRadius: 3,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
              }}>ON</span>
            )}
          </button>

          {/* Saved Routes */}
          <div>
            <div className="section-header">
              <span className="section-label">
                My Voyages
                {savedRoutes.length > 0 && (
                  <span style={{ marginLeft: 6, opacity: 0.6 }}>
                    [{savedRoutes.length}]
                  </span>
                )}
              </span>
              <div className="section-line" />

            </div>

            {savedRoutes.length === 0 ? (
              <div className="empty-state">No voyages saved yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

                {/* Select All / Unselect All */}
                <div style={{ display: "flex", gap: 6, marginBottom: 2 }}>
                  <button
                    className="btn btn-ghost"
                    style={{ flex: 1, fontSize: 10, padding: "3px 0" }}
                    onClick={() => setSelectedRouteIds(new Set(savedRoutes.map(r => r.id)))}
                  >
                    ☑ Select All
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ flex: 1, fontSize: 10, padding: "3px 0" }}
                    onClick={() => setSelectedRouteIds(new Set())}
                  >
                    ☐ Clear
                  </button>
                </div>

                {savedRoutes.map(r => (
                  <div
                    key={r.id}
                    className={[
                      "route-card",
                      selectedRouteIds.has(r.id) ? "selected" : "",
                      route.status !== "idle" ? "disabled" : "",
                    ].join(" ")}
                    onClick={() => {
                      if (route.status !== "idle") return;
                      // Toggle this route's presence in the selected set (for map display)
                      setSelectedRouteIds(prev => {
                        const next = new Set(prev);
                        if (next.has(r.id)) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      });
                    }}
                  >
                    <div className="route-card-header">
                      <span className="route-icon">🚢</span>
                      {renamingId === r.id ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              const name = renameValue.trim() || r.name;
                              setSavedRoutes(prev => prev.map(x => x.id === r.id ? { ...x, name } : x));
                              setRenamingId(null);
                            } else if (e.key === "Escape") {
                              setRenamingId(null);
                            }
                          }}
                          onBlur={() => {
                            const name = renameValue.trim() || r.name;
                            setSavedRoutes(prev => prev.map(x => x.id === r.id ? { ...x, name } : x));
                            setRenamingId(null);
                          }}
                          onClick={e => e.stopPropagation()}
                          style={{
                            flex: 1,
                            background: "rgba(15,23,42,0.6)",
                            border: "1px solid rgba(45,212,191,0.4)",
                            borderRadius: 4,
                            color: "var(--slate-200)",
                            fontFamily: "var(--font-mono)",
                            fontSize: 12,
                            padding: "2px 6px",
                            outline: "none",
                          }}
                        />
                      ) : (
                        <span
                          className="route-name"
                          onDoubleClick={e => {
                            e.stopPropagation();
                            setRenamingId(r.id);
                            setRenameValue(r.name);
                          }}
                          title="Double-click to rename"
                        >{r.name}</span>
                      )}
                    </div>

                    <div className="route-ports">
                      {r.ports
                        .map(p => ports.find(port => port.id === p.id)?.name || p.id)
                        .join(" › ")}
                    </div>

                    <div className="route-card-actions">

                      <button
                        className="btn btn-ghost"
                        onClick={e => {
                          e.stopPropagation();
                          setRenamingId(r.id);
                          setRenameValue(r.name);
                        }}
                        title="Rename voyage"
                      >
                        ✎ Rename
                      </button>

                      <button
                        className="btn btn-ghost"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingId(prev => prev === r.id ? null : r.id);
                        }}
                      >
                        {editingId === r.id ? "Close" : "Edit"}
                      </button>

                      {r.ports.length > 2 && (
                        <button
                          className="btn btn-ghost"
                          title="Split this voyage into individual legs"
                          onClick={e => {
                            e.stopPropagation();
                            // Build one route per consecutive port pair
                            const newRoutes = [];
                            for (let i = 0; i < r.ports.length - 1; i++) {
                              const fromPort = r.ports[i];
                              const toPort   = r.ports[i + 1];
                              // Find the segment between these two ports
                              const seg = r.segments?.find(
                                s => s.from === fromPort.id && s.to === toPort.id
                              ) ?? { from: fromPort.id, to: toPort.id, distance: 0 };
                              newRoutes.push({
                                ...r,
                                id:       `${r.id}-leg${i + 1}-${Date.now()}`,
                                name:     `${r.name} — leg ${i + 1}`,
                                ports:    [fromPort, toPort],
                                segments: [seg],
                              });
                            }
                            setSavedRoutes(prev => {
                              const idx = prev.findIndex(x => x.id === r.id);
                              const next = [...prev];
                              next.splice(idx, 1, ...newRoutes);
                              return next;
                            });
                            setSelectedRouteIds(new Set(newRoutes.map(nr => nr.id)));
                            setEditingId(null);
                          }}
                          style={{ borderColor: "rgba(45,212,191,0.35)", color: "var(--teal-400)" }}
                        >
                          ✂ Split legs
                        </button>
                      )}

                      <button
                        className="btn btn-danger"
                        onClick={e => {
                          e.stopPropagation();
                          confirmDeleteRoute(r.id);
                        }}
                      >
                        Delete
                      </button>

                    </div>

                    {editingId === r.id && (
                      <div className="vessel-editor">

                        {/* ── OPERATIONS ── */}
                        <div className="section-header" style={{ marginTop: 10 }}>
                          <span className="section-label">Operations</span>
                          <div className="section-line" />
                        </div>

                        <div className="vessel-field">
                          <span className="vessel-field-label">Voyages per year</span>
                          <input
                            type="number"
                            min="0"
                            className="vessel-input"
                            value={r.operations?.tripsPerYear ?? 1}
                            onChange={e =>
                              updateTripsPerYear(r.id, Number(e.target.value))
                            }
                          />
                        </div>

                        {/* ── VESSEL ── */}
                        <div className="section-header" style={{ marginTop: 12 }}>
                          <span className="section-label">Vessel</span>
                          <div className="section-line" />
                        </div>

                        {/* PRESET */}
                        <div className="vessel-field">
                          <span className="vessel-field-label">Preset</span>
                          <select
                            className="vessel-input"
                            value={r.vessel.preset}
                            onChange={e => updateVesselPreset(r.id, e.target.value)}
                          >
                            {VESSEL_PRESETS.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* FUEL TYPE — always shown */}
                        <div className="vessel-field">
                          <span className="vessel-field-label">Fuel Type</span>
                          <select
                            className="vessel-input"
                            value={r.vessel.fuel.type}
                            onChange={e =>
                              updateVesselField(r.id, "fuelType", e.target.value)
                            }
                          >
                            {renderFuelOptions()}
                          </select>
                        </div>

                        {/* SPEED — always shown (read-only for presets) */}
                        <div className="vessel-field">
                          <span className="vessel-field-label">Speed (kn)</span>
                          <input
                            type="number"
                            className="vessel-input"
                            value={r.vessel.speed.value}
                            readOnly={r.vessel.preset !== "custom"}
                            style={r.vessel.preset !== "custom" ? { opacity: 0.5 } : {}}
                            onChange={e =>
                              r.vessel.preset === "custom" &&
                              updateVesselField(r.id, "speed", Number(e.target.value))
                            }
                          />
                        </div>

                        {/* CAPACITY — always shown (read-only for presets) */}
                        <div className="vessel-field">
                          <span className="vessel-field-label">Capacity (TEU)</span>
                          <input
                            type="number"
                            className="vessel-input"
                            value={r.vessel.capacity.value}
                            readOnly={r.vessel.preset !== "custom"}
                            style={r.vessel.preset !== "custom" ? { opacity: 0.5 } : {}}
                            onChange={e =>
                              r.vessel.preset === "custom" &&
                              updateVesselField(r.id, "capacity", Number(e.target.value))
                            }
                          />
                        </div>

                        {/* CONSUMPTION — always shown (read-only for presets) */}
                        <div className="vessel-field">
                          <span className="vessel-field-label">Consumption (t/day)</span>
                          <input
                            type="number"
                            className="vessel-input"
                            value={r.vessel.fuel.consumption}
                            readOnly={r.vessel.preset !== "custom"}
                            style={r.vessel.preset !== "custom" ? { opacity: 0.5 } : {}}
                            onChange={e =>
                              r.vessel.preset === "custom" &&
                              updateVesselField(r.id, "fuelConsumption", Number(e.target.value))
                            }
                          />
                        </div>

                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
          <div className="sidebar-footer">
              <button
                className="btn btn-primary"
                style={{ width: "100%", marginBottom: 8 }}
                onClick={openNewRouteModal}
              >
                + New Voyage
              </button>
              {savedRoutes.length > 0 && (
                <button
                  className="btn btn-danger"
                  style={{ width: "100%", opacity: 0.75 }}
                  onClick={() => setShowClearModal(true)}
                >
                  ✕ Clear All Voyages
                </button>
              )}
            </div>
      </aside>


      {/* ── CENTER: TABBED PANEL ── */}
      <div className="center-panel">

        {/* TAB BAR */}
        <div className="tab-bar">
          {/* LEFT SIDEBAR TOGGLE — far left of tab bar */}
          <button
            className="tab-sidebar-toggle tab-sidebar-toggle-left"
            onClick={() => setLeftOpen(v => !v)}
            title={leftOpen ? "Hide voyage panel" : "Show voyage panel"}
          >
            {leftOpen ? "◀" : "▶"}
          </button>

          {/* CENTERED TABS */}
          <div className="tab-bar-center">
            {[
              { id: "map",        icon: "🗺",  label: "Voyage Editor",  tourRef: "tour-tab-map"        },
              { id: "strategy",   icon: "⚗️",  label: "Fuel Strategy",  tourRef: "tour-tab-strategy"   },
              { id: "allocation", icon: "⚡",  label: "Allocation",     tourRef: "tour-tab-allocation" },
              { id: "tables",     icon: "📊",  label: "Data Tables",    tourRef: "tour-tab-tables"     },
              { id: "analytics",  icon: "📈",  label: "Analytics",      tourRef: "tour-tab-analytics"  },
            ].map(tab => (
              <button
                key={tab.id}
                ref={el => tourRefs.current[tab.tourRef] = el}
                className={"tab-btn" + (activeTab === tab.id ? " active" : "")}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* RIGHT SIDEBAR TOGGLE — far right of tab bar */}
          <button
            className="tab-sidebar-toggle tab-sidebar-toggle-right"
            onClick={() => setRightOpen(v => !v)}
            title={rightOpen ? "Hide parameters panel" : "Show parameters panel"}
          >
            {rightOpen ? "▶" : "◀"}
          </button>
        </div>

        {/* TAB CONTENT */}
        <div className="tab-content">

          {/* ── TAB 1: ROUTE EDITOR (MAP) ── */}
          {activeTab === "map" && (
            <div className="tab-pane-map" style={{ position: "relative" }}>

              {/* Voyage-in-progress banner */}
              {route.status !== "idle" && (
                <div style={{
                  position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
                  zIndex: 20, display: "flex", alignItems: "center", gap: 10,
                  background: "rgba(255,255,255,0.95)", border: "1px solid rgba(45,212,191,0.5)",
                  borderRadius: 8, padding: "8px 16px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  backdropFilter: "blur(8px)", maxWidth: "calc(100% - 48px)",
                }}>
                  <span style={{ fontSize: 16 }}>✏️</span>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    <span style={{ color: "#0d7a6b", fontWeight: 600, letterSpacing: "0.5px" }}>
                      Creating voyage:
                    </span>
                    {" "}
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>
                      {pendingRouteName || "Unnamed Voyage"}
                    </span>
                    <span style={{ color: "#64748b", marginLeft: 10 }}>
                      {route.ports.length === 0
                        ? "→ Select the first port on the map"
                        : route.status === "finished"
                          ? `→ ${route.ports.length} ports selected · ready to save`
                          : `→ ${route.ports.length} port${route.ports.length !== 1 ? "s" : ""} selected · select next port or finish`}
                    </span>
                  </div>
                </div>
              )}

              <MapView
                ports={ports}
                connections={connections}
                route={route}
                availableConnections={availableConnections}
                selectedRoutes={selectedRoutes}
                onStart={id => dispatch({ type: "START", portId: id })}
                onAddSegment={conn => dispatch({ type: "ADD_SEGMENT", connection: conn })}
                onUndo={() => dispatch({ type: "UNDO" })}
                onFinish={handleFinishRoute}
                onSetDays={(index, days) =>
                  dispatch({ type: "SET_PORT_DAYS", index, days })
                }
                exploreMode={exploreMode}
                onToggleExploreMode={() => setExploreMode(v => !v)}
                forceMapControls={forceMapControls}
                onMapControlsRef={el => { if (el) tourRefs.current["tour-map-controls"] = el; }}
              />

              {/* ── Map legend ── */}
              <div style={{
                position: "absolute", bottom: 28, left: 12, zIndex: 10,
                background: "rgba(255,255,255,0.93)", borderRadius: 8,
                padding: "8px 12px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                border: "1px solid rgba(0,0,0,0.08)",
                fontFamily: "var(--font-mono)", fontSize: 10,
                display: "flex", flexDirection: "column", gap: 5,
                pointerEvents: "none",
              }}>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 2, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 9 }}>Route coverage</div>
                {[
                  { color: "#06b6d4", label: "Intra EU",     sub: "100% in scope" },
                  { color: "#f59e0b", label: "Extra EU",      sub: "50% in scope",  dashed: true },
                  { color: "#94a3b8", label: "Out of Scope", sub: "0% in scope",   dashed: true },
                ].map(({ color, label, sub, dashed }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <svg width="28" height="10" style={{ flexShrink: 0 }}>
                      <line x1="0" y1="5" x2="28" y2="5"
                        stroke={color} strokeWidth="2.5"
                        strokeDasharray={dashed ? "4 3" : "none"}
                      />
                      <polygon points="22,2 28,5 22,8" fill={color} />
                    </svg>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>{label}</span>
                    <span style={{ color: "#64748b", fontSize: 9 }}>{sub}</span>
                  </div>
                ))}
                <div style={{ marginTop: 3, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <svg width="28" height="10" style={{ flexShrink: 0 }}>
                      <circle cx="14" cy="5" r="4" fill="#2dd4bf" opacity="0.8" />
                    </svg>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>Port</span>
                    <span style={{ color: "#64748b", fontSize: 9 }}>call point</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── TAB 2: FUEL STRATEGY (blending timeline) ── */}
          {activeTab === "strategy" && (
            <div className="tab-pane-scroll">
              <div className="tab-pane-sticky-header">
                <div className="tab-pane-title">Fuel Strategy</div>
                <div className="tab-pane-subtitle">
                  Define base fuel and blending mix per voyage across scenario years
                </div>
              </div>
              <div className="tab-pane-inner tab-pane-wide">
                {savedRoutes.length === 0 ? (
                  <div className="tab-empty-state">
                    <div className="tab-empty-icon">🚢</div>
                    <div className="tab-empty-title">No voyages yet</div>
                    <div className="tab-empty-body">Create and save a voyage in the Voyage Editor first, then define its fuel strategy here.</div>
                  </div>
                ) : (
                  <div className="tab-card">
                    <div className="strategy-content">
                      {renderScenarioConfiguration()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 3: DATA TABLES ── */}
          {activeTab === "tables" && (
            <div className="tab-pane-scroll" ref={el => tourRefs.current["tour-tables-pane"] = el}>
              <div className="tab-pane-sticky-header">
                <div className="tab-pane-title">Data Tables</div>
                <div className="tab-pane-subtitle">
                  Fuel consumption, emissions and cost breakdown across the fleet
                </div>
              </div>
              <div className="tab-pane-inner">
                {!fleetSummary || savedRoutes.length === 0 ? (
                  <div className="tab-empty-state">
                    <div className="tab-empty-icon">📊</div>
                    <div className="tab-empty-title">No data yet</div>
                    <div className="tab-empty-body">Save at least one voyage to see results here. Tables update automatically.</div>
                  </div>
                ) : (
                  <div className="tab-card">
                    <div className="tables-content">
                      {renderScenarioTables()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 4: ANALYTICS (charts) ── */}
          {/* ── TAB 4: ANALYTICS (charts) ── */}
          {activeTab === "analytics" && (
            <div className="tab-pane-scroll">
              <div className="tab-pane-sticky-header">
                <div className="tab-pane-title">Analytics</div>
                <div className="tab-pane-subtitle">
                  CO₂ intensity, ETS compliance trajectory and cost projections
                </div>
              </div>
              <div className="tab-pane-inner tab-pane-wide">
                {!fleetSummary || savedRoutes.length === 0 ? (
                  <div className="tab-empty-state">
                    <div className="tab-empty-icon">📈</div>
                    <div className="tab-empty-title">No data yet</div>
                    <div className="tab-empty-body">Save at least one voyage to see charts here. Graphs update automatically.</div>
                  </div>
                ) : (
                  <div className="analytics-content">
                    {renderScenarioGraphsCO2()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 5: ALLOCATION ── */}
          {activeTab === "allocation" && (
            <div className="tab-pane-scroll">
              <div className="tab-pane-sticky-header">
                <div className="tab-pane-title">Fuel Allocation</div>
                <div className="tab-pane-subtitle">
                  Visualise optimal vs proportional in-scope allocation · toggle drives all penalty calculations
                </div>
              </div>
              <div className="tab-pane-inner tab-pane-wide">
                {!fleetSummary || savedRoutes.length === 0 ? (
                  <div className="tab-empty-state">
                    <div className="tab-empty-icon">⚡</div>
                    <div className="tab-empty-title">No voyages yet</div>
                    <div className="tab-empty-body">Save at least one voyage to see the allocation visualisation.</div>
                  </div>
                ) : (
                  <div className="tab-card">
                    <AllocationPanel
                      savedRoutes={savedRoutes}
                      fuelParams={fuelParams}
                      fuelConfig={fuelConfig}
                      euFactors={euFactors}
                      FUEL_COLORS={FUEL_COLORS}
                      SCENARIO_YEARS={SCENARIO_YEARS}
                      useOptimalAllocation={useOptimalAllocation}
                      setUseOptimalAllocation={setUseOptimalAllocation}
                      resolveScenarioYear={resolveScenarioYear}
                      getRouteEuCategory={getRouteEuCategory}
                      computeFuelEuPenalty={computeFuelEuPenalty}
                      computeFuelEuPenaltyOptimal={computeFuelEuPenaltyOptimal}
                      allocToggleRef={el => tourRefs.current["tour-alloc-toggle"] = el}
                      calculateBlendedFuels={calculateBlendedFuels}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>


      <RightSidebar
        fuelParams={fuelParams}
        setFuelParams={setFuelParams}
        euFactors={euFactors}
        setEuFactors={setEuFactors}
        fuelConfig={fuelConfig}
        EU_MARITIME_FACTORS={EU_MARITIME_FACTORS}
        activeFuelIds={activeFuelIds}
        allFuels={allFuels}
        onOpenFuelLibrary={() => setShowFuelLibrary(true)}
        isOpen={rightOpen}
        onToggle={() => setRightOpen(v => !v)}
        sidebarRef={el => tourRefs.current["tour-right-sidebar"] = el}
        manageRef={el => tourRefs.current["tour-manage-btn"] = el}
        tourControlRef={sidebarControlRef}
        onFirstFuelRef={el => { tourRefs.current["tour-fuel-params-group"] = el; }}
        co2InGCO2MJ={co2InGCO2MJ}
        setCo2InGCO2MJ={setCo2InGCO2MJ}
      />

      {/* ── FOOTER ── */}
      <div
        className="app-footer"
        style={{
          left: leftOpen ? "var(--sidebar-width)" : 0,
          right: rightOpen ? "var(--right-sidebar-width)" : 0,
        }}
      >
        <button
          className="footer-btn"
          onClick={exportFleet}
          disabled={!hasUnsavedChanges}
          title="Export fleet"
        >
          ⬇ Export
        </button>
        <label className="footer-btn" style={{ cursor: "pointer" }} title="Import fleet">
          ⬆ Import
          <input
            type="file"
            accept="application/json"
            style={{ display: "none" }}
            onChange={e => { if (e.target.files[0]) importFleet(e.target.files[0], e.target); }}
          />
        </label>
        <div className="footer-sep" />
        <div
          ref={el => tourRefs.current["tour-footer-cases"] = el}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <button
            className="footer-btn"
            onClick={() => onOpenStartup?.()}
            title="Load a preset or saved case"
          >
            📂 Load
          </button>
          <button
            className="footer-btn footer-btn-save"
            onClick={openSaveCaseModal}
            disabled={!hasUnsavedChanges}
            title={hasUnsavedChanges ? "Save current work as a named case" : "No changes to save"}
            style={hasUnsavedChanges ? {
              background: "rgba(45,212,191,0.18)",
              borderColor: "var(--teal-400)",
              color: "var(--teal-300)",
              boxShadow: "0 0 0 1px rgba(45,212,191,0.4), 0 0 12px rgba(45,212,191,0.35)",
              animation: "saveGlow 1.8s ease-in-out infinite",
            } : undefined}
          >
            💾 Save Case
          </button>
          <style>{`
            @keyframes saveGlow {
              0%, 100% { box-shadow: 0 0 0 1px rgba(45,212,191,0.4), 0 0 8px rgba(45,212,191,0.25); }
              50% { box-shadow: 0 0 0 1px rgba(45,212,191,0.7), 0 0 16px rgba(45,212,191,0.55); }
            }
          `}</style>
        </div>
        <div className="footer-sep" />
        <button
          className="footer-btn"
          onClick={startTour}
          title="Take the guided tour"
          style={{ opacity: 0.7 }}
        >
          ? Tour
        </button>
        <div className="footer-sep" />
        <button
          className={"footer-btn footer-btn-fullscreen" + (!leftOpen && !rightOpen ? " active" : "")}
          disabled={activeTab === "map"}
          title={activeTab === "map" ? "Full screen not available on Voyage Editor" : (!leftOpen && !rightOpen ? "Restore sidebars" : "Expand content area")}
          onClick={() => {
            if (!leftOpen && !rightOpen) {
              setLeftOpen(true);
              setRightOpen(true);
            } else {
              setLeftOpen(false);
              setRightOpen(false);
            }
          }}
        >
          {!leftOpen && !rightOpen ? "⊠ Restore" : "⛶ Full Screen"}
        </button>
        <div className="footer-sep" />
        <button
          className="footer-btn"
          onClick={() => onOpenContact?.()}
          title="Get in touch"
          style={{
            background: "var(--teal-500, #14b8a6)",
            borderColor: "var(--teal-500, #14b8a6)",
            color: "#ffffff",
            fontWeight: 600,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--teal-600, #0d9488)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "var(--teal-500, #14b8a6)"; }}
        >
          ✉ Contact Us
        </button>
      </div>


      {/* ── MODAL ── */}
      {routeToDelete && (
        <div className="modal-overlay">
          <div className="modal-box">

            <div className="modal-header">
              <div className="modal-eyebrow">Warning</div>
              <div className="modal-title">{"Delete Voyage '" + routeToDeleteName + "'"} </div>
            </div>

            <div className="modal-body">
              <span className="modal-field-label">
                This action cannot be undone.
              </span>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn-cancel"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>

              <button
                className="modal-btn-create"
                style={{ background: "var(--red-400)" }}
                onClick={handleDeleteConfirmed}
              >
                Delete
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── FUEL PARAM CONFLICT DIALOG ── */}
      {fuelParamConflicts && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: 560, maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-eyebrow">Import Warning</div>
                <div className="modal-title">Fuel Parameter Conflicts</div>
              </div>
            </div>
            <div style={{ padding: "16px 24px" }}>
              <p style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-300)", marginBottom: 16, lineHeight: 1.6 }}>
                The imported case has <strong style={{ color: "#f97316" }}>{fuelParamConflicts.conflicts.length} fuel value{fuelParamConflicts.conflicts.length > 1 ? "s" : ""}</strong> that differ from the current built-in defaults in <code>data.js</code>. This may happen if defaults were updated since the case was saved.
              </p>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: 11, marginBottom: 20 }}>
                <thead>
                  <tr style={{ color: "var(--teal-300)", borderBottom: "1px solid rgba(45,212,191,0.2)" }}>
                    <th align="left" style={{ padding: "0 0 8px" }}>Fuel</th>
                    <th align="left" style={{ padding: "0 0 8px" }}>Field</th>
                    <th align="right" style={{ padding: "0 0 8px" }}>Imported value</th>
                    <th align="right" style={{ padding: "0 0 8px" }}>Current default</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelParamConflicts.conflicts.map((c, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(148,163,184,0.08)" }}>
                      <td style={{ padding: "6px 0", color: "var(--slate-200)" }}>{c.fuelId}</td>
                      <td style={{ padding: "6px 0", color: "var(--slate-400)" }}>{c.field}</td>
                      <td align="right" style={{ padding: "6px 0", color: "#f97316" }}>{c.importedValue}</td>
                      <td align="right" style={{ padding: "6px 0", color: "#22c55e" }}>{c.defaultValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-400)", marginBottom: 20 }}>
                Which values do you want to use?
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    // Keep current defaults — ignore imported fuelParams for built-in fuels
                    const customOnly = Object.fromEntries(
                      Object.entries(fuelParamConflicts.importedParams)
                        .filter(([id]) => !(id in DEFAULT_FUEL_PARAMS))
                    );
                    setFuelParams(prev => mergeFuelParams({ ...DEFAULT_FUEL_PARAMS, ...prev }, customOnly));
                    setFuelParamConflicts(null);
                  }}
                >
                  ✓ Use current defaults (green)
                </button>
                <button
                  className="modal-btn-create"
                  onClick={() => {
                    // Use imported values
                    setFuelParams(prev => mergeFuelParams({ ...DEFAULT_FUEL_PARAMS, ...prev }, fuelParamConflicts.importedParams));
                    setFuelParamConflicts(null);
                  }}
                >
                  ⚠ Keep imported values (orange)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-eyebrow">New voyage</div>
            </div>

            <div className="modal-body">
              <label className="modal-field-label">Voyage Designation</label>
              <input
                className="modal-input"
                placeholder="e.g. Pacific"
                value={routeNameInput}
                onChange={e => setRouteNameInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && confirmNewRoute()}
                autoFocus
              />
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn-create"
                onClick={confirmNewRoute}
                disabled={!routeNameInput.trim()}
              >
                Confirm
              </button>
              <button
                className="modal-btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFuelLibrary && (
        <FuelLibraryModal
          allFuels={allFuels}
          activeFuelIds={activeFuelIds}
          fuelParams={fuelParams}
          onToggleFuel={handleToggleFuel}
          onAddCustomFuel={handleAddCustomFuel}
          onClose={() => setShowFuelLibrary(false)}
          fuelCardRef={el => tourRefs.current["tour-fuel-card"] = el}
          fuelDupRef={el => tourRefs.current["tour-fuel-dup"] = el}
          co2InGCO2MJ={co2InGCO2MJ}
          setCo2InGCO2MJ={setCo2InGCO2MJ}
        />
      )}

      {/* ── SAVE CASE MODAL ── */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: 440 }}>
            <div className="modal-header">
              <div className="modal-eyebrow">Save Case</div>
              <div className="modal-title">Save Current Work</div>
            </div>
            <div className="modal-body">
              {/* New name */}
              <label className="modal-field-label" style={{ marginBottom: 6, display: "block" }}>Save as new case</label>
              <input
                className="modal-input"
                placeholder="e.g. North Sea Q3 scenario"
                value={saveNameInput}
                autoFocus
                onChange={e => { setSaveNameInput(e.target.value); setSaveOverwriteId(null); }}
                onKeyDown={e => e.key === "Enter" && saveNameInput.trim() && confirmSaveCase()}
              />
              {/* Overwrite existing */}
              {savedCases.length > 0 && (
                <>
                  <div className="modal-field-label" style={{ margin: "16px 0 8px" }}>Or overwrite an existing case</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                    {savedCases.map(c => (
                      <div
                        key={c.id}
                        onClick={() => { setSaveOverwriteId(c.id); setSaveNameInput(""); }}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${saveOverwriteId === c.id ? "var(--teal-400)" : "rgba(148,163,184,0.2)"}`,
                          background: saveOverwriteId === c.id ? "rgba(45,212,191,0.07)" : "rgba(255,255,255,0.02)",
                          transition: "var(--transition)",
                        }}
                      >
                        <div>
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--slate-200)" }}>{c.name}</div>
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)", marginTop: 2 }}>
                            Saved {new Date(c.savedAt).toLocaleDateString()} · {c.routes?.length ?? 0} voyage{c.routes?.length !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <div style={{ color: saveOverwriteId === c.id ? "var(--teal-400)" : "var(--slate-500)", fontSize: 14 }}>
                          {saveOverwriteId === c.id ? "✓" : "○"}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn-cancel"
                onClick={() => setShowSaveModal(false)}
              >Cancel</button>
              <button
                className="modal-btn-create"
                disabled={saveOverwriteId === null && !saveNameInput.trim()}
                onClick={confirmSaveCase}
              >
                {saveOverwriteId !== null ? "Overwrite" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLEAR ALL MODAL ── */}
      {showClearModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-eyebrow">Warning</div>
              <div className="modal-title">Clear All Voyages</div>
            </div>
            <div className="modal-body">
              <span className="modal-field-label">
                This will remove all {savedRoutes.length} voyage{savedRoutes.length !== 1 ? "s" : ""}. This cannot be undone.
              </span>
            </div>
            <div className="modal-footer">
              <button className="modal-btn-cancel" onClick={() => setShowClearModal(false)}>Cancel</button>
              <button
                className="modal-btn-create"
                style={{ background: "var(--red-400)" }}
                onClick={() => {
                  setSavedRoutes([]);
                  setSelectedRouteIds(new Set());
                  setEditingId(null);
                  setFleetSummary(null);
                  dispatch({ type: "RESET" });
                  setShowClearModal(false);
                }}
              >Clear All</button>
            </div>
          </div>
        </div>
      )}

        {/* ── SNACKBAR ── */}
        {snackbar && (
          <div
            key={snackbar.id}
            onClick={() => setSnackbar(null)}
            style={{
              position: "fixed",
              bottom: 28,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 18px",
              borderRadius: 8,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              cursor: "pointer",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              whiteSpace: "nowrap",
              animation: "snackbar-in 0.2s ease",
              background:
                snackbar.type === "success" ? "#f0fdf4" :
                snackbar.type === "error"   ? "#fef2f2" :
                "#f0f9ff",
              border: `1px solid ${
                snackbar.type === "success" ? "#bbf7d0" :
                snackbar.type === "error"   ? "#fecaca" :
                "#bae6fd"
              }`,
              color:
                snackbar.type === "success" ? "#15803d" :
                snackbar.type === "error"   ? "#dc2626" :
                "#0369a1",
            }}
          >
            <span style={{ fontSize: 14 }}>
              {snackbar.type === "success" ? "✓" : snackbar.type === "error" ? "✕" : "ℹ"}
            </span>
            {snackbar.message}
            <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }}>✕</span>
          </div>
        )}

      {/* ── GUIDED TOUR OVERLAY ── */}
      {tourStep !== null && (
        <TourOverlay
          stepIndex={tourStep}
          onNext={tourNext}
          onBack={tourBack}
          onSkip={closeTour}
          tourRefs={tourRefs}
        />
      )}

    </div>

  );

  
}

/* ─── FUEL LIBRARY MODAL ──────────────────────────────── */

/* ── Helpers ── */
const MAX_TAG_LEVELS = 10;

/** Collect all tag-level keys that exist across any fuel (tag1…tag10) */
function collectTagLevels(allFuels) {
  const levels = new Set();
  allFuels.forEach(f => {
    if (!f.tags) return;
    for (let i = 1; i <= MAX_TAG_LEVELS; i++) {
      if (f.tags[`tag${i}`] !== undefined && f.tags[`tag${i}`] !== "") {
        levels.add(i);
      }
    }
  });
  return Array.from(levels).sort((a, b) => a - b);
}

/** Collect distinct values for a given tag level, optionally filtered by parent selections */
function collectTagValues(allFuels, levelIndex, selectedTags) {
  const vals = new Set();
  allFuels.forEach(f => {
    if (!f.tags) return;
    // Check that all previously selected parent tags match
    for (let i = 1; i < levelIndex; i++) {
      if (selectedTags[`tag${i}`] && f.tags[`tag${i}`] !== selectedTags[`tag${i}`]) return;
    }
    const v = f.tags[`tag${levelIndex}`];
    if (v !== undefined && v !== "") vals.add(v);
  });
  return Array.from(vals).sort();
}

/** TAG_LABELS — human-readable label for each tag level (tag1 = Category, handled by the category buttons) */
const TAG_LABELS = {
  tag2: "Fuel type",
  tag3: "Product",
  tag4: "Electrolysis",
  tag5: "Carbon source",
  tag6: "Biofeedstock",
  tag7: "Tag 7",
  tag8: "Tag 8",
  tag9: "Tag 9",
  tag10: "Tag 10",
};

/** DescriptionTooltip — uses fixed positioning so it escapes any overflow/stacking context */
function DescriptionTooltip({ text }) {
  const [coords, setCoords] = useState(null);
  const iconRef = useRef(null);
  if (!text) return null;

  function handleMouseEnter() {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setCoords({
      left: rect.left + rect.width / 2,
      bottom: window.innerHeight - rect.top + 8,
    });
  }

  return (
    <span
      style={{ position: "relative", display: "inline-block", flexShrink: 0 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setCoords(null)}
    >
      <span ref={iconRef} className="fuel-lib-tooltip-icon">ⓘ</span>
      {coords && (
        <div
          className="fuel-lib-tooltip-popup"
          style={{ left: coords.left, bottom: coords.bottom }}
        >
          {text}
          <div className="fuel-lib-tooltip-arrow" />
        </div>
      )}
    </span>
  );
}

/** TagFilterRow — cascading selectors starting from tag2 (tag1 = Category, already handled by buttons) */
function TagFilterRow({ allFuels, selectedTags, onTagChange }) {
  const allLevels = collectTagLevels(allFuels);
  const levels = allLevels.filter(l => l > 1);
  if (levels.length === 0) return null;

  const hasAnySelection = levels.some(l => selectedTags[`tag${l}`]);

  return (
    <div className="fuel-lib-tag-filters">
      {levels.map(levelIndex => {
        const values = collectTagValues(allFuels, levelIndex, selectedTags);
        if (values.length === 0) return null;
        const key = `tag${levelIndex}`;
        const current = selectedTags[key] || "";
        const isActive = !!current;

        return (
          <div key={levelIndex} className="fuel-lib-tag-filter">
            <span className={`fuel-lib-tag-label${isActive ? " active" : ""}`}>
              {TAG_LABELS[key] || `Tag ${levelIndex}`}
            </span>
            <select
              value={current}
              onChange={e => onTagChange(levelIndex, e.target.value)}
              className={`fuel-lib-tag-select${isActive ? " active" : ""}`}
            >
              <option value="">All</option>
              {values.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        );
      })}
      {hasAnySelection && (
        <button className="fuel-lib-tag-clear" onClick={() => onTagChange(null, null)}>
          ✕ Clear
        </button>
      )}
    </div>
  );
}

/** TagEditor — renders up to MAX_TAG_LEVELS tag inputs in a form */
function TagEditor({ tags, onChange }) {
  const existingLevels = [];
  for (let i = 1; i <= MAX_TAG_LEVELS; i++) {
    if (tags[`tag${i}`] !== undefined && tags[`tag${i}`] !== "") existingLevels.push(i);
  }
  const maxShow = Math.min(MAX_TAG_LEVELS, (existingLevels.length > 0 ? Math.max(...existingLevels) : 0) + 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {Array.from({ length: maxShow }, (_, idx) => {
        const i = idx + 1;
        const val = tags[`tag${i}`] || "";
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="fuel-lib-tag-label" style={{ minWidth: 44 }}>
              tag {i}
            </span>
            <input
              className="fuel-custom-input"
              style={{ flex: 1 }}
              placeholder={`Level ${i} tag…`}
              value={val}
              onChange={e => onChange(i, e.target.value)}
            />
          </div>
        );
      })}
      {maxShow < MAX_TAG_LEVELS && (
        <button
          onClick={() => onChange(maxShow + 1, "")}
          className="fuel-lib-custom-toggle"
          style={{ width: "fit-content", marginBottom: 0, marginTop: 2 }}
        >
          + add tag level
        </button>
      )}
    </div>
  );
}

const EMPTY_CUSTOM_FORM = {
  name: "", category: "Custom",
  cost: "", costLow: "", costHigh: "",
  co2: "", hfoEquivalentFactor: "",
  lhv: "", density: "",
  description: "",
  tags: {},
};

function FuelLibraryModal({
  allFuels,
  activeFuelIds,
  fuelParams,
  onToggleFuel,
  onAddCustomFuel,
  onClose,
  fuelCardRef,
  fuelDupRef,
  co2InGCO2MJ,
  setCo2InGCO2MJ,
}) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [selectedTags, setSelectedTags] = useState({});
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState(EMPTY_CUSTOM_FORM);
  const [formError, setFormError] = useState("");

  const categories = ["All", ...Array.from(new Set(allFuels.map(f => f.category)))];

  const CATEGORY_COLORS = {
    "E-fuel":    "#06b6d4",
    "Biofuel":   "#22c55e",
    "Blue fuel": "#9333ea",
    Conventional: "#f97316", Gas: "#06b6d4", Alcohol: "#8b5cf6",
    Hydrogen: "#e2e8f0", Ammonia: "#a855f7",
    Synthetic: "#f0abfc", Custom: "#fbbf24",
  };

  function handleTagFilterChange(levelIndex, value) {
    if (levelIndex === null) {
      setSelectedTags({});
      return;
    }
    setSelectedTags(prev => {
      const next = { ...prev, [`tag${levelIndex}`]: value };
      // Clear child levels when parent changes
      for (let i = levelIndex + 1; i <= MAX_TAG_LEVELS; i++) {
        delete next[`tag${i}`];
      }
      return next;
    });
  }

  const filtered = allFuels.filter(f => {
    const matchCat = categoryFilter === "All" || f.category === categoryFilter;
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.id.toLowerCase().includes(search.toLowerCase()) ||
      f.category.toLowerCase().includes(search.toLowerCase());
    // Tag filter: each selected tag level must match
    const matchTags = Object.entries(selectedTags).every(([key, val]) => {
      if (!val) return true;
      return f.tags && f.tags[key] === val;
    });
    return matchCat && matchSearch && matchTags;
  });

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  function handleDuplicate(e, fuel) {
    e.stopPropagation();
    // Always duplicate from original library values, not sidebar overrides
    const co2tPerT = fuel.co2.value;
    const lhvMJperT = fuel.lhv.value * 1000;
    const co2Display = co2InGCO2MJ && lhvMJperT > 0
      ? String(parseFloat((co2tPerT * 1e6 / lhvMJperT).toFixed(4)))
      : String(co2tPerT);
    setCustomForm({
      name: "",
      category: fuel.category || "Custom",
      cost: String(fuel.cost.value),
      costLow: fuel.costLow !== undefined ? String(fuel.costLow) : "",
      costHigh: fuel.costHigh !== undefined ? String(fuel.costHigh) : "",
      co2: co2Display,
      hfoEquivalentFactor: String(fuel.hfoEquivalentFactor.value),
      lhv: String(fuel.lhv.value),
      density: String(fuel.density.value),
      description: fuel.description || "",
      tags: { ...(fuel.tags || {}) },
    });
    setFormError("");
    setShowDuplicateModal(true);
    setShowCustomForm(false);
  }

  function handleCustomSubmit() {
    if (!customForm.name.trim()) {
      setFormError("Name is required."); return;
    }
    const derivedId = customForm.name.trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
    if (allFuels.some(f => f.id === derivedId)) {
      setFormError("A fuel with this name already exists."); return;
    }
    const num = (v, fallback = 0) => { const n = parseFloat(v); return isNaN(n) ? fallback : n; };
    const baseCost = num(customForm.cost, 0);
    const lhvMJperT = num(customForm.lhv, 40) * 1000;
    // If toggle is in gCO₂/MJ mode, convert back to tCO₂/t for storage
    const co2Value = co2InGCO2MJ && lhvMJperT > 0
      ? num(customForm.co2, 0) * lhvMJperT / 1e6
      : num(customForm.co2, 0);

    // Build clean tags object (strip empty strings)
    const cleanTags = {};
    Object.entries(customForm.tags).forEach(([k, v]) => {
      if (v && v.trim()) cleanTags[k] = v.trim();
    });

    onAddCustomFuel({
      id: derivedId,
      name: customForm.name.trim(),
      category: "Custom",
      color: "#fbbf24",
      description: customForm.description.trim() || undefined,
      lhv: { value: num(customForm.lhv, 40), unit: "MJ/kg" },
      density: { value: num(customForm.density, 0.9), unit: "t/m3" },
      hfoEquivalentFactor: { value: num(customForm.hfoEquivalentFactor, 1), unit: "tHFOeq/tFuel" },
      cost: { value: baseCost, unit: "USD/t" },
      costLow: customForm.costLow !== "" ? num(customForm.costLow) : undefined,
      costHigh: customForm.costHigh !== "" ? num(customForm.costHigh) : undefined,
      co2: { value: co2Value, unit: "tCO2/tFuel" },
      tags: Object.keys(cleanTags).length > 0 ? cleanTags : undefined,
    });
    setCustomForm(EMPTY_CUSTOM_FORM);
    setFormError("");
    setShowCustomForm(false);
  }

  function updateTag(level, value) {
    setCustomForm(prev => ({
      ...prev,
      tags: { ...prev.tags, [`tag${level}`]: value },
    }));
  }

  return (
    <>
    <div className="modal-overlay">
      <div
        className="modal-box fuel-library-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="modal-header">
          <div>
            <div className="modal-eyebrow">Configuration</div>
            <div className="modal-title">Fuel Library</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "var(--slate-400)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "4px 8px" }}
            title="Close"
          >×</button>
        </div>

        {/* TOOLBAR */}
        <div className="fuel-lib-toolbar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              className="fuel-lib-search"
              placeholder="Search fuels…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            {/* CO₂ unit toggle — synced with sidebar */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}
              title={co2InGCO2MJ ? "Showing gCO₂/MJ — click for tCO₂/t" : "Showing tCO₂/t — click for gCO₂/MJ"}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "#64748b", opacity: co2InGCO2MJ ? 0.4 : 1 }}>tCO₂/t</span>
              <div onClick={() => setCo2InGCO2MJ(v => !v)} style={{
                width: 28, height: 14, borderRadius: 7, cursor: "pointer", flexShrink: 0,
                background: co2InGCO2MJ ? "#0d9488" : "rgba(148,163,184,0.3)",
                position: "relative", transition: "background 0.2s",
              }}>
                <div style={{
                  position: "absolute", top: 2, left: co2InGCO2MJ ? 16 : 2,
                  width: 10, height: 10, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s",
                }} />
              </div>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "#64748b", opacity: co2InGCO2MJ ? 1 : 0.4 }}>gCO₂/MJ</span>
            </div>
          </div>
          <div className="fuel-lib-cats">
            {categories.map(cat => (
              <button
                key={cat}
                className={"fuel-cat-btn" + (categoryFilter === cat ? " active" : "")}
                onClick={() => setCategoryFilter(cat)}
                style={categoryFilter === cat && cat !== "All"
                  ? { borderColor: CATEGORY_COLORS[cat], color: CATEGORY_COLORS[cat] }
                  : {}}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Tag filters — only shows if any fuel has tags */}
          <TagFilterRow
            allFuels={allFuels}
            selectedTags={selectedTags}
            onTagChange={handleTagFilterChange}
          />
        </div>

        {/* FUEL GRID */}
        <div className="fuel-lib-grid-scroll">
          <div className="fuel-lib-grid">
          {filtered.map(fuel => {
            const isActive = activeFuelIds.includes(fuel.id);
            const params = fuelParams[fuel.id];
            // Always display original library values — sidebar overrides (fuelParams) only affect calculations
            const cost = fuel.cost.value;
            const co2 = fuel.co2.value;
            const lhvMJperT = fuel.lhv.value * 1000;
            const co2Display = co2InGCO2MJ && lhvMJperT > 0
              ? `${(co2 * 1e6 / lhvMJperT).toFixed(1)} gCO₂/MJ`
              : `${co2} tCO₂/t`;
            const hasCostScenarios = fuel.costLow !== undefined || fuel.costHigh !== undefined;
            return (
              <div
                key={fuel.id}
                ref={filtered.indexOf(fuel) === 0 ? fuelCardRef : null}
                className={"fuel-lib-card" + (isActive ? " active" : "")}
                onClick={() => onToggleFuel(fuel)}
              >
                <div className="fuel-lib-card-top">
                  <div className="fuel-lib-dot" style={{ background: fuel.color || "#94a3b8" }} />
                  <div className="fuel-lib-card-name" style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {fuel.name}
                    {fuel.description && <DescriptionTooltip text={fuel.description} />}
                  </div>
                  <div className={"fuel-lib-badge" + (isActive ? " badge-active" : "")}>
                    {isActive ? "✓" : "+"}
                  </div>
                </div>
                <div className="fuel-lib-cat-tag" style={{ color: CATEGORY_COLORS[fuel.category] || "#94a3b8" }}>
                  {fuel.category} · <span style={{ opacity: 0.7 }}>{fuel.id}</span>
                </div>
                {/* Tag chips */}
                {fuel.tags && Object.keys(fuel.tags).length > 0 && (
                  <div className="fuel-lib-tag-chips">
                    {Object.entries(fuel.tags)
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <span key={k} className="fuel-lib-tag-chip">{v}</span>
                      ))}
                  </div>
                )}
                <div className="fuel-lib-stats">
                  <div className="fuel-lib-stat">
                    <span className="fuel-lib-stat-label">Cost</span>
                    <span className="fuel-lib-stat-val">
                      ${cost}{hasCostScenarios && ` [${ fuel.costLow !== undefined ? `$${fuel.costLow}` : "—"}, ${fuel.costHigh !== undefined ? `$${fuel.costHigh}` : "—"}]`}{" "}<span className="fuel-lib-stat-unit">USD/t</span>
                    </span>
                  </div>
                  <div className="fuel-lib-stat">
                    <span className="fuel-lib-stat-label">CO₂</span>
                    <span className="fuel-lib-stat-val">{co2Display}</span>
                  </div>
                  <div className="fuel-lib-stat">
                    <span className="fuel-lib-stat-label">LHV</span>
                    <span className="fuel-lib-stat-val">{fuel.lhv.value} <span className="fuel-lib-stat-unit">MJ/kg</span></span>
                  </div>
                </div>
                {/* Show override indicator when sidebar values differ from library */}
                {params && (params.cost !== fuel.cost.value || params.co2 !== fuel.co2.value || params.lhvMJperT !== fuel.lhv.value * 1000) && (
                  <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 9, color: "#f97316", opacity: 0.8 }}>
                    ⚙ sidebar overrides active — calculations use modified values
                  </div>
                )}
                {/* Duplicate button — full-width footer */}
                <button
                  ref={filtered.indexOf(fuel) === 0 ? fuelDupRef : null}
                  onClick={e => handleDuplicate(e, fuel)}
                  title="Use as template for a new custom fuel"
                  className="fuel-lib-dup-btn"
                >
                  ⎘ duplicate as custom
                </button>
              </div>
            );
          })}
          </div>

          {/* CUSTOM FUEL FORM */}
          <div className="fuel-lib-custom-section">
            <button
              className="fuel-lib-custom-toggle"
              onClick={() => { setShowCustomForm(v => !v); setFormError(""); if (showCustomForm) setCustomForm(EMPTY_CUSTOM_FORM); }}
            >
              {showCustomForm ? "− Cancel" : "+ Define Custom Fuel"}
            </button>

            {showCustomForm && (
              <div className="fuel-lib-custom-form">

                {/* ── Core fields ── */}
                <div className="fuel-custom-grid">
                  {[
                    { field: "name", label: "Fuel Name *", placeholder: "e.g. My Special Fuel" },
                    { field: "co2", label: co2InGCO2MJ ? "CO₂ factor (gCO₂/MJ)" : "CO₂ factor (tCO₂/t)", placeholder: co2InGCO2MJ ? "e.g. 91.0" : "e.g. 1.5" },
                    { field: "hfoEquivalentFactor", label: "HFO Equiv. (tHFOeq/t)", placeholder: "e.g. 1.0" },
                    { field: "lhv", label: "LHV (MJ/kg)", placeholder: "e.g. 40.4" },
                    { field: "density", label: "Density (t/m³)", placeholder: "e.g. 0.9" },
                  ].map(({ field, label, placeholder }) => (
                    <div className="fuel-custom-field" key={field}>
                      <label className="fuel-custom-label">{label}</label>
                      <input
                        className="fuel-custom-input"
                        placeholder={placeholder}
                        value={customForm[field]}
                        onChange={e => setCustomForm(prev => ({ ...prev, [field]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>

                {/* ── Cost scenarios ── */}
                <div className="param-cost-scenarios" style={{ margin: "12px 0 0", padding: 0, border: "none", borderTop: "none" }}>
                  <div className="fuel-custom-label" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                    Cost Scenarios (USD/t)
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--slate-400)", opacity: 0.6, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                      Expected cost used in calculations
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { field: "costLow", label: "Low", placeholder: "e.g. 600" },
                      { field: "cost", label: "Expected *", placeholder: "e.g. 750" },
                      { field: "costHigh", label: "High", placeholder: "e.g. 900" },
                    ].map(({ field, label, placeholder }) => (
                      <div className="fuel-custom-field" key={field}>
                        <label className="fuel-custom-label">{label}</label>
                        <input
                          className="fuel-custom-input"
                          placeholder={placeholder}
                          value={customForm[field]}
                          onChange={e => setCustomForm(prev => ({ ...prev, [field]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Description ── */}
                <div style={{ marginTop: 12 }}>
                  <label className="fuel-custom-label" style={{ display: "block", marginBottom: 6 }}>
                    Description <span style={{ opacity: 0.5, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(shown as tooltip)</span>
                  </label>
                  <textarea
                    className="fuel-custom-input"
                    style={{ width: "100%", minHeight: 52, resize: "vertical", fontFamily: "var(--font-body)", fontSize: 12 }}
                    placeholder="Brief description of this fuel, its origin, use case…"
                    value={customForm.description}
                    onChange={e => setCustomForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                {/* ── Tags ── */}
                <div style={{ marginTop: 12 }}>
                  <label className="fuel-custom-label" style={{ display: "block", marginBottom: 8 }}>
                    Tags <span style={{ opacity: 0.5, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(hierarchical, up to {MAX_TAG_LEVELS} levels)</span>
                  </label>
                  <TagEditor tags={customForm.tags} onChange={updateTag} />
                </div>

                {formError && <div className="fuel-custom-error">{formError}</div>}
                <button className="modal-btn-create" style={{ marginTop: 12 }} onClick={handleCustomSubmit}>
                  Add to Library
                </button>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)", opacity: 0.7 }}>
            {activeFuelIds.length} fuel{activeFuelIds.length !== 1 ? "s" : ""} active · Click a card to toggle · ⎘ to duplicate
          </div>
          <button className="modal-btn-create" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>

    {/* ── Duplicate-as-custom modal ── */}
    {showDuplicateModal && (
      <div className="modal-overlay">
        <div className="modal-box" style={{ width: 480, maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="modal-eyebrow">Fuel Library</div>
              <div className="modal-title">Duplicate as Custom Fuel</div>
            </div>
            <button
              onClick={() => { setShowDuplicateModal(false); setCustomForm(EMPTY_CUSTOM_FORM); setFormError(""); }}
              style={{ background: "transparent", border: "none", color: "var(--slate-400)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "4px 8px" }}
              title="Close"
            >×</button>
          </div>

          <div style={{ padding: "16px 24px 24px" }}>
            <div className="fuel-custom-grid">
              {[
                { field: "name", label: "Fuel Name *", placeholder: "e.g. My Special Fuel" },
                { field: "co2", label: co2InGCO2MJ ? "CO₂ factor (gCO₂/MJ)" : "CO₂ factor (tCO₂/t)", placeholder: co2InGCO2MJ ? "e.g. 91.0" : "e.g. 1.5" },
                { field: "hfoEquivalentFactor", label: "HFO Equiv. (tHFOeq/t)", placeholder: "e.g. 1.0" },
                { field: "lhv", label: "LHV (MJ/kg)", placeholder: "e.g. 40.4" },
                { field: "density", label: "Density (t/m³)", placeholder: "e.g. 0.9" },
              ].map(({ field, label, placeholder }) => (
                <div className="fuel-custom-field" key={field}>
                  <label className="fuel-custom-label">{label}</label>
                  <input
                    className="fuel-custom-input"
                    placeholder={placeholder}
                    value={customForm[field]}
                    onChange={e => setCustomForm(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                </div>
              ))}
            </div>

            <div className="param-cost-scenarios" style={{ margin: "12px 0 0", padding: 0, border: "none" }}>
              <div className="fuel-custom-label" style={{ marginBottom: 6 }}>Cost Scenarios (USD/t)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { field: "costLow", label: "Low", placeholder: "e.g. 600" },
                  { field: "cost",    label: "Expected *", placeholder: "e.g. 750" },
                  { field: "costHigh",label: "High", placeholder: "e.g. 900" },
                ].map(({ field, label, placeholder }) => (
                  <div className="fuel-custom-field" key={field}>
                    <label className="fuel-custom-label">{label}</label>
                    <input
                      className="fuel-custom-input"
                      placeholder={placeholder}
                      value={customForm[field]}
                      onChange={e => setCustomForm(prev => ({ ...prev, [field]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="fuel-custom-label" style={{ display: "block", marginBottom: 6 }}>Description</label>
              <textarea
                className="fuel-custom-input"
                style={{ width: "100%", minHeight: 52, resize: "vertical", fontFamily: "var(--font-body)", fontSize: 12 }}
                placeholder="Brief description…"
                value={customForm.description}
                onChange={e => setCustomForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="fuel-custom-label" style={{ display: "block", marginBottom: 8 }}>Tags</label>
              <TagEditor tags={customForm.tags} onChange={updateTag} />
            </div>

            {formError && <div className="fuel-custom-error">{formError}</div>}

            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button
                className="btn btn-ghost"
                onClick={() => { setShowDuplicateModal(false); setCustomForm(EMPTY_CUSTOM_FORM); setFormError(""); }}
              >Cancel</button>
              <button
                className="modal-btn-create"
                onClick={() => {
                  if (!customForm.name.trim()) { setFormError("Name is required."); return; }
                  const num = (v, fb = 0) => { const n = parseFloat(v); return isNaN(n) ? fb : n; };
                  const lhvMJperT = num(customForm.lhv, 40) * 1000;
                  const co2Value = co2InGCO2MJ && lhvMJperT > 0
                    ? num(customForm.co2, 0) * lhvMJperT / 1e6
                    : num(customForm.co2, 0);
                  const derivedId = customForm.name.trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
                  if (allFuels.some(f => f.id === derivedId)) { setFormError("A fuel with this name already exists."); return; }
                  const cleanTags = {};
                  Object.entries(customForm.tags).forEach(([k, v]) => { if (v && v.trim()) cleanTags[k] = v.trim(); });
                  onAddCustomFuel({
                    id: derivedId,
                    name: customForm.name.trim(),
                    category: customForm.category || "Custom",
                    color: "#fbbf24",
                    description: customForm.description.trim() || undefined,
                    lhv: { value: num(customForm.lhv, 40), unit: "MJ/kg" },
                    density: { value: num(customForm.density, 0.9), unit: "t/m3" },
                    hfoEquivalentFactor: { value: num(customForm.hfoEquivalentFactor, 1), unit: "tHFOeq/tFuel" },
                    cost: { value: num(customForm.cost, 0), unit: "USD/t" },
                    costLow: customForm.costLow !== "" ? num(customForm.costLow) : undefined,
                    costHigh: customForm.costHigh !== "" ? num(customForm.costHigh) : undefined,
                    co2: { value: co2Value, unit: "tCO2/tFuel" },
                    tags: Object.keys(cleanTags).length > 0 ? cleanTags : undefined,
                  });
                  setShowDuplicateModal(false);
                  setCustomForm(EMPTY_CUSTOM_FORM);
                  setFormError("");
                }}
              >Add to Library</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

/* ─── AllocationPanel ────────────────────────────────────────── */
function AllocationPanel({
  savedRoutes, fuelParams, fuelConfig, euFactors, FUEL_COLORS,
  SCENARIO_YEARS, useOptimalAllocation, setUseOptimalAllocation,
  resolveScenarioYear, getRouteEuCategory,
  computeFuelEuPenalty, computeFuelEuPenaltyOptimal, calculateBlendedFuels,
  allocToggleRef,
}) {
  const [selectedYear, setSelectedYear] = useState(SCENARIO_YEARS[0]);
  const [hoveredFuelKey, setHoveredFuelKey] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const strip1Ref = useRef(null);
  const strip2Ref = useRef(null);

  // Close tooltip whenever mouse is not over either strip bar
  useEffect(() => {
    function handleMouseMove(e) {
      const over1 = strip1Ref.current?.contains(e.target);
      const over2 = strip2Ref.current?.contains(e.target);
      if (!over1 && !over2) {
        setHoveredFuelKey(null);
        setTooltip(null);
      }
    }
    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);


  // ── All GHG numbers come from the same pipeline used by tables & graphs ──
  // calculateBlendedFuels → computeFuelEuPenalty / computeFuelEuPenaltyOptimal
  // No separate reimplementation here — one source of truth.

  const target  = euFactors.yearlyTargets[selectedYear] ?? 89.34;
  const blended = calculateBlendedFuels(selectedYear);

  // Strip RFNBO flag for the "physical only" penalty (main card value)
  const blendedFuelsNoRfnbo = Object.fromEntries(
    Object.entries(blended.fuels).map(([k, v]) => [k, { ...v, isRfnbo: false }])
  );
  const propPenalty = computeFuelEuPenalty(blendedFuelsNoRfnbo, selectedYear);
  const optPenalty  = computeFuelEuPenaltyOptimal(blendedFuelsNoRfnbo, selectedYear);

  // GHG intensity values — directly from the shared penalty functions
  const propGHG = propPenalty.actualIntensity ?? 0;
  const optGHG  = optPenalty.actualIntensity  ?? 0;

  // With RFNBO: use blended.fuels as-is (isRfnbo flags intact)
  const hasAnyRfnboAlloc = Object.values(blended.fuels).some(v => v.isRfnbo);
  const rfnboPropPenalty = hasAnyRfnboAlloc ? computeFuelEuPenalty(blended.fuels, selectedYear) : null;
  const rfnboOptPenalty  = hasAnyRfnboAlloc ? computeFuelEuPenaltyOptimal(blended.fuels, selectedYear) : null;
  const rfnboPropGHG = rfnboPropPenalty?.actualIntensity ?? null;
  const rfnboOptGHG  = rfnboOptPenalty?.actualIntensity  ?? null;

  // ── Strip bar chunks — built from calculateBlendedFuels output ──────────
  // MJ basis: hfoEquivalentTons × vlsfoLhv  (same as computeFuelEuPenalty)
  // This ensures strip widths and tooltip GHG intensities are consistent
  // with the penalty calculation.
  const vlsfoLhv = fuelParams["VLSFO"]?.lhvMJperT ?? (fuelConfig.VLSFO.lhv.value * 1000);

  // Total fleet physical MJ (all regions, unweighted) — for raw strip widths
  const totalFleetMJ = (() => {
    let sum = 0;
    Object.values(blended.fuels).forEach(v => {
      sum += (v.inter.hfoEquivalentTons + v.mixed.hfoEquivalentTons + v.out.hfoEquivalentTons) * vlsfoLhv;
    });
    return sum || 1;
  })();

  // In-scope budget MJ (coverage-weighted) — for allocation strip sizing
  const inScopeBudgetMJ = (() => {
    let sum = 0;
    Object.values(blended.fuels).forEach(v => {
      sum +=
        v.inter.hfoEquivalentTons * vlsfoLhv * (euFactors.inter ?? 1) +
        v.mixed.hfoEquivalentTons * vlsfoLhv * (euFactors.mixed ?? 0.5) +
        v.out.hfoEquivalentTons   * vlsfoLhv * (euFactors.out   ?? 0);
    });
    return sum || 1;
  })();

  // Raw strip: one chunk per fuel, using total physical MJ across all regions
  const rawChunks = Object.entries(blended.fuels).map(([fuel, v]) => {
    const mj  = (v.inter.hfoEquivalentTons + v.mixed.hfoEquivalentTons + v.out.hfoEquivalentTons) * vlsfoLhv;
    const co2 = v.inter.co2 + v.mixed.co2 + v.out.co2;
    const ghgIntensity = mj > 0 ? (co2 / mj) * 1_000_000 : 0;
    return { voyage: "fleet", fuel, euCat: "all", scopeFactor: 1, mj, co2, ghgIntensity };
  }).filter(c => c.mj > 0);

  // Proportional in-scope chunks: each fuel's eligible MJ = hfoEqTons × vlsfoLhv × scopeFactor
  const propChunks = Object.entries(blended.fuels).map(([fuel, v]) => {
    const mj =
      v.inter.hfoEquivalentTons * vlsfoLhv * (euFactors.inter ?? 1) +
      v.mixed.hfoEquivalentTons * vlsfoLhv * (euFactors.mixed ?? 0.5) +
      v.out.hfoEquivalentTons   * vlsfoLhv * (euFactors.out   ?? 0);
    const co2 =
      v.inter.co2 * (euFactors.inter ?? 1) +
      v.mixed.co2 * (euFactors.mixed ?? 0.5) +
      v.out.co2   * (euFactors.out   ?? 0);
    const ghgIntensity = mj > 0 ? (co2 / mj) * 1_000_000 : 0;
    return { voyage: "fleet", fuel, euCat: "mixed", scopeFactor: euFactors.mixed ?? 0.5, mj, co2, ghgIntensity };
  }).filter(c => c.mj > 0);

  // Optimal in-scope chunks: intra-EU fixed + mixed filled greedily cleanest-first
  // Mirrors computeFuelEuPenaltyOptimal exactly.
  const optChunks = (() => {
    const fixed = [];
    const mixedSegs = [];
    let mixedBudgetMJ = 0;

    Object.entries(blended.fuels).forEach(([fuel, v]) => {
      // Intra-EU: always 100% in scope
      const interMJ  = v.inter.hfoEquivalentTons * vlsfoLhv * (euFactors.inter ?? 1);
      const interCO2 = v.inter.co2 * (euFactors.inter ?? 1);
      if (interMJ > 0) {
        const ghgIntensity = (interCO2 / interMJ) * 1_000_000;
        fixed.push({ voyage: "fleet", fuel, euCat: "inter", scopeFactor: 1, mj: interMJ, co2: interCO2, ghgIntensity });
      }

      // Mixed: allocatable — full physical MJ available, budget = physical × 0.5
      const physicalMixedMJ = v.mixed.hfoEquivalentTons * vlsfoLhv;
      const mixedCO2        = v.mixed.co2;
      if (physicalMixedMJ > 0) {
        const co2PerMJ = mixedCO2 / physicalMixedMJ;
        mixedSegs.push({ fuel, mjAvailable: physicalMixedMJ, co2PerMJ });
        mixedBudgetMJ += physicalMixedMJ * (euFactors.mixed ?? 0.5);
      }
    });

    mixedSegs.sort((a, b) => a.co2PerMJ - b.co2PerMJ);
    let remaining = mixedBudgetMJ;
    const allocatedMixed = mixedSegs.map(seg => {
      if (remaining <= 0) return null;
      const take = Math.min(seg.mjAvailable, remaining);
      remaining -= take;
      const co2 = take * seg.co2PerMJ;
      const ghgIntensity = take > 0 ? (co2 / take) * 1_000_000 : 0;
      return { voyage: "fleet", fuel: seg.fuel, euCat: "mixed", scopeFactor: euFactors.mixed ?? 0.5, mj: take, co2, ghgIntensity };
    }).filter(Boolean).filter(c => c.mj > 0.1);

    return [...fixed, ...allocatedMixed];
  })();

  // voyageSegs kept for voyage tick labels in StripBar (name + totalMJ only)
  const voyageSegs = (() => {
    return savedRoutes.map(route => {
      if (!route.segments || !route.vessel) return null;
      const vesselFuel = route.vessel?.fuel?.type || "HFO";
      const vesselLhvR = fuelParams[vesselFuel]?.lhvMJperT ?? ((fuelConfig[vesselFuel]?.lhv?.value ?? 40.4) * 1000);
      const speed      = Number(route.vessel?.speed?.value || 0);
      const consumption = Number(route.vessel?.fuel?.consumption || 0);
      const trips      = Number(route.operations?.tripsPerYear || 1);
      if (speed <= 0 || consumption <= 0) return null;
      let totalMJ = 0;
      route.segments.forEach(seg => {
        const dist = Number(seg.distance || 0) * trips;
        totalMJ += (dist / speed / 24) * consumption * vesselLhvR;
      });
      const euCat = getRouteEuCategory(route.segments);
      return { name: route.name, euCat, scopeFactor: euFactors[euCat] ?? 0, totalMJ, fuels: [] };
    }).filter(Boolean);
  })();

  // All unique fuels for legend — from blended.fuels (only fuels with actual consumption)
  const allFuels = Object.keys(blended.fuels).filter(f => (blended.fuels[f].total?.fuelTons ?? 0) > 0);

  // Hover: which prop chunk corresponds to hovered raw fuel key?
  function keyOf(voyage, fuel) { return `${voyage}::${fuel}`; }

  function StripBar({ chunks, totalMJ, isAllocation, label, sublabel, containerRef }) {
    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "1px", textTransform: "uppercase", color: "var(--slate-400)" }}>{label}</span>
          {sublabel && <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--slate-500)" }}>{sublabel}</span>}
        </div>
        <div
          ref={containerRef}
          style={{ position: "relative", height: 48, width: "100%", borderRadius: 6, overflow: "hidden", border: "0.5px solid rgba(148,163,184,0.2)", display: "flex" }}
        >
          {chunks.map((c, i) => {
            const w = (c.mj / totalMJ * 100).toFixed(4);
            const k = keyOf(c.voyage, c.fuel);
            const isHovered = hoveredFuelKey === k;
            const dimmed = hoveredFuelKey && !isHovered;
            return (
              <div
                key={i}
                style={{
                  width: w + "%", minWidth: 1, height: "100%", flexShrink: 0,
                  background: FUEL_COLORS[c.fuel] || "#888",
                  opacity: dimmed ? 0.3 : 1,
                  outline: isHovered ? "2px solid rgba(255,255,255,0.8)" : "none",
                  outlineOffset: -2,
                  transition: "opacity .12s",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden",
                }}
                onMouseEnter={e => {
                  setHoveredFuelKey(k);
                  const pct = (c.mj / inScopeBudgetMJ * 100).toFixed(1);
                  const raw = (c.mj / totalFleetMJ * 100).toFixed(1);
                  setTooltip({ x: e.clientX, y: e.clientY, html:
                    `<b style="color:#5DCAA5">${c.voyage}</b><br>` +
                    `Fuel: <b>${c.fuel}</b><br>` +
                    `GHG intensity: <b>${c.ghgIntensity.toFixed(1)} gCO₂/MJ</b><br>` +
                    `Energy: <b>${(c.mj / 1000).toFixed(1)} GJ</b> (${isAllocation ? pct + "% of scope" : raw + "% of fleet"})<br>` +
                    `EU scope: <b>${c.euCat === "inter" ? "Intra EU" : c.euCat === "mixed" ? "Extra EU" : "out of scope"}</b>`
                  });
                }}
                onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
              >
                {parseFloat(w) > 3 && (
                  <span style={{ fontSize: 9, color: "rgba(255,255,255,.8)", pointerEvents: "none", letterSpacing: ".3px", overflow: "hidden", whiteSpace: "nowrap", padding: "0 3px" }}>
                    {c.fuel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* voyage tick marks */}
        <div style={{ position: "relative", height: 16, marginTop: 3 }}>
          {voyageSegs.reduce((acc, v) => {
            const prev = acc.cumPct;
            const w = v.totalMJ / totalFleetMJ * 100;
            acc.items.push(
              <div key={v.name} style={{ position: "absolute", left: (prev + w / 2) + "%", transform: "translateX(-50%)", fontFamily: "var(--font-mono)", fontSize: 8, color: "var(--slate-500)", whiteSpace: "nowrap" }}>
                {v.name.split("→")[0].trim().slice(0, 6)}
              </div>
            );
            acc.cumPct += w;
            return acc;
          }, { cumPct: 0, items: [] }).items}
        </div>
      </div>
    );
  }

  // Connector SVG: draw bezier arcs for hovered key between two strips

  const RAW_Y = 48;
  // eslint-disable-next-line
  const ALLOC_Y = 48;
  const GAP = 88; // px heights

  function ConnectorLayer() {
    if (!hoveredFuelKey) return null;
    // eslint-disable-next-line
    const [hVoyage, hFuel] = hoveredFuelKey.split("::");

    // find x-centres of matching segment in raw strip and alloc strip
    function findCentre(chunks, totalMJ) {
      let cum = 0;
      for (const c of chunks) {
        const w = c.mj / totalMJ;
        if (keyOf(c.voyage, c.fuel) === hoveredFuelKey) {
          return (cum + w / 2) * 100;
        }
        cum += w;
      }
      return null;
    }

    const xRaw   = findCentre(rawChunks, totalFleetMJ);
    const xAlloc = findCentre(useOptimalAllocation ? optChunks : propChunks, totalFleetMJ);
    if (xRaw === null || xAlloc === null) return null;
    const color = FUEL_COLORS[hFuel] || "#888";

    return (
      <svg style={{ position: "absolute", top: RAW_Y + 18, left: 0, width: "100%", height: GAP - 18, pointerEvents: "none", overflow: "visible" }}>
        <defs>
          <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={color} />
          </marker>
        </defs>
        <path
          d={`M ${xRaw}% 0 C ${xRaw}% ${(GAP - 18) * 0.6}px, ${xAlloc}% ${(GAP - 18) * 0.4}px, ${xAlloc}% ${GAP - 18}px`}
          fill="none" stroke={color} strokeWidth="1.5" strokeDasharray="4 3"
          opacity="0.75" markerEnd="url(#arr)"
        />
      </svg>
    );
  }

  const fmtEUR = v => v > 0 ? "€" + Math.round(v).toLocaleString() : "—";
  const fmtGHG = v => v.toFixed(1) + " gCO₂/MJ";

  return (
    <div style={{ paddingBottom: 16 }}>

      {/* ── TOGGLE + YEAR CONTROLS ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
        {/* Allocation toggle — this is the master switch */}
        <div ref={allocToggleRef} style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)",
          borderRadius: 8, padding: "10px 16px",
        }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, color: "var(--slate-200)", letterSpacing: ".3px" }}>
              Optimal allocation
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)", marginTop: 2 }}>
              Applies to all penalty calculations across the tool
            </div>
          </div>
          <div
            onClick={() => setUseOptimalAllocation(v => !v)}
            style={{
              width: 40, height: 22, borderRadius: 11, cursor: "pointer",
              background: useOptimalAllocation ? "#1D9E75" : "rgba(148,163,184,0.3)",
              border: `1px solid ${useOptimalAllocation ? "#0F6E56" : "rgba(148,163,184,0.4)"}`,
              position: "relative", transition: "background .2s, border .2s",
              flexShrink: 0,
            }}
          >
            <div style={{
              position: "absolute", top: 3, left: useOptimalAllocation ? 21 : 3,
              width: 14, height: 14, borderRadius: "50%",
              background: "#fff", transition: "left .2s",
            }} />
          </div>
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "1px", textTransform: "uppercase",
            padding: "3px 8px", borderRadius: 4,
            background: useOptimalAllocation ? "rgba(29,158,117,0.12)" : "rgba(148,163,184,0.1)",
            color: useOptimalAllocation ? "#5DCAA5" : "#94a3b8",
            border: `1px solid ${useOptimalAllocation ? "rgba(29,158,117,0.4)" : "rgba(148,163,184,0.25)"}`,
          }}>
            {useOptimalAllocation ? "ON" : "OFF"}
          </span>
        </div>

        {/* Year pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SCENARIO_YEARS.map(y => (
            <button
              key={y}
              onClick={() => setSelectedYear(y)}
              style={{
                fontFamily: "var(--font-mono)", fontSize: 11, padding: "5px 12px", borderRadius: 20, cursor: "pointer",
                border: `1px solid ${selectedYear === y ? "#1D9E75" : "rgba(148,163,184,0.25)"}`,
                background: selectedYear === y ? "rgba(29,158,117,0.12)" : "transparent",
                color: selectedYear === y ? "#5DCAA5" : "var(--slate-400)",
              }}
            >{y}</button>
          ))}
        </div>
      </div>

      {/* ── METRIC CARDS ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          {
            label: "In-scope budget",
            value: (inScopeBudgetMJ / 1000).toFixed(0) + " GJ",
            color: "#60a5fa",
          },
          {
            label: `Target ${selectedYear}`,
            value: fmtGHG(target),
            color: "#60a5fa",
          },
          {
            label: "Proportional GHG",
            value: fmtGHG(propGHG),
            color: propGHG <= target ? "#4ade80" : "#f87171",
            sub: hasAnyRfnboAlloc && rfnboPropGHG !== null
              ? `⚡ with RFNBO: ${rfnboPropGHG.toFixed(1)} gCO₂/MJ`
              : null,
            subColor: rfnboPropGHG !== null && rfnboPropGHG <= target ? "#4ade80" : "#2dd4bf",
          },
          {
            label: "Optimal GHG",
            value: fmtGHG(optGHG),
            color: optGHG <= target ? "#4ade80" : "#f87171",
            sub: hasAnyRfnboAlloc && rfnboOptGHG !== null
              ? `⚡ with RFNBO: ${rfnboOptGHG.toFixed(1)} gCO₂/MJ`
              : null,
            subColor: rfnboOptGHG !== null && rfnboOptGHG <= target ? "#4ade80" : "#2dd4bf",
          },
          {
            label: "Proportional penalty",
            value: fmtEUR(propPenalty.penaltyEur),
            color: propPenalty.isCompliant ? "#4ade80" : "#f87171",
            sub: hasAnyRfnboAlloc && rfnboPropPenalty !== null
              ? `⚡ with RFNBO: ${fmtEUR(rfnboPropPenalty.penaltyEur)}`
              : null,
            subColor: rfnboPropPenalty?.isCompliant ? "#4ade80" : "#2dd4bf",
          },
          {
            label: "Optimal penalty",
            value: fmtEUR(optPenalty.penaltyEur),
            color: optPenalty.isCompliant ? "#4ade80" : "#f87171",
            sub: hasAnyRfnboAlloc && rfnboOptPenalty !== null
              ? `⚡ with RFNBO: ${fmtEUR(rfnboOptPenalty.penaltyEur)}`
              : null,
            subColor: rfnboOptPenalty?.isCompliant ? "#4ade80" : "#2dd4bf",
          },
        ].map(m => (
          <div key={m.label} style={{ flex: "1 1 130px", background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--slate-400)", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 600, color: m.color }}>{m.value}</div>
            {m.sub && <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: m.subColor ?? "#2dd4bf", marginTop: 4 }}>{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── STRIPS + CONNECTORS ── */}
      <div style={{ position: "relative" }}>

        {/* Raw energy strip */}
        <StripBar
          chunks={rawChunks}
          totalMJ={totalFleetMJ}
          isAllocation={false}
          label="All voyage energy — raw consumption"
          sublabel="100% of fuel burned across all voyages"
          containerRef={strip1Ref}
        />

        {/* Separator + connector layer */}
        <div style={{ position: "relative", height: 32, display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ flex: 1, height: "0.5px", background: "var(--color-border-tertiary)" }} />
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase",
            color: "var(--slate-400)", padding: "2px 10px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 20, whiteSpace: "nowrap",
          }}>
            {useOptimalAllocation ? "⚡ optimal — cleanest fuel into scope first ↓" : "⇌ proportional — same blend ratio into scope ↓"}
          </span>
          <div style={{ flex: 1, height: "0.5px", background: "var(--color-border-tertiary)" }} />
          <ConnectorLayer />
        </div>

        {/* Allocation strip */}
        <StripBar
          chunks={useOptimalAllocation ? optChunks : propChunks}
          totalMJ={totalFleetMJ}
          isAllocation={true}
          label="In-scope energy — FuelEU compliance window"
          sublabel={`${(inScopeBudgetMJ / totalFleetMJ * 100).toFixed(0)}% of fleet energy · hover a segment to trace it`}
          containerRef={strip2Ref}
        />
      </div>

      {/* ── LEGEND ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
        {allFuels.map(f => (
          <div key={f} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-300)" }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: FUEL_COLORS[f] || "#888", flexShrink: 0 }} />
            {f}
            <span style={{ color: "var(--slate-500)", fontSize: 10 }}>
              {((() => {
                const chunk = rawChunks.find(c => c.fuel === f);
                return chunk ? chunk.ghgIntensity.toFixed(0) : "?";
              })())} gCO₂/MJ
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-500)", lineHeight: 1.7 }}>
        Hover any segment to highlight its counterpart in the other strip and trace the connector. GHG intensity shown is WtW (well-to-wake) per your fuel parameters. Toggle above applies globally — all tables, charts and penalty calculations reflect the selected mode.
      </div>

      {/* Regulation note */}
      <div style={{
        marginTop: 16, padding: "10px 14px", borderRadius: 6,
        background: "rgba(96,165,250,0.06)", border: "0.5px solid rgba(96,165,250,0.2)",
        fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)", lineHeight: 1.8,
      }}>
        <span style={{ color: "#60a5fa", fontWeight: 600, letterSpacing: ".3px" }}>Regulation note — when does allocation apply?</span>
        <br />
        <span style={{ color: "var(--slate-300)" }}>Intra-EU voyages (both ports EU)</span>
        {" — 100% of energy is always in scope. No allocation choice: the full fuel mix enters the compliance calculation as-is."}
        <br />
        <span style={{ color: "#fcd34d" }}>Mixed voyages (one EU port)</span>
        {" — only 50% of total energy is counted. The regulation lets you choose "}
        <span style={{ color: "#5DCAA5" }}>which 50%</span>
        {" enters scope. Optimal allocation front-loads your cleanest fuels into that window, lowering the attained GHG intensity."}
        <br />
        <span style={{ color: "#94a3b8" }}>Out-of-scope voyages (no EU port)</span>
        {" — 0% in scope. These voyages don't contribute to the FuelEU calculation regardless of fuel used."}
        <br />
        <span style={{ color: "var(--slate-500)", fontSize: 9 }}>
          Source: Reg. EU 2023/1805 Art. 2 & 4 · ESSF SAPS WS1 calculation methodologies (May 2025) · EC FAQ Q A.1
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: "fixed", left: tooltip.x + 14, top: tooltip.y - 10,
          background: "#0f172a", color: "#e2e8f0", fontFamily: "var(--font-mono)", fontSize: 11,
          padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(45,212,191,.3)",
          pointerEvents: "none", zIndex: 9999, lineHeight: 1.7, whiteSpace: "nowrap",
        }} dangerouslySetInnerHTML={{ __html: tooltip.html }} />
      )}
    </div>
  );
}

/* ─── Root: owns the landing↔tool switch ─────────────────────── */
function StartupModal({ onClose, onLoadPayload }) {
  const [tab, setTab] = useState("scratch"); // "scratch" | "preset" | "saved"
  const LS_KEY = "fuelTool_savedCases";
  const [savedCases] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); }
    catch { return []; }
  });

  const tabStyle = (active) => ({
    flex: 1,
    padding: "10px 0",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "1px",
    textTransform: "uppercase",
    cursor: "pointer",
    border: "none",
    borderBottom: active ? "2px solid var(--teal-400)" : "2px solid transparent",
    background: "transparent",
    color: active ? "var(--teal-300)" : "var(--slate-400)",
    transition: "var(--transition)",
  });

  const cardStyle = (selected) => ({
    padding: "14px 16px",
    borderRadius: 8,
    border: `1px solid ${selected ? "var(--teal-400)" : "rgba(148,163,184,0.18)"}`,
    background: selected ? "rgba(45,212,191,0.06)" : "rgba(255,255,255,0.02)",
    cursor: "pointer",
    transition: "var(--transition)",
    marginBottom: 8,
  });

  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedCase, setSelectedCase]   = useState(null);

  function handleConfirm() {
    if (tab === "scratch") { onClose(); return; }
    if (tab === "preset" && selectedPreset) { onLoadPayload(selectedPreset); onClose(); return; }
    if (tab === "saved"  && selectedCase)   { onLoadPayload(selectedCase);   onClose(); return; }
  }

  const canConfirm =
    tab === "scratch" ||
    (tab === "preset" && selectedPreset) ||
    (tab === "saved"  && selectedCase);

  return (
    <div className="modal-overlay" style={{ backdropFilter: "blur(4px)" }}>
      <div className="modal-box" style={{ width: 520, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>

        <div className="modal-header" style={{ paddingBottom: 0 }}>
          <div className="modal-eyebrow">Welcome</div>
          <div className="modal-title">How would you like to start?</div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(148,163,184,0.15)", margin: "16px 0 0" }}>
          {[
            { key: "scratch", label: "Start Fresh" },
            { key: "preset",  label: `Presets (${PRESETS.length})` },
            { key: "saved",   label: `My Cases (${savedCases.length})` },
          ].map(t => (
            <button key={t.key} style={tabStyle(tab === t.key)} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 0 4px" }}>

          {tab === "scratch" && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🗺</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--slate-200)", marginBottom: 8 }}>
                Open the tool with no routes
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-400)" }}>
                Build your fleet from scratch using the Voyage Editor.
              </div>
            </div>
          )}

          {tab === "preset" && (
            <div>
              {PRESETS.map((p, i) => (
                <div
                  key={i}
                  style={cardStyle(selectedPreset === p)}
                  onClick={() => setSelectedPreset(p)}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--slate-100)", fontWeight: 600 }}>
                      {p.name}
                    </div>
                    <div style={{ color: selectedPreset === p ? "var(--teal-400)" : "var(--slate-500)", fontSize: 14, flexShrink: 0 }}>
                      {selectedPreset === p ? "✓" : "○"}
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-400)", marginTop: 5 }}>
                    {p.description}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-500)", marginTop: 6 }}>
                    {p.routes?.length ?? 0} voyage{p.routes?.length !== 1 ? "s" : ""} · fuels: {p.activeFuelIds?.slice(0, 4).join(", ")}{p.activeFuelIds?.length > 4 ? "…" : ""}
                  </div>
                </div>
              ))}
              {PRESETS.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 0", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-400)" }}>
                  No presets defined yet. Add entries to presets.js.
                </div>
              )}
            </div>
          )}

          {tab === "saved" && (
            <div>
              {savedCases.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--slate-400)" }}>
                    No saved cases yet. Use 💾 Save Case in the tool footer to save your work.
                  </div>
                </div>
              ) : savedCases.map(c => (
                <div
                  key={c.id}
                  style={cardStyle(selectedCase === c)}
                  onClick={() => setSelectedCase(c)}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--slate-100)", fontWeight: 600 }}>
                      {c.name}
                    </div>
                    <div style={{ color: selectedCase === c ? "var(--teal-400)" : "var(--slate-500)", fontSize: 14, flexShrink: 0 }}>
                      {selectedCase === c ? "✓" : "○"}
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--slate-400)", marginTop: 5 }}>
                    Saved {new Date(c.savedAt).toLocaleDateString()} · {c.routes?.length ?? 0} voyage{c.routes?.length !== 1 ? "s" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ marginTop: 0 }}>
          <button className="modal-btn-cancel" onClick={onClose}>Skip</button>
          <button
            className="modal-btn-create"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {tab === "scratch" ? "Start Fresh" : tab === "preset" ? "Load Preset" : "Load Case"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── EMAIL CAPTURE MODAL ─────────────────────────────────────────────────
   Sends to Airtable. Setup:
   1. Create a free Airtable base with a table named "Subscribers"
   2. Add fields: Email (Email), Name (Single line text), SubmittedAt (Date),
      Referrer (Single line text), Country (Single line text)
   3. Create a Personal Access Token at airtable.com/create/tokens
      with scope: data.records:write  on your base
   4. Replace AIRTABLE_TOKEN and AIRTABLE_BASE_ID below
──────────────────────────────────────────────────────────────────────── */

const AIRTABLE_TOKEN   = "YOUR_AIRTABLE_PAT";       // pat...
const AIRTABLE_BASE_ID = "YOUR_AIRTABLE_BASE_ID";   // app...
const AIRTABLE_TABLE   = "Subscribers";

async function submitToAirtable({ email, name }) {
  const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(AIRTABLE_TABLE)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      fields: {
        Email:       email,
        Name:        name || "",
        SubmittedAt: new Date().toISOString(),
        Referrer:    document.referrer || "direct",
        Country:     Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      },
    }),
  });
  if (!res.ok) throw new Error(`Airtable error ${res.status}`);
  return res.json();
}

function EmailCaptureModal({ onClose }) {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [status, setStatus]   = useState("idle"); // idle | sending | success | error
  const [errMsg, setErrMsg]   = useState("");

  async function handleSubmit() {
    if (!email.trim() || !email.includes("@")) {
      setErrMsg("Please enter a valid email address.");
      return;
    }
    setStatus("sending");
    setErrMsg("");
    try {
      await submitToAirtable({ email: email.trim(), name: name.trim() });
      setStatus("success");
    } catch (e) {
      setStatus("error");
      setErrMsg("Something went wrong — please try again.");
    }
  }

  const MONO = "'JetBrains Mono','Fira Mono','Consolas',monospace";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 8000,
      background: "rgba(15,23,42,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: "32px 36px 28px",
        maxWidth: 440, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        fontFamily: MONO,
        position: "relative",
      }}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 16,
            background: "none", border: "none", cursor: "pointer",
            color: "#94a3b8", fontSize: 18, lineHeight: 1, padding: 4,
          }}
        >✕</button>

        {status === "success" ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
              You're on the list!
            </div>
            <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.7, marginBottom: 24 }}>
              We'll keep you posted on new features, updates, and FuelEU regulatory news.
            </div>
            <button
              onClick={onClose}
              style={{
                background: "#0d9488", border: "none", borderRadius: 7,
                color: "#fff", fontFamily: MONO, fontSize: 12,
                padding: "9px 28px", cursor: "pointer",
              }}
            >
              Back to the tool →
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: 6, fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", color: "#0d9488" }}>
              Stay in the loop
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 8, lineHeight: 1.3 }}>
              Get updates on the FuelEU Planner
            </div>
            <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.75, marginBottom: 24 }}>
              New features, regulatory updates, and methodology improvements — straight to your inbox. No spam, unsubscribe anytime.
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "#f1f5f9", marginBottom: 20 }} />

            {/* Name field */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 10, color: "#64748b", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>
                First name <span style={{ color: "#94a3b8" }}>(optional)</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Marie"
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "8px 12px", border: "1px solid #e2e8f0",
                  borderRadius: 6, fontFamily: MONO, fontSize: 12,
                  color: "#0f172a", background: "#f8fafc",
                  outline: "none",
                }}
                onFocus={e => e.target.style.borderColor = "#0d9488"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>

            {/* Email field */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 10, color: "#64748b", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>
                Email address <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrMsg(""); }}
                placeholder="you@company.com"
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "8px 12px",
                  border: `1px solid ${errMsg ? "#ef4444" : "#e2e8f0"}`,
                  borderRadius: 6, fontFamily: MONO, fontSize: 12,
                  color: "#0f172a", background: "#f8fafc",
                  outline: "none",
                }}
                onFocus={e => e.target.style.borderColor = "#0d9488"}
                onBlur={e => e.target.style.borderColor = errMsg ? "#ef4444" : "#e2e8f0"}
              />
              {errMsg && (
                <div style={{ fontSize: 10, color: "#ef4444", marginTop: 4 }}>{errMsg}</div>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                onClick={handleSubmit}
                disabled={status === "sending"}
                style={{
                  flex: 1, padding: "10px 0",
                  background: status === "sending" ? "#94a3b8" : "#0d9488",
                  border: "none", borderRadius: 7, color: "#fff",
                  fontFamily: MONO, fontSize: 12, fontWeight: 600,
                  cursor: status === "sending" ? "not-allowed" : "pointer",
                  transition: "background 0.15s",
                  letterSpacing: "0.3px",
                }}
              >
                {status === "sending" ? "Sending…" : "Subscribe →"}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: "10px 16px", background: "transparent",
                  border: "1px solid #e2e8f0", borderRadius: 7,
                  color: "#64748b", fontFamily: MONO, fontSize: 11,
                  cursor: "pointer", transition: "all 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#94a3b8"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
              >
                Maybe later
              </button>
            </div>

            <div style={{ marginTop: 14, fontSize: 9, color: "#94a3b8", textAlign: "center", letterSpacing: "0.5px" }}>
              Your data is stored securely and never shared with third parties.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Contact modal (tool footer) ──────────────────────────────────
   Same form + onContact wiring as the landing page's contact section,
   just without the left-hand image — shown over the tool itself.    */
function ContactModal({ onClose, onContact }) {
  const [sent, setSent] = useState(false);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 8000,
        background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: "32px 36px 28px",
          maxWidth: 440, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          fontFamily: "'IBM Plex Sans', sans-serif",
          position: "relative",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 16,
            background: "none", border: "none", cursor: "pointer",
            color: "#94a3b8", fontSize: 18, lineHeight: 1, padding: 4,
          }}
        >✕</button>

        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
          letterSpacing: "2px", textTransform: "uppercase",
          color: "#0d9488", marginBottom: 6,
        }}>
          Get in touch
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 20 }}>
          Contact us
        </div>

        {sent ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
              color: "#0d9488", letterSpacing: 1,
            }}>
              ✓ Message sent — we'll be in touch soon.
            </div>
            <button
              onClick={onClose}
              style={{
                marginTop: 20,
                background: "#0d9488", border: "none", borderRadius: 7,
                color: "#fff", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                padding: "9px 28px", cursor: "pointer",
              }}
            >
              Back to the tool →
            </button>
          </div>
        ) : (
          <ContactForm onSent={() => setSent(true)} onContact={onContact} />
        )}
      </div>
    </div>
  );
}

function Root() {
  const [showLanding, setShowLanding] = useState(true);
  const [showStartup, setShowStartup] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactError, setContactError] = useState(null);
  const applyPayloadRef = useRef(null);
  const contactErrorTimer = useRef(null);

  useEffect(() => {
    // Initialize session and log page view
    (async () => {
      await sessionManager.init();
      const result = await sessionManager.logAnalytics({ event: 'page_load' });
      if (!result.success && result.error === 'Invalid session') {
        // Stale session left over from a previous load — clear it and retry once.
        sessionManager.resetSession();
        await sessionManager.init();
        await sessionManager.logAnalytics({ event: 'page_load' });
      }
    })();

    // Log analytics on route changes (if you add routing later)
    // history.listen(() => sessionManager.logAnalytics());
  }, []);

  const handleContactSubmit = async (formData) => {
    sessionManager.logAnalytics({ event: 'form_sent' });
    const result = await sessionManager.submitContact(formData);
    if (!result.success) {
      if (contactErrorTimer.current) clearTimeout(contactErrorTimer.current);
      setContactError("Unexpected error. Please try again.");
      contactErrorTimer.current = setTimeout(() => setContactError(null), 3500);
    }
    return result.success;
  };
  

  const handleLogDocumentation = async () => {
    sessionManager.logAnalytics({ event: 'documentation' });
  };

  useEffect(() => {
    document.body.classList.toggle("landing", showLanding);
    document.body.classList.toggle("tool", !showLanding);
    return () => document.body.classList.remove("landing", "tool");
  }, [showLanding]);

  function handleEnter() {
    sessionManager.logAnalytics({ event: 'enter_tool' });
    setShowLanding(false);
    try {
      const tourSeen = localStorage.getItem("fuelTool_tourSeen");
      // const emailCaptured = localStorage.getItem("fuelTool_emailSeen");
      const emailCaptured = true;
      if (tourSeen) {
        setShowStartup(true);
        // Show email modal after a short delay if not yet captured
        if (!emailCaptured) {
          setTimeout(() => setShowEmailCapture(true), 1200);
        }
      }
      // If tour not seen, tour auto-triggers from App — email modal shown after tour
    } catch {
      setShowStartup(true);
    }
  }

  function handleTourEnd() {
    setTourActive(false);
    setShowStartup(true);
    // // Show email capture after tour, unless already seen
    // try {
    //   if (!localStorage.getItem("fuelTool_emailSeen")) {
    //     setTimeout(() => setShowEmailCapture(true), 800);
    //   }
    // } catch {}
  }

  if (showLanding) {
    return (
      <>
        <LandingPage onEnter={handleEnter} onContact={handleContactSubmit} onshowDocumentation={handleLogDocumentation}/>
        {contactError && (
          <div
            onClick={() => setContactError(null)}
            style={{
              position: "fixed",
              bottom: 28,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 18px",
              borderRadius: 8,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              cursor: "pointer",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              whiteSpace: "nowrap",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
            }}
          >
            <span style={{ fontSize: 14 }}>✕</span>
            {contactError}
            <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }}>✕</span>
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <App
        onReady={fn => { applyPayloadRef.current = fn; }}
        onTourStart={() => setTourActive(true)}
        onTourEnd={handleTourEnd}
        onOpenStartup={() => setShowStartup(true)}
        onOpenContact={() => setShowContactModal(true)}
      />
      {showStartup && !tourActive && !showEmailCapture && (
        <StartupModal
          onClose={() => setShowStartup(false)}
          onLoadPayload={payload => {
            applyPayloadRef.current?.(payload);
            setShowStartup(false);
          }}
        />
      )}
      {showEmailCapture &&(
        <EmailCaptureModal
          onClose={() => {
            setShowEmailCapture(false);
            try { localStorage.setItem("fuelTool_emailSeen", "1"); } catch {}
          }}
        />
      )}
      {showContactModal && (
        <ContactModal
          onClose={() => setShowContactModal(false)}
          onContact={handleContactSubmit}
        />
      )}
      {contactError && (
        <div
          onClick={() => setContactError(null)}
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 18px",
            borderRadius: 8,
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            whiteSpace: "nowrap",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#dc2626",
          }}
        >
          <span style={{ fontSize: 14 }}>✕</span>
          {contactError}
          <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }}>✕</span>
        </div>
      )}
    </>
  );
}

export default Root;